import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { pushSupported, pushStatus, subscribeToPush, unsubscribeFromPush } from '../utils/pushNotifications';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import {
  DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors, DragOverlay,
  pointerWithin, rectIntersection,
} from '@dnd-kit/core';
import {
  RefreshCw, Phone, Mail, User, Clock, MessageSquare, ArrowRight,
  Loader2, AlertTriangle, Copy, Filter, Send, Bell,
  Plus, Settings, Trash2, ChevronUp, ChevronDown, CalendarClock,
  Search, BarChart3, X, Sheet, CheckCircle2, ExternalLink, CheckSquare, Square, Zap, Download, Pin,
  Calculator, Play,
  ArrowLeft, ChevronRight, FileText, Pencil, UserCheck, Info, MoreHorizontal,
  LayoutGrid, Rows3, Briefcase,
} from 'lucide-react';
import { useLeadRoute } from '../lib/route';
import { toast } from 'sonner';
import { getApiUrl } from '../utils/api';
import './funnel.css';

const API_URL = getApiUrl();

// Откуда пришла заявка. Ярлык короткий: в карточке места мало, а источник
// нужно различать с одного взгляда.
const SOURCES = {
  meta: { label: 'Meta' },
  sheets: { label: 'Реклама' },
  tilda: { label: 'Tilda' },
  site: { label: 'Сайт' },
  allegro: { label: 'Allegro' },
  olx: { label: 'OLX' },
  oferteo: { label: 'Oferteo' },
  phone: { label: 'Звонок' },
  call: { label: 'Входящий' },
  mail: { label: 'Почта' },
  amo: { label: 'Из amo' },
  messenger: { label: 'Messenger' },
  instagram: { label: 'Instagram' },
  manual: { label: 'Вручную' },
  other: { label: 'Другое' },
};

const PRODUCTS = {
  sauna: 'Сауна',
  balia: 'Купель',
  greenhouse: 'Теплица',
};

/** Имена менеджеров для показа.
 *
 *  Логин — то, чем человек входит; на доске и в карточке должно стоять имя,
 *  как в amoCRM. Держим реестром на уровне модуля, а не прокидываем списком
 *  через props: карточка на доске о менеджерах ничего не знает и знать не
 *  должна, а имена меняются раз в год.
 *
 *  Пока список не загрузился, показываем логин — он всегда есть и всегда
 *  верен, в отличие от пустого места.
 */
let MANAGER_NAMES = {};
const rememberManagers = (list) => {
  MANAGER_NAMES = Object.fromEntries(
    (list || []).filter((m) => m.fullName).map((m) => [m.username, m.fullName]));
};
// Как человека зовут на экране. Логин остаётся значением, именем он быть
// не должен: `MOP-Viyaleta` общий с калькулятором намеренно, второй завёл
// бы второго человека, и «кто сделал» разошлось бы между системами молча.
// Поэтому таблица отображаемых имён, а не правка логина.
//
// Для `by` из ленты это работает и когда автор не человек: правила пишут
// туда своё имя («правило «Без движения 72 часа»»), в таблице его нет, и
// строка возвращается как есть — то есть читается как и задумано.
const managerLabel = (username) => MANAGER_NAMES[username] || username || '';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('authToken')}`,
  'Content-Type': 'application/json',
});

/** Устойчивый оттенок из строки: одна и та же метка всегда одного цвета.
 *  Насыщенность и светлота остаются общими, поэтому метки выглядят набором. */
function hueFromText(text) {
  let h = 0;
  for (let i = 0; i < text.length; i += 1) h = (h * 31 + text.charCodeAt(i)) % 360;
  return h;
}

/* Как зовут сделку на экране. Название задаёт менеджер и пишет в него
   что угодно — «хочет на лето», «перезвонить после отпуска»; пустое
   название означает «звать по клиенту», и тогда собирается из имени и
   номера. Имя клиента при этом остаётся именем: оно уходит в раздел
   «Клиенты», в передачу в CRM саун и в выгрузку, и пометка менеджера его
   не затирает. */
function leadTitle(lead) {
  return (lead.title || '').trim()
      || (lead.name || '').trim()
      || (lead.phone || '').trim()
      || 'Без имени';
}

/* Строки под заголовком: то, чего в нём нет.

   Имя и номер были склеены в одну строку через точку — «Sławomir
   Stodolak · +48692981558». В колонке шириной 272 и в шапке карточки
   такая строка не помещается и обрывается посреди номера: на боевой
   выходило «Sławomir Stodolak · +486929», а обрывок номера читается как
   настоящий короткий номер. Склейка экономила строку и стоила номера.
   Каждое своей строкой: имя сверху, номер под ним. */
function leadSubLines(lead) {
  const head = leadTitle(lead);
  const name = (lead.name || '').trim();
  const phone = (lead.phone || '').trim();
  return [name, phone].filter((v) => v && v !== head);
}

/* Что написано на плитке доски.
 *
 * Номера клиентов с доски убраны по решению владельца 21.10.2026: доску
 * видят все менеджеры целиком, и сотня чужих номеров на одном экране —
 * это выписанная база, а не рабочий инструмент. В открытой карточке
 * номер на месте: кто сделку взял, тот и звонит.
 *
 * Это ограничение видимости, а не защита: номер по-прежнему приезжает
 * в браузер вместе с карточкой, и тот, кто захочет, достанет его из
 * ответа сервера. Настоящее ограничение режется на сервере — владелец
 * выбрал пока не резать.
 */
/** Убрать из текста телефонные номера.
 *
 * Снять поле «телефон» с плитки оказалось мало: у заявок, приехавших из
 * amo, номер вписан в само название — «Phone call from 48533909201».
 * Таких на доске нашлось четырнадцать из девятисот тридцати, и глазом
 * я бы их не заметил: карточка выглядит как обычная, с текстом вместо
 * номера. Нашлись счётом по цифрам.
 *
 * Девять цифр подряд — минимальная длина польского номера без кода.
 * Короткие числа не трогаем: «Сауна 200x200» должна остаться собой.
 */
function hideDigits(text) {
  return String(text || '').replace(/[+\d][\d\s().-]{8,}\d/g, '…');
}

function boardTitle(lead) {
  const own = hideDigits((lead.title || '').trim()) || (lead.name || '').trim();
  // Без имени и названия на плитке остаётся наш собственный номер.
  // Пустая карточка «Без имени» не отличалась бы от соседних — таких
  // на боевой каждая шестнадцатая, и в разговоре на них не сослаться.
  return own || (lead.no ? `№ ${lead.no}` : 'Без имени');
}

function boardSubLines(lead) {
  const head = boardTitle(lead);
  // Имя чистим тем же способом, что и название: у части заявок номер
  // записан прямо в имя — «Anonim 8057359015», «New contact for the
  // call from +48601884261 by CallGear». Это не выдумка, а две живые
  // карточки, оставшиеся после первой правки.
  const name = hideDigits((lead.name || '').trim());
  return [name].filter((v) => v && v !== head);
}

/** Как назвать клиента в списке сделок.
 *
 * Имя приезжает не всегда: из 300 строк боевого слепка его нет у 82. У
 * трети таких заявок человек подписан в названии сделки — «Zbigniew —
 * sauna sucha, Wałbrzych», — и показывать им «Без имени» значит звонить
 * человеку, не зная, как его зовут.
 *
 * Но часть названий техническая, их поставила amo: «Phone call from
 * 48533909201» (восемь штук из двадцати девяти). Это не имя, а номер,
 * переодетый в название, и в колонке «Клиент» ему делать нечего —
 * номер и так стоит в соседней колонке. Таким показываем наш номер
 * сделки, по нему хотя бы можно сослаться.
 */
const TECHNICAL_TITLES = [/^phone call from/i, /^new contact for the call/i, /^сделка\s*#/i];

function listClientName(lead) {
  const name = (lead.name || '').trim();
  if (name) return name;
  const title = (lead.title || '').trim();
  if (title && !TECHNICAL_TITLES.some((re) => re.test(title))) return title;
  // Номер сделки здесь не повторяем: в списке он стоит отдельной
  // колонкой слева, и «№ 4667» в двух соседних ячейках — не ответ на
  // вопрос «кто это», а шум.
  return 'Без имени';
}

/** Запомненный вид раздела.
 *
 * Менеджер выбирал «Список», уходил в «Клиенты», возвращался — и снова
 * доска. Выбор вида — это не разовое действие, а то, как человек
 * привык работать; переспрашивать его на каждом возврате значит
 * заставлять выбирать заново по десять раз на дню.
 *
 * Хранилище может быть недоступно (приватное окно, запрет на сайт), и
 * тогда просто остаётся умолчание — ради памяти о виде экран ломаться
 * не должен.
 */
/** Поиск, который переживает уход в карточку и обратно.
 *
 * Менеджер искал клиента по номеру, открывал его сделку, нажимал
 * возврат — и список снова показывал все три тысячи девятьсот строк, а
 * набранное исчезало. Искать приходилось заново, и это при клиенте на
 * линии.
 *
 * Держим в sessionStorage, а не в localStorage: поиск — это текущая
 * работа, а не настройка. Закрыл вкладку — забыли.
 */
function useKeptSearch(key) {
  const [value, setValue] = useState(() => {
    try { return window.sessionStorage.getItem(key) || ''; } catch { return ''; }
  });
  const keep = useCallback((next) => {
    setValue(next);
    try { window.sessionStorage.setItem(key, next); } catch { /* не страшно */ }
  }, [key]);
  return [value, keep];
}

function useRememberedMode(key, fallback) {
  const [mode, setMode] = useState(() => {
    try { return window.localStorage.getItem(key) || fallback; } catch { return fallback; }
  });
  const remember = useCallback((next) => {
    setMode(next);
    try { window.localStorage.setItem(key, next); } catch { /* не страшно */ }
  }, [key]);
  return [mode, remember];
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'только что';
  if (diff < 3600) return `${Math.floor(diff / 60)} мин`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ч`;
  return `${Math.floor(diff / 86400)} дн`;
}

function LeadCard({ lead, onOpen, dragging, locked }) {
  /* Сделка на этапе-исходе не перетаскивается: ею распоряжается
     производство, и перенос у нас разошёлся бы с CRM саун. Запрет стоит и
     на сервере — здесь он для того, чтобы человек не узнал о правиле из
     ошибки после того, как уже потащил. */
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: lead.id, disabled: locked,
  });
  const src = SOURCES[lead.source] || SOURCES.other;
  const overdue = (lead.overdueTasks || 0) > 0;
  const openTasks = lead.openTasks || 0;
  // Клиент написал позже, чем пришла заявка, — значит разговор живой.
  // «Клиент ответил» значит «клиент написал ПОСЛЕ нашего ответа», то есть
  // «ждёт нас». Сравнение с датой заявки отвечало на другой вопрос —
  // «писал ли клиент когда-нибудь», — и пилюля не гасла никогда: ни
  // после ответа менеджера, ни после ответа бота. У сделки, рождённой
  // входящим сообщением, дата заявки и дата реплики ставятся в одну
  // секунду, поэтому пилюля загоралась сразу при появлении карточки и
  // навсегда прятала «просрочено» и «без задачи» — они стоят следующими
  // в той же цепочке выбора.
  const lastReply = lead.lastOutboundAt || lead.receivedAt || '';
  const answered = lead.lastInboundAt && lead.lastInboundAt > lastReply
    ? lead.lastInboundAt : '';

  return (
    <div
      ref={setNodeRef}
      style={transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : undefined}
      className={`fn-card${dragging ? ' is-dragging' : ''}${lead.crmLeadId ? ' is-handed' : ''}${locked ? ' is-locked' : ''}`}
      {...listeners}
      {...attributes}
    >
      <button type="button" onClick={() => onOpen(lead)} className="block w-full text-left"
              data-testid={`funnel-card-${lead.id}`}>
        {/* Кружок источника вместо точки перед меткой. В каждом референсе,
            который выбрал владелец, у строки слева стоит цветной кружок —
            там это лицо человека. Лиц у нас нет: почти все заявки без
            имени, и аватар клиента был бы одинаковым серым на всей доске.
            А вот источник у карточек разный и уже имеет свой тон — из него
            и выходит якорь, по которому глаз держит колонку. */}
        <div className="fn-card-top">
          <span className="fn-avatar" data-src={lead.source}
                style={lead.sourceLabel ? { '--tag-h': hueFromText(lead.sourceLabel) } : undefined}
                title={lead.sourceLabel ? `${src.label}: ${lead.sourceLabel}` : src.label}>
            {(lead.sourceLabel || src.label || '?').trim().charAt(0).toUpperCase()}
          </span>
          <span className="fn-card-ident">
            <span className="fn-card-name">{boardTitle(lead)}</span>
            {boardSubLines(lead).map((line) => (
              <span key={line} className="block text-[length:var(--fn-t-sm)] leading-4 fn-ink-3">
                {line}
              </span>
            ))}
            {/* Комментарий одной строкой. Менеджеры и в amoCRM пишут такие
                пометки прямо в название сделки — именно потому, что иначе
                их не видно с доски. Раз видно, писать в название незачем. */}
            {(lead.managerNote || '').trim() && (
              <span className="fn-card-note" title={lead.managerNote}>{lead.managerNote}</span>
            )}
          </span>
          {/* Возраст заявки стоит в шапке справа — там же, где в референсе
              расстояние рейса. Это вспомогательное число при заголовке, а
              не свойство: в подвале оно отбирало место у состояния, и на
              колонке в 272 пикселя строка ломалась пополам. */}
          {/* Время считаем от позднейшего события, а не от заявки. Клиент,
              ответивший десять минут назад, показывал «13 дн» — возраст
              обращения, — и на доске выглядел мёртвым. `lastInboundAt`
              приезжает в каждой карточке и не использовался нигде. */}
          <time className="fn-card-age" title={answered ? 'Клиент написал' : 'Заявка получена'}>
            {timeAgo(answered || lead.receivedAt)}
          </time>
        </div>

        {/* Подвал карточки, отделённый волоском во всю ширину. Приём снят с
            логистической панели Ronas IT: верх карточки отвечает «что это»,
            подвал — «когда и в каком состоянии», и между ними линия. У нас
            состояние стояло сверху рядом с именем и спорило с ним за первое
            место: сперва читалось «просрочено», а уже потом — чья заявка. */}
        <div className="fn-meta fn-card-foot">
          <span className="fn-tag" data-src={lead.source}
                style={lead.sourceLabel ? { '--tag-h': hueFromText(lead.sourceLabel) } : undefined}>
            {lead.sourceLabel || src.label}
          </span>
          {lead.productType && (
            <span className="fn-product">{PRODUCTS[lead.productType] || lead.productType}</span>
          )}
          {/* Сумма стоит слева, при свойствах сделки, а не справа, как в
              amo. Справа у нас живут состояния — «просрочено», «не
              открывали», — и сумма, поставленная к ним, выдавливала
              последнее за край карточки: в 272 пикселя шесть меток в
              строку не помещаются. Заполнена сумма у семидесяти сделок
              из девятисот; у остальных строка молчит, а не рисует
              прочерк. */}
          {lead.budget > 0 && (
            <b className="fn-card-money" title="Бюджет сделки">
              {Math.round(lead.budget).toLocaleString('ru-RU')}&nbsp;zł
            </b>
          )}
          {/* Разделителя между продуктом и менеджером нет: при переносе
              строки он оставался висеть в конце первой — точка без пары.
              Элементы и так разведены зазором. */}
          {lead.assignedTo && <span className="truncate">{managerLabel(lead.assignedTo)}</span>}
          <span className="ml-auto flex items-center gap-2">
            {lead.comments?.length > 0 && (
              <span className="inline-flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />{lead.comments.length}
              </span>
            )}
            {/* Счётчик задач с доски убран: пилюля отвечает на тот же
                вопрос точнее. «☑ 1» говорило «задача есть», но не говорило
                главного — просрочена она или нет, и рядом с пилюлей
                «просрочено» повторяло уже сказанное. Молчание теперь
                означает «задача есть и срок не вышел». */}
            {/* Пилюля состояния — то, по чему сделки разбирают. Точка перед
                словом, как в референсе: цвет узнаётся раньше, чем текст
                прочитан, а слово снимает нужду в легенде. Пилюля молчит,
                когда всё в порядке: помечено только требующее действия. */}
            {/* «Клиент ответил» идёт первым: это единственное состояние, где
                человек ждёт нас прямо сейчас. Просрочка и отсутствие задачи —
                наш долг, он никуда не денется; ответ клиента остывает. */}
            {/* В подсказке — счёт, который стоит за словом. «Просрочено»
                отвечает «куда идти», но не говорит, одна там задача или
                шесть, а разговор по сделке с шестью просроченными
                другой. Приём из OpenCRM, где у индикатора задачи висит
                «3 открытые задачи, просрочено: 1». */}
            {answered ? (
              <span className="fn-state fn-state-good">клиент ответил</span>
            ) : overdue ? (
              <span className="fn-state fn-state-alarm"
                    title={`Просроченных задач: ${lead.overdueTasks || 1}`
                           + (openTasks ? `, открытых всего: ${openTasks}` : '')}>
                просрочено
              </span>
            ) : openTasks === 0 ? (
              <span className="fn-state fn-state-warn"
                    title="Ни одной открытой задачи — сделка на доске молчит">
                без задачи
              </span>
            ) : null}
            {/* «Никто не открывал» — рядом, а не в той же цепочке выбора:
                это не состояние работы, а состояние знания о заявке, и оно
                сочетается с остальными. Заявка может быть и просроченной, и
                ни разу не открытой — второе объясняет первое. Показываем
                только у неразобранных: дальше по воронке сделка без
                просмотра не бывает, а метка на каждой карточке стала бы
                шумом. */}
            {!lead.firstSeenAt && !lead.assignedTo && (
              <span className="fn-state" title="Карточку ни разу не открывали">
                не открывали
              </span>
            )}
          </span>
        </div>
      </button>
    </div>
  );
}

function Column({ stage, count, amount, overdue, leads, onOpen, activeId, rules, onRules, filtered }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id });

  return (
    <div
      ref={setNodeRef}
      className={`fn-col${isOver ? ' is-over' : ''}`}
      style={{ '--fn-stage': stage.color }}
    >
      <div className="fn-col-head">
        <span className="fn-col-title" title={stage.name}>{stage.name}</span>
        {/* Второй строкой — сколько здесь стоит и на сколько. Название
            этапа отвечает «где», цифры — «сколько»; вперемешку в одну
            строку они складывались в ребус из четырёх чисел подряд. */}
        <span className="fn-col-figures">
        {/* Настройка этапа там же, где сам этап: искать её в общем списке
            правил — значит каждый раз вспоминать, как этап называется. */}
        {onRules && (
          <button type="button" onClick={() => onRules(stage)} className="fn-col-rules"
                  title={rules ? `Правил на этапе: ${rules}` : 'Что делать со сделкой на этом этапе'}>
            {rules ? <><Zap className="h-3 w-3" />{rules}</> : <Plus className="h-3 w-3" />}
          </button>
        )}
        {/* Сколько на этапе горит. «253» отвечает, сколько накопилось;
            «12 просрочено» отвечает, куда идти. Разбор интерфейсов
            ELMA365 называет это отличием живого канбана от списка со
            счётчиками — и это первое проверяемое утверждение в той
            статье, которого у нас не было. Молчит, когда просрочки нет:
            ноль просроченных — не новость. */}
        {overdue > 0 && (
          <span className="fn-col-overdue" title={`Сделок с просроченной задачей: ${overdue}`}>
            {overdue}
          </span>
        )}
        <span className="fn-col-count">{count}</span>
        {amount > 0 && (
          <span className="fn-col-money" title="Сумма бюджетов на этапе">
            {Math.round(amount).toLocaleString('ru-RU')}&nbsp;zł
          </span>
        )}
        </span>
      </div>

      <div className="fn-col-body" style={{ maxHeight: 'calc(100vh - 260px)' }}>
        {/* Пустая колонка говорит, почему она пуста. Прочерк отвечал
            одинаково на «здесь нет сделок» и «ничего не нашлось по
            поиску», и при поиске доска превращалась в стену прочерков,
            которую менеджер читает как «ничего не найдено» — хотя
            найденное стоит в двадцати прокрутках правее. */}
        {leads.length === 0 && (
          <p className="px-1 py-6 text-center text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">
            {filtered ? 'ничего не нашлось' : '—'}
          </p>
        )}
        {leads.map((l) => (
          <LeadCard key={l.id} lead={l} onOpen={onOpen} dragging={activeId === l.id}
                    locked={!!stage.readOnly} />
        ))}
        {/* Колонка отдаёт сто сделок, а счётчик в шапке показывает все.
            Молчать об этом нельзя: менеджер пролистает до конца и решит,
            что в этапе сто сделок, хотя их тысяча. Замер на двадцати
            тысячах: показано 1800 из 16651. */}
        {count > leads.length && (
          <p className="px-1 py-3 text-center text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">
            Показаны {leads.length} из {count}.<br />
            Остальные найдутся поиском или фильтром.
          </p>
        )}
      </div>
    </div>
  );
}

/** Человеческая дата для разделителя в ленте. */
function dayLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date(Date.now() - 86400000);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, today)) return 'Сегодня';
  if (same(d, yest)) return 'Вчера';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

/** «1 пустое», «3 пустых» — иначе интерфейс выглядит недоделанным. */
function plural(n, one, few, many) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

const GROUPS = [
  { id: 'client', title: 'Клиент' },
  // Деньги вторыми, сразу за клиентом: из всей карточки менеджер и
  // владелец чаще всего ищут именно сумму. Группа устроена иначе остальных —
  // её строки не настраиваются и не правятся, см. moneyRows.
  { id: 'money', title: 'Деньги' },
  { id: 'config', title: 'Что хочет' },
  { id: 'ad', title: 'Реклама' },
];

/** Сумма по-польски: пробел тысячами, запятая копейками, целое без копеек.
 *
 * Копейки не срезаем у нецелых: 33 354,50 превратилось бы в 33 354,5, а это
 * другое число на вид, и менеджер сверяет его с фактурой. Та же оговорка
 * стоит в calc_record на бэкенде — формат один, иначе одна и та же сумма
 * выглядит в ленте и в карточке по-разному.
 */
function zlote(n) {
  return n.toLocaleString('pl-PL', {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/** Деньги по бумагам. Строки собираются из ленты, а не из полей сделки.
 *
 * Суммы не вводят руками и мы их у себя не храним вторым экземпляром: их
 * присылает калькулятор вместе с КП и договором — он же их и выписывает.
 * Трёх источников по заказам, которые уже не сходятся между собой
 * (таблица, amoCRM, наш сервис), достаточно; четвёртый, набираемый в
 * карточке поверх присланного, расходился бы с ними молча.
 *
 * Чего здесь намеренно нет — платежей. Заказ, по которому внесена
 * предоплата, живёт в производстве, и оплаты ведутся там. Показывать их
 * копию здесь значило бы отвечать на вопрос «сколько внесено» из места,
 * которое об этом не знает.
 */
function moneyRows(feed) {
  const docs = (feed || []).filter(
    (e) => e.type === 'doc' && e.meta && e.meta.amount != null);
  // По присланному времени, а не по порядку в ленте: запись могла пролежать
  // в очереди на стороне калькулятора, и тогда «последняя пришедшая» и
  // «последняя выписанная» — разные бумаги.
  const last = (kind) => docs
    .filter((d) => d.meta.kind === kind)
    .sort((a, b) => (a.at || '').localeCompare(b.at || ''))
    .slice(-1)[0] || null;

  const kp = last('kp');
  const contract = last('contract');
  // Правки заказа: номер заказа при них не меняется, растёт seq. Берём
  // наибольший seq, а не последнюю по времени: время у двух правок может
  // совпасть до секунды, а seq — счётчик, он однозначен.
  const change = docs
    .filter((d) => d.meta.kind === 'order_changed')
    .sort((a, b) => (a.meta.seq || 0) - (b.meta.seq || 0))
    .slice(-1)[0] || null;

  const rows = [];
  const row = (label, doc, note) => rows.push({
    label, note, amount: doc.meta.amount,
    currency: doc.meta.currency || 'PLN',
    number: doc.meta.number || '', url: doc.meta.url || '', at: doc.at,
  });

  // Подписи короткие: под них в ряду 6.5rem, и «Коммерческое предложение»
  // переносилось бы на три строки, разводя высоту рядов в одной колонке.
  if (kp) row('КП', kp);
  if (contract) row('Договор', contract);
  // Сумму правки не называем договорной и ничего из неё не вычитаем: значит
  // ли она новую полную сумму заказа или только разницу — вопрос к чату
  // «Калькулятор», он её присылает. Пока подписываем ровно тем, что знаем.
  if (change) {
    row('Правка', change,
        change.meta.seq ? `после правки №${change.meta.seq}` : 'после правки заказа');
  }
  // Разницу показываем числом и без трактовки. Называть её скидкой, уступкой
  // или ошибкой — значит объяснять то, чего мы не мерили.
  if (kp && contract && kp.meta.amount !== contract.meta.amount) {
    const diff = contract.meta.amount - kp.meta.amount;
    rows.push({
      label: 'Разница', diff,
      amount: Math.abs(diff), currency: contract.meta.currency || 'PLN',
      note: `${diff < 0 ? 'договор дешевле' : 'договор дороже'} на ${
        Math.round(Math.abs(diff) / kp.meta.amount * 100)}%`,
    });
  }
  return rows;
}

/* Тип записи в ленте читается по значку, а не по подписи.
 *
 * В карточке amoCRM у каждого события слева кружок: конверт — письмо,
 * трубка — звонок, галочка — задача. Тип видно, не читая; подпись
 * «КОММЕНТАРИЙ» капслоком приходилось прочитывать, а на длинной переписке
 * таких подписей два десятка подряд. Цвет здесь — цвет кружка и значка. */
/* Что сделал человек — и что записала система.
 *
 * Устройство снято с ленты amoCRM (замер 20.09.2026): у них карточку с
 * рамкой получают звонок, письмо, заметка и задача — то, что кто-то
 * сделал. Смена этапа, правка поля, теги, смена ответственного идут
 * однострочками без рамки и без значка, плотным столбцом.
 *
 * Различие там структурное, а не цветовое: кегль у обоих видов одинаковый
 * (15px), разнятся рамка, поля и зазор — 20 пикселей между карточками
 * против 7 между строками. Мы делаем так же, только системные строки
 * ставим на ступень мельче: наш рабочий кегль 13, и 12 для журнала
 * отделяет его, не превращая в нечитаемое.
 */
// Калькулятор КП. Домен уже менялся: wm-kalkulator.pl отключён 14.09.2026 и
// сегодня отдаёт 404 намеренно — там страница, которая снимает у браузера
// сохранённую копию старого приложения. Поднимать его нельзя, живой адрес
// один. Если сменится снова — править здесь.
const CALC_BASE = 'https://al-spa.pl';

const HUMAN_EVENTS = new Set(['comment', 'message', 'mail', 'call', 'task', 'handoff']);

const FEED_STYLE = {
  comment: { label: 'Комментарий', dot: 'hsl(160 60% 42%)', Icon: FileText },
  stage: { label: 'Этап', dot: 'hsl(205 70% 52%)', Icon: ArrowRight },
  edit: { label: 'Правка', dot: 'hsl(40 75% 48%)', Icon: Pencil },
  call: { label: 'Звонок', dot: 'hsl(265 55% 58%)', Icon: Phone },
  created: { label: 'Заявка', dot: 'hsl(215 15% 60%)', Icon: Plus },
  duplicate: { label: 'Повтор', dot: 'hsl(35 85% 50%)', Icon: Copy },
  message: { label: 'Сообщение', dot: 'hsl(140 55% 40%)', Icon: MessageSquare },
  mail: { label: 'Письмо', dot: 'hsl(205 70% 52%)', Icon: Mail },
  task: { label: 'Задача', dot: 'hsl(280 50% 55%)', Icon: CheckCircle2 },
  rule: { label: 'Правило', dot: 'hsl(190 60% 45%)', Icon: Zap },
  handoff: { label: 'Передан', dot: 'hsl(160 60% 35%)', Icon: UserCheck },
  system: { label: 'Событие', dot: 'hsl(215 12% 70%)', Icon: Info },
};

/** Поле карточки: показывает значение, по клику превращается в поле ввода. */
/** Значение поля квиза: суть и пояснение в скобках — разной величины.
 *
 * Ответы приходят из лид-формы целиком: «Długośći 5–6m (rodzinna, z
 * przedsionkiem i prysznicem)». В узкой колонке это три строки, из которых
 * важна первая. Скобку не прячем — она бывает нужна, — но приглушаем,
 * чтобы взгляд цеплялся за размер, а не за перечисление.
 */
function FieldValue({ text }) {
  const m = /^(.*?)\s*\(([^)]+)\)\s*$/.exec(text || '');
  if (!m) return <>{text}</>;
  return (
    <>
      {m[1]}
      <span className="fn-field-aside">{m[2]}</span>
    </>
  );
}

function FieldRow({ field, value, onSave, required = false, borrowedFrom = null }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  useEffect(() => { setDraft(value || ''); }, [value]);

  const commit = () => {
    setEditing(false);
    if ((value || '') !== draft) onSave(draft);
  };

  if (editing) {
    const Comp = field.type === 'textarea' ? Textarea : Input;
    return (
      <div className="fn-field-edit px-2 py-1">
        <div className="fn-field-label mb-1">{field.label}</div>
        <Comp
          autoFocus
          value={draft}
          rows={field.type === 'textarea' ? 3 : undefined}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && field.type !== 'textarea') commit();
            if (e.key === 'Escape') { setDraft(value || ''); setEditing(false); }
          }}
        />
      </div>
    );
  }

  const empty = !value;
  return (
    <button type="button" onClick={() => setEditing(true)} className="fn-field group"
            title="Нажмите, чтобы изменить">
      <div className="fn-field-label">
        {field.label}
        {/* Звёздочка, а не слово «обязательно»: подписи отведено 6.5rem, и
            слово вытеснило бы саму подпись. Цветом — потому что пустое
            обязательное поле это не украшение, а то, что держит сделку. */}
        {required && <span className="fn-danger" title="Обязательно на этом этапе"> *</span>}
        {/* Откуда взято чужое значение. Без пометки менеджер решит, что
            клиент отвечал по этой заявке, и сошлётся на неё в разговоре;
            а ответ он давал в другой. */}
        {borrowedFrom && (
          <span className="fn-ink-3" title={`Ответ из заявки № ${borrowedFrom.no || ''}`}> ·&nbsp;из&nbsp;№&nbsp;{borrowedFrom.no}</span>
        )}
      </div>
      {empty ? (
        <div className={`fn-field-value group-hover:opacity-100 ${required ? 'fn-danger' : 'fn-ink-2'}`}>
          {required ? 'нужно заполнить' : 'не заполнено'}</div>
      ) : field.type === 'phone' ? (
        <a href={`tel:${value}`} onClick={(e) => e.stopPropagation()}
           className="fn-field-value fn-phone font-medium hover:underline">{value}</a>
      ) : field.type === 'url' ? (
        <a href={value} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
           className="fn-field-value block truncate fn-good hover:underline">{value}</a>
      ) : (
        <div className="fn-field-value whitespace-pre-wrap"><FieldValue text={value} /></div>
      )}
    </button>
  );
}

/** Скелет ленты на время загрузки.
 *
 * Показывает форму будущего содержимого — значок, шапку, одну-две строки
 * текста. Три записи, а не десять: обещать больше, чем придёт, — тот же
 * обман, что и полоса прогресса, дорисованная до ста процентов.
 */
function FeedSkeleton() {
  return (
    <div aria-busy="true" aria-label="Загружаю ленту">
      {[0, 1, 2].map((i) => (
        <div key={i} className="fn-event">
          <span className="fn-skel fn-skel-icon" />
          <div className="min-w-0 flex-1">
            <span className="fn-skel" style={{ width: '7rem', height: '0.75rem' }} />
            <span className="fn-skel mt-1.5 block"
                  style={{ width: i === 1 ? '70%' : '45%', height: '0.875rem' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Срок задачи человеческими словами: в amoCRM это «Завтра», а не дата.
 *
 * «17.09.2026 14:04» приходится переводить в уме в «это было позавчера»;
 * «просрочена на 2 дня» отвечает на тот же вопрос сразу. Точную дату
 * оставляем в подсказке — она нужна, но реже.
 */
function dueWord(iso) {
  const d = new Date(iso);
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(d) - day(new Date())) / 86400000);
  const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (days === 0) return `сегодня в ${time}`;
  if (days === 1) return `завтра в ${time}`;
  if (days === -1) return `вчера в ${time}`;
  if (days < -1) return `просрочена на ${-days} ${plural(-days, 'день', 'дня', 'дней')}`;
  return d.toLocaleString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
}

// Сроки, которые ставятся одним нажатием. Значение считается в момент
// нажатия, а не при загрузке страницы: вкладку держат открытой весь день,
// и «сегодня к 18:00», посчитанное утром, к вечеру означало бы прошлое.
//
// Формат — тот, который ждёт `datetime-local`: местное время без часового
// пояса. Поэтому не `toISOString()` — он переводит в UTC, и выбранное
// «18:00» приехало бы в поле как «16:00» летом в Польше.
const QUICK_DUE = [
  {
    short: '18:00',
    label: 'сегодня к 18:00',
    value: () => {
      const d = new Date();
      d.setHours(18, 0, 0, 0);
      // Если уже вечер, «сегодня к 18:00» — это прошлое. Переносим на час
      // вперёд от сейчас: срок в прошлом сразу считается просроченным, и
      // кнопка создавала бы просрочку в момент нажатия.
      if (d <= new Date()) d.setTime(Date.now() + 60 * 60 * 1000);
      return localInput(d);
    },
  },
  {
    short: 'завтра',
    label: 'завтра утром',
    value: () => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      d.setHours(9, 0, 0, 0);
      return localInput(d);
    },
  },
  {
    short: 'неделя',
    label: 'через неделю',
    value: () => {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      d.setHours(10, 0, 0, 0);
      return localInput(d);
    },
  },
];

function localInput(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}


// Окно закрытия задачи. Своё, а не `window.prompt`.
//
// Закрытие задачи — самое частое действие дня: в amoCRM того же аккаунта
// 31 301 задача семи типов, и менеджеры работают именно по ним.
// `window.prompt` для такого не годится по трём причинам, и третья
// решающая: он блокирует страницу, он не наш по виду, а Chrome после
// нескольких подряд предлагает «запретить этому сайту создавать
// диалоги» — после чего закрытие задач перестаёт работать МОЛЧА. Кнопка
// нажимается, ничего не происходит, в журнале чисто.
//
// Один компонент на оба места — карточку и раздел «Задачи». Прежде это
// действие было написано дважды и вело себя по-разному: в карточке
// «Отмена» закрывала задачу, потому что `null || ''` даёт пустую строку.
/* Чем чаще всего кончается задача. Четыре исхода закрывают почти
   всякий звонок; дальше менеджер дописывает словами.

   Не делаем результат обязательным — это меняло бы работу людей, и
   такое решает владелец. Но закрытая без результата задача ничего не
   сообщает следующему, кто откроет сделку: он видит «связаться»
   закрытым и не знает, дозвонились или нет. Кнопка дешевле
   принуждения: одно нажатие вместо набора. */
const TASK_RESULTS = ['дозвонился', 'не дозвонился', 'перенёс', 'отказ'];

function TaskDoneDialog({ task, onClose, onDone, onNext }) {
  const [result, setResult] = useState('');
  const [busy, setBusy] = useState(false);
  /* Следующий шаг предлагается сразу, как в amoCRM: там задачу нельзя
     закрыть, не решив, что дальше, и именно это держит воронку. У нас
     так не было — и на боевой три тысячи семьсот сделок без единой
     задачи, то есть без следующего шага вообще. Решение Максима
     21.10.2026: делать как в amo.

     Снять галочку можно: разговор бывает окончен — клиент купил или
     отказался. Но тогда это решение человека, а не умолчание. */
  const [nextOn, setNextOn] = useState(true);
  const [nextTitle, setNextTitle] = useState('');
  const [nextDue, setNextDue] = useState('');

  // Поля чистим при каждой новой задаче: иначе результат предыдущей
  // подставится в следующую, а это чужой текст в чужой задаче.
  useEffect(() => {
    setResult('');
    setNextOn(true);
    setNextTitle('');
    // Завтра утром — самый частый следующий шаг после разговора.
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    setNextDue(QUICK_DUE[1] ? QUICK_DUE[1].value() : d.toISOString().slice(0, 16));
  }, [task?.id]);

  if (!task) return null;

  const close = async () => {
    setBusy(true);
    try {
      await onDone(task, result.trim());
      if (nextOn && onNext) {
        // Пустое название — не повод не ставить задачу: человек уже
        // сказал, что следующий шаг нужен, галочкой. «Связаться» —
        // ровно то, что в таком случае и имеется в виду.
        await onNext(task, {
          title: nextTitle.trim() || 'Связаться с клиентом',
          type: 'Связаться',
          dueAt: nextDue,
        });
      }
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!task} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="funnel-ui max-w-sm">
        <DialogHeader>
          <DialogTitle>Закрыть задачу</DialogTitle>
          <DialogDescription>
            {task.title || task.type || 'Задача'}
          </DialogDescription>
        </DialogHeader>
        <Input
          autoFocus
          value={result}
          placeholder="что вышло — можно не писать"
          onChange={(e) => setResult(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !busy) close(); }}
        />
        <div className="flex flex-wrap gap-1">
          {TASK_RESULTS.map((r) => (
            <button key={r} type="button" className={`fn-chip${result === r ? ' is-on' : ''}`}
                    onClick={() => setResult(result === r ? '' : r)}>
              {r}
            </button>
          ))}
        </div>
        {onNext && (
          <div className="mt-1 border-t pt-3" style={{ borderColor: 'var(--fn-line)' }}>
            <label className="flex items-center gap-2 text-[length:var(--fn-t-md)] leading-5">
              <input type="checkbox" checked={nextOn}
                     onChange={(e) => setNextOn(e.target.checked)} />
              Поставить следующую задачу
            </label>
            {nextOn && (
              <div className="mt-2 space-y-1.5">
                <Input
                  value={nextTitle}
                  placeholder="что дальше — если не вписать, будет «Связаться с клиентом»"
                  onChange={(e) => setNextTitle(e.target.value)}
                />
                <div className="flex flex-wrap gap-1">
                  {QUICK_DUE.map((q) => (
                    <button key={q.label} type="button"
                            className={`fn-chip${nextDue === q.value() ? ' is-on' : ''}`}
                            onClick={() => setNextDue(q.value())}>
                      {q.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Отмена</Button>
          <Button onClick={close} disabled={busy}>
            {nextOn && onNext ? 'Закрыть и поставить следующую' : 'Закрыть задачу'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/** Вложения сделки: файлы и ссылки.
 *
 * Менеджеры прикладывают то фотографию участка, то скан договора, то
 * ссылку на диск с проектом. До сих пор прикладывать было некуда: файл
 * уходил в WhatsApp и там оставался — найти его через полгода значило
 * пролистать переписку целиком.
 *
 * Ссылка и файл в одном списке намеренно: для менеджера это одно
 * действие — «приложить к сделке», — и разводить их по двум спискам
 * значит заставлять помнить, куда он положил в прошлый раз.
 */
function FilesBlock({ leadId, locked, onChanged }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [link, setLink] = useState({ url: '', name: '' });
  const picker = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/funnel/leads/${leadId}/files`,
                            { headers: authHeaders() });
      if (r.ok) setFiles((await r.json()).files || []);
    } catch { /* сеть моргнула — список покажем в следующий раз */ }
  }, [leadId]);

  useEffect(() => { setLinkOpen(false); setLink({ url: '', name: '' }); load(); }, [leadId, load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      // Content-Type не ставим руками: границу multipart проставляет сам
      // браузер, а заданный вручную заголовок её затирает и сервер
      // получает тело, которое не может разобрать.
      const r = await fetch(`${API_URL}/api/funnel/leads/${leadId}/files`,
                            { method: 'POST', headers: authHeaders(), body: form });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Ошибка ${r.status}`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); if (picker.current) picker.current.value = ''; }
  };

  const addLink = async () => {
    if (!link.url.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/funnel/leads/${leadId}/links`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(link),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Ошибка ${r.status}`);
      setLink({ url: '', name: '' }); setLinkOpen(false);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  /* Скачиваем запросом, а не обычной ссылкой: ручка закрыта токеном, а
     <a href> заголовок Authorization не отправит — вышла бы ссылка,
     которая у всех открывается ошибкой доступа. */
  const download = async (f) => {
    try {
      const r = await fetch(`${API_URL}/api/funnel/leads/${leadId}/files/${f.id}`,
                            { headers: authHeaders() });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `Ошибка ${r.status}`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = f.name || 'файл';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (f) => {
    // Последствие называем прямо: корзины у вложений нет, файл стирается
    // с диска, и вернуть его нечем. Остальные подтверждения в разделе
    // сделаны так же — «пропадёт с доски и из статистики», «отменить
    // передачу нельзя»; это единственное, где я об этом забыл.
    if (!window.confirm(f.kind === 'link'
      ? `Убрать ссылку «${f.name}» из сделки?`
      : `Удалить «${f.name}»? Файл будет стёрт с сервера, вернуть его нечем.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/funnel/leads/${leadId}/files/${f.id}`,
                            { method: 'DELETE', headers: authHeaders() });
      if (!r.ok) throw new Error(`Ошибка ${r.status}`);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.message); }
  };

  const sizeOf = (n) => (n >= 1024 * 1024
    ? `${(n / 1024 / 1024).toFixed(1)} МБ`
    : `${Math.max(1, Math.round(n / 1024))} КБ`);

  return (
    <section className="mt-4">
      <div className="mb-1.5 flex items-baseline gap-3">
        <h3 className="fn-section-label">Вложения</h3>
        {!locked && (
          <div className="ml-auto flex items-center gap-3">
            <button type="button" disabled={busy}
                    onClick={() => picker.current?.click()}
                    className="fn-section-label hover:text-[color:var(--fn-ink-2)]">
              {busy ? 'грузим…' : '+ файл'}
            </button>
            <button type="button" onClick={() => setLinkOpen((v) => !v)}
                    className="fn-section-label hover:text-[color:var(--fn-ink-2)]">
              + ссылка
            </button>
          </div>
        )}
      </div>

      <input ref={picker} type="file" className="hidden"
             onChange={(e) => upload(e.target.files?.[0])} />

      {linkOpen && (
        <div className="mb-2 space-y-1.5">
          <Input value={link.url} onChange={(e) => setLink((l) => ({ ...l, url: e.target.value }))}
                 placeholder="https://..." className="h-8 text-[length:var(--fn-t-md)] leading-5" />
          <Input value={link.name} onChange={(e) => setLink((l) => ({ ...l, name: e.target.value }))}
                 placeholder="как назвать (необязательно)"
                 className="h-8 text-[length:var(--fn-t-md)] leading-5"
                 onKeyDown={(e) => { if (e.key === 'Enter') addLink(); }} />
          <div className="flex gap-2">
            <button type="button" className="fn-chip" disabled={busy} onClick={addLink}>приложить</button>
            <button type="button" className="fn-chip" onClick={() => setLinkOpen(false)}>отмена</button>
          </div>
        </div>
      )}

      {files.length === 0 ? (
        <p className="text-[length:var(--fn-t-sm)] leading-4 fn-ink-3">пока ничего не приложено</p>
      ) : (
        <ul className="fn-files">
          {files.map((f) => (
            <li key={f.id} className="fn-file-row">
              {f.kind === 'link' ? (
                <a href={f.url} target="_blank" rel="noreferrer noopener"
                   className="fn-link min-w-0 flex-1 truncate" title={f.url}>{f.name}</a>
              ) : (
                <button type="button" onClick={() => download(f)}
                        className="fn-link min-w-0 flex-1 truncate text-left"
                        title={`${f.name} · ${sizeOf(f.size || 0)}`}>
                  {f.name}
                </button>
              )}
              <span className="shrink-0 text-[length:var(--fn-t-xs)] leading-4 fn-ink-3">
                {f.kind === 'link' ? 'ссылка' : sizeOf(f.size || 0)}
              </span>
              {!locked && (
                <button type="button" onClick={() => remove(f)} title="Убрать из сделки"
                        className="shrink-0 fn-ink-3 hover:text-[color:var(--fn-danger)]">
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TaskBlock({ leadId, managers, onChanged }) {
  const [tasks, setTasks] = useState([]);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({ title: '', type: 'Связаться', dueAt: '', assignedTo: '' });
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  // Задача, которую закрываем: окно спрашивает результат.
  const [closing, setClosing] = useState(null);

  const load = useCallback(async () => {
    const [t, ty] = await Promise.all([
      fetch(`${API_URL}/api/funnel/leads/${leadId}/tasks`, { headers: authHeaders() }),
      fetch(`${API_URL}/api/funnel/task-types`, { headers: authHeaders() }),
    ]);
    if (t.ok) setTasks((await t.json()).tasks || []);
    if (ty.ok) setTypes((await ty.json()).types || []);
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const call = async (path, options) => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/funnel${path}`, { headers: authHeaders(), ...options });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.detail || `Ошибка ${r.status}`);
      await load();
      onChanged();
      return b;
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!form.title.trim() && !form.type) return;
    await call(`/leads/${leadId}/tasks`, {
      method: 'POST',
      body: JSON.stringify({
        ...form,
        dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : null,
        assignedTo: form.assignedTo || null,
      }),
    });
    setForm({ title: '', type: 'Связаться', dueAt: '', assignedTo: '' });
    setAdding(false);
  };

  const open = tasks.filter((t) => !t.done);
  const closed = tasks.filter((t) => t.done);

  return (
    <section>
      {/* Подпись блока и действие при ней набраны одним кеглем: это одна
          строка, и разными размерами их набирать незачем. */}
      <div className="mb-1.5 flex items-baseline gap-3">
        {/* Линейки через всю ширину здесь нет: рядом группы полей уже
            отделены серой полосой заголовка, и вторая линия делит колонку
            ещё раз, ничего не сообщая. */}
        <h3 className="fn-section-label">Задачи</h3>
        <button type="button" onClick={() => setAdding((v) => !v)}
                className="fn-section-label ml-auto hover:text-[color:var(--fn-ink-2)]">
          {adding ? 'отмена' : '+ поставить'}
        </button>
      </div>

      {adding && (
        <div className="mb-2 space-y-2 rounded border p-2.5" style={{ borderColor: 'var(--fn-line)' }}>
          <Input autoFocus value={form.title} className="h-8"
                 placeholder="что сделать"
                 onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          {/* Тип и исполнитель — в ряд, срок — своей строкой.
              Все трое в ряд помещались только на широком экране: внутри
              колонки карточки на поле срока оставалось 83 пикселя, и
              «22.10.2026 09:00» показывалось как «22.10.202» — год
              обрезан, времени не видно вовсе. Значение при этом лежало
              верное, то есть человек не мог прочитать то, что сам
              только что выбрал. */}
          <div className="grid gap-2 grid-cols-2">
            <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.assignedTo || 'me'}
                    onValueChange={(v) => setForm((f) => ({ ...f, assignedTo: v === 'me' ? '' : v }))}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="me">себе</SelectItem>
                {managers.map((m) => <SelectItem key={m.username} value={m.username}>{m.fullName || m.username}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <div className="flex flex-col gap-1">
              <Input type="datetime-local" className="h-8 w-full" value={form.dueAt}
                     onChange={(e) => setForm((f) => ({ ...f, dueAt: e.target.value }))} />
              {/* Сроки кнопками. Набирать дату и время в нативном поле —
                  больше движений, чем на весь остальной разговор, и люди
                  оставляют поле пустым. А задача без срока делает сделку
                  на доске МОЛЧАЩЕЙ: «просрочено» для неё не считается,
                  «без задачи» тоже ложно, и карточка выглядит так же, как
                  та, где всё в порядке. Два срока закрывают почти всё —
                  сегодня вечером и завтра утром; третий нужен для
                  «перезвонить на той неделе». */}
              <div className="flex flex-wrap gap-1">
                {QUICK_DUE.map((q) => (
                  <button key={q.label} type="button" className="fn-chip"
                          onClick={() => setForm((f) => ({ ...f, dueAt: q.value() }))}>
                    {q.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <Button size="sm" className="w-full" onClick={submit} disabled={busy}>Поставить</Button>
        </div>
      )}

      <TaskDoneDialog
        task={closing}
        onClose={() => setClosing(null)}
        onDone={(t, result) => call(`/tasks/${t.id}/done`, {
          method: 'POST', body: JSON.stringify({ result }),
        })}
        onNext={(t, next) => call(`/leads/${leadId}/tasks`, {
          method: 'POST',
          body: JSON.stringify({
            title: next.title,
            type: next.type,
            dueAt: next.dueAt ? new Date(next.dueAt).toISOString() : null,
          }),
        })}
      />

      {open.length === 0 && !adding && (
        <p className="px-2 py-1 text-xs fn-ink-2">задач нет</p>
      )}

      <div className="space-y-1">
        {open.map((t) => {
          const late = t.dueAt && new Date(t.dueAt) <= new Date();
          return (
            <div key={t.id} className="fn-task"
                 style={{ '--fn-task-tone': late ? 'hsl(350 72% 46%)' : 'hsl(215 70% 50%)' }}>
              {/* Значок отвечает «что это и насколько срочно» до чтения:
                  часы — задача впереди, восклицание — просрочена. Раньше
                  здесь стоял пустой квадрат, одинаковый в обоих случаях. */}
              <button type="button" disabled={busy}
                      onClick={() => setClosing(t)}
                      className="fn-task-check" title="Закрыть задачу">
                {late ? <AlertTriangle className="h-3.5 w-3.5" />
                      : <Clock className="h-3.5 w-3.5" />}
              </button>
              <div className="min-w-0 flex-1">
                <div className="fn-task-meta">
                  {t.dueAt && (
                    <span className={late ? 'fn-task-when' : ''}>
                      {dueWord(t.dueAt)}
                    </span>
                  )}
                  {t.assignedTo && <> · для {managerLabel(t.assignedTo)}</>}
                  {t.auto && !t.assignedTo && ' · поставлена системой'}
                </div>
                <div className="fn-task-title">
                  <span className="font-medium">{t.type}</span>
                  {t.title && <> — {t.title}</>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {closed.length > 0 && (
        <details className="mt-1.5">
          <summary className="cursor-pointer select-none text-[length:var(--fn-t-xs)] leading-4 fn-ink-2 hover:opacity-70">
            закрытые ({closed.length})
          </summary>
          <div className="mt-1 space-y-1">
            {closed.map((t) => (
              <div key={t.id} className="flex items-start gap-2 px-2.5 py-1 text-[length:var(--fn-t-sm)] leading-4 fn-ink-2">
                <CheckSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 fn-good" />
                <span>
                  <span className="line-through">{t.title}</span>
                  {t.result && <span className="opacity-70"> — {t.result}</span>}
                  <span className="block text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">
                    {managerLabel(t.doneBy)} · {t.doneAt ? new Date(t.doneAt).toLocaleDateString('ru-RU') : ''}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

/** Карточка сделки.
 *
 * Одна и та же начинка показывается двумя способами: страницей по адресу
 * /lead/IN-… и окном поверх доски. Страница — основной: у неё есть ссылка,
 * её можно открыть в новой вкладке и послать коллеге, а модальное окно
 * ничего этого не даёт. Окно оставлено для мест, где карточку открывают
 * мимоходом и сразу закрывают.
 */
/** Всплывающая панель у кнопки: общая механика для «…» и «Фильтра».
 *
 * Закрывается по щелчку мимо и по Escape, раскрывается от своей кнопки.
 * Держим своё, а не тянем @radix-ui/react-dropdown-menu: здесь нужны
 * тридцать строк, а не ещё один пакет с порталами и слоями.
 */
function Popup({ trigger, children, align = 'right', width }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <div className="relative shrink-0" ref={box}>
      {trigger({ open, toggle: () => setOpen((v) => !v) })}
      {open && (
        <div className={`fn-menu fn-menu-${align}`} style={width ? { minWidth: width } : undefined}>
          {typeof children === 'function' ? children({ close: () => setOpen(false) }) : children}
        </div>
      )}
    </div>
  );
}

/** Меню «…».
 *
 * Разрушающее и редкое — за одной кнопкой. Удаление стояло кнопкой во всю
 * ширину панели карточки, рядом с «Передать в CRM» и отличалось от неё
 * только цветом рамки; над доской в ряд стояли восемь значков подряд.
 * В amoCRM и то и другое убрано в «…»: найти легко, промахнуться — нет.
 */
function CardMenu({ items, trigger }) {
  if (!items.length) return null;
  return (
    <Popup
      align="right"
      trigger={trigger || (({ open, toggle }) => (
        <button type="button" className="fn-icon-btn" aria-label="Ещё"
                aria-haspopup="menu" aria-expanded={open} onClick={toggle}>
          <MoreHorizontal className="h-4 w-4" />
        </button>
      ))}
    >
      {({ close }) => (
        <div role="menu">
          {items.map((it) => (
            <button key={it.label} type="button" role="menuitem"
                    className={`fn-menu-item${it.danger ? ' is-danger' : ''}`}
                    onClick={() => { close(); it.onSelect(); }}>
              {it.icon}
              {it.label}
            </button>
          ))}
        </div>
      )}
    </Popup>
  );
}

/** Пометить прокручиваемые колонки карточки, у которых есть продолжение.
 *
 * Без этого последняя запись ленты обрезается ровно краем колонки, и
 * обрезок читается как поломка вёрстки, а не как «дальше есть ещё».
 * Пометка ставится атрибутом, затухание рисует CSS — так затухания нет,
 * когда прокручивать нечего, и оно не съедает последнюю строку зря.
 */
/** Пузырь переписки. Один на оба места: вкладку «Переписка» и вставку
 *  беседы в ленте. Была бы вторая разметка — они разошлись бы на первой
 *  же правке, а человек видит их рядом и сравнивает.
 */
function Bubble({ m }) {
  const out = m.meta?.direction !== 'in';
  const time = m.at
    ? new Date(m.at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    : '';
  return (
    <>
      <div className={`fn-bubble ${out ? 'fn-bubble-out' : 'fn-bubble-in'}`}>
        {/* Время плывёт вправо внутри текста: отдельной строкой оно
            удваивало высоту короткой реплики, а короткие тут почти все. */}
        <span className="fn-bubble-meta">
          {m.meta?.external && <span title="Отправлено не отсюда">↗</span>}
          <time>{time}</time>
        </span>
        <span className="whitespace-pre-wrap">{m.text}</span>
      </div>
      {out && m.by && <div className="fn-bubble-author">{managerLabel(m.by)}</div>}
    </>
  );
}

function useScrollFade(deps) {
  useEffect(() => {
    const cols = [...document.querySelectorAll('.fn-lead-scroll')];
    const mark = (el) => {
      const more = el.scrollHeight - el.scrollTop - el.clientHeight > 2;
      el.toggleAttribute('data-more', more);
    };
    // Обработчик прокрутки снимаем так же, как ставим. Раньше снимался
    // только `ResizeObserver`, а `scroll` оставался — и на каждую
    // перерисовку ленты (то есть на каждое сообщение, звонок, комментарий)
    // навешивался ещё один. За день в одной открытой карточке их копятся
    // сотни: каждое движение прокрутки пересчитывает признак столько раз,
    // сколько обработчиков накопилось. Своя функция на элемент нужна
    // именно для снятия: стрелка в аргументе `addEventListener` —
    // анонимная, и `removeEventListener` её не найдёт.
    const bound = cols.map((el) => {
      const fn = () => mark(el);
      mark(el);
      el.addEventListener('scroll', fn, { passive: true });
      return [el, fn];
    });
    const ro = new ResizeObserver(() => cols.forEach(mark));
    cols.forEach((el) => ro.observe(el));
    return () => {
      ro.disconnect();
      bound.forEach(([el, fn]) => el.removeEventListener('scroll', fn));
    };
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Теги сделки: показать, повесить, снять.
 *
 * Свободный ввод, а не список из настроек: словарь тегов — это то, чем
 * команда действительно пользуется, а не то, что кто-то предусмотрел.
 * Подсказка собирается из уже стоящих тегов, поэтому третий менеджер
 * напишет «бот-пиши» так же, как первые два, не сговариваясь.
 */
function LeadTags({ leadId, tags, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState('');
  const [known, setKnown] = useState([]);

  useEffect(() => {
    if (!adding) return;
    fetch(`${API_URL}/api/funnel/tags`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { tags: [] }))
      .then((d) => setKnown(d.tags || []))
      .catch(() => {});
  }, [adding]);

  const put = async (tag, on) => {
    const r = await fetch(`${API_URL}/api/funnel/leads/${leadId}/tag`, {
      method: 'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ tag, on }),
    });
    if (r.ok) { setValue(''); setAdding(false); onChanged(); }
    else toast.error((await r.json().catch(() => ({}))).detail || 'Не вышло');
  };

  return (
    <div className="fn-tags">
      {(tags || []).map((t) => (
        <span key={t} className="fn-chip">
          {t}
          <button type="button" onClick={() => put(t, false)}
                  title="Снять тег" aria-label={`Снять тег ${t}`}>
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      {!adding && (
        <button type="button" className="fn-chip fn-chip-add" onClick={() => setAdding(true)}>
          <Plus className="h-3 w-3" />тег
        </button>
      )}
      {adding && (
        <span className="inline-flex items-center gap-1">
          <input autoFocus list="fn-known-tags" value={value} maxLength={40}
                 onChange={(e) => setValue(e.target.value)}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter' && value.trim()) put(value, true);
                   if (e.key === 'Escape') { setAdding(false); setValue(''); }
                 }}
                 placeholder="новый или из списка"
                 className="fn-inline-input" />
          <datalist id="fn-known-tags">
            {known.map((k) => <option key={k.tag} value={k.tag}>{k.leads}</option>)}
          </datalist>
        </span>
      )}
    </div>
  );
}


/** Запись разговора в ленте — приём из amoCRM, где плеер стоит прямо в
 *  событии звонка.
 *
 *  Ссылку не храним и не запрашиваем заранее: у Binotel она временная, а
 *  звонков в ленте бывает два десятка — просить их все при открытии карточки
 *  значит два десятка запросов ради одного, который послушают.
 *
 *  «Записи нет» — обычный ответ, а не поломка: у короткого или сорвавшегося
 *  звонка её не бывает. Поэтому 404 показываем спокойной строкой, а не
 *  ошибкой.
 */
function CallRecord({ callId }) {
  const [url, setUrl] = useState('');
  const [state, setState] = useState('idle');
  const [why, setWhy] = useState('');

  const load = async () => {
    setState('loading');
    try {
      const r = await fetch(`${API_URL}/api/integrations/binotel/record/${encodeURIComponent(callId)}`,
                            { headers: authHeaders() });
      if (r.status === 404) { setState('none'); return; }
      if (!r.ok) {
        // Сервер различает причины: 503 — ключи телефонии не заданы, 502
        // — Binotel не ответил. «Попробуйте позже» одинаково врало в обоих
        // случаях: в первом ждать бессмысленно, пока не настроят, во
        // втором — осмысленно. Берём текст сервера, а не выдумываем свой.
        const d = await r.json().catch(() => ({}));
        setWhy(d.detail || `Ошибка ${r.status}`);
        setState('error');
        return;
      }
      const d = await r.json();
      if (d.url) { setUrl(d.url); setState('ready'); } else { setState('none'); }
    } catch {
      setWhy('Не удалось связаться с сервером');
      setState('error');
    }
  };

  if (state === 'ready') {
    return <audio className="mt-1 h-8 w-full max-w-sm" controls preload="none" src={url} />;
  }
  if (state === 'none') {
    return <p className="mt-1 text-xs fn-ink-3">Записи этого разговора нет</p>;
  }
  if (state === 'error') {
    // Кнопка остаётся: прежде она исчезала насовсем, и повторить попытку
    // можно было только перезагрузив страницу — при том что половина
    // причин временные.
    return (
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <span className="text-xs fn-ink-3">{why}</span>
        <button type="button" onClick={load} className="fn-mode">Ещё раз</button>
      </div>
    );
  }
  return (
    <button type="button" onClick={load} disabled={state === 'loading'}
            className="fn-mode mt-1 inline-flex items-center gap-1.5">
      {state === 'loading'
        ? <Loader2 className="h-3 w-3 animate-spin" />
        : <Play className="h-3 w-3" />}
      Прослушать
    </button>
  );
}


/* Куда ведёт возврат из карточки. Разделы помечают, откуда её открыли,
   и кнопка называется по месту: иначе она говорит «К доске», а
   возвращает в «Клиенты» — туда, откуда человек пришёл. */
const BACK_TITLES = {
  board: 'К доске', list: 'К списку', tasks: 'К задачам',
  contacts: 'К клиентам', mail: 'К почте',
};

function LeadDialog({ lead, onClose, onChanged, admin, asPage = false, cameFrom = null }) {
  const backTitle = BACK_TITLES[cameFrom] || 'К доске';
  const [data, setData] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [managers, setManagers] = useState([]);
  const [showEmpty, setShowEmpty] = useState(false);
  // Комментарий виден только своим, сообщение уходит клиенту. Их нельзя
  // перепутать, поэтому это два явных режима, а не одно поле.
  const [mode, setMode] = useState('comment');
  // Кто кому — для записи о звонке. Пустое значение разрешено: человек
  // может не помнить, а выбор «по умолчанию исходящий» заполнил бы поле
  // догадкой и сделал бы счётчик входящих неправдой.
  const [logDirection, setLogDirection] = useState('');
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState('');
  const [tab, setTab] = useState('feed');
  const [openGroups, setOpenGroups] = useState({});
  const [feedQuery, setFeedQuery] = useState('');
  const [feedKind, setFeedKind] = useState('all');
  // Реклама свёрнута сразу: её смотрят редко, а строк там больше всего.
  const [closedGroups, setClosedGroups] = useState({ ad: true });

  // Черновик правок. Раньше каждое изменение поля улетало на сервер сразу:
  // задел выпадающий список, выбрал не то — и в базе уже другой этап, а в
  // ленте запись о переходе. Отменить можно было, только помня прежнее
  // значение.
  //
  // Теперь правки копятся здесь и уходят по кнопке. Отправка сообщений и
  // комментариев осталась мгновенной: это не правка карточки, а действие,
  // и откладывать его нельзя.
  const [draft, setDraft] = useState({});
  const dirty = Object.keys(draft).length > 0;
  const [saving, setSaving] = useState(false);

  // Значение поля: из черновика, если правили, иначе с сервера.
  /* Через ref, а не через замыкание: edit объявлен раньше, чем становится
     известен этап, и прочитанное там значение осталось бы вчерашним.
     Объявлен здесь, среди прочих хуков: ниже по коду есть ранние выходы, и
     хук за ними вызывался бы не в каждом рендере. */
  const lockedRef = useRef(false);
  const val = (key, fallback) => (key in draft ? draft[key] : fallback);
  /* Сделка на этапе-исходе не правится: ею распоряжается производство.
     Черновик не копится вовсе — иначе человек заполнил бы поля, нажал
     «Сохранить» и только тогда узнал от сервера, что было нельзя. */
  const edit = (key, value) => {
    if (lockedRef.current) return;
    setDraft((d) => ({ ...d, [key]: value }));
  };
  const [mail, setMail] = useState(null);
  const [mailboxes, setMailboxes] = useState([]);
  const [mailbox, setMailbox] = useState('');
  const [subject, setSubject] = useState('');
  const [similar, setSimilar] = useState([]);

  const leadId = lead?.id;

  const loadCard = useCallback(async () => {
    if (!leadId) return;
    try {
      const r = await fetch(`${API_URL}/api/funnel/leads/${leadId}/card`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Не удалось загрузить карточку');
      setData(await r.json());
    } catch (e) { toast.error(e.message); }
  }, [leadId]);

  useEffect(() => { setData(null); setComment(''); loadCard(); }, [leadId, loadCard]);

  // Похожие ищем при открытии карточки: менеджер должен увидеть «этот
  // клиент уже писал» до того, как начнёт разговор, а не после.
  useEffect(() => {
    if (!leadId) { setSimilar([]); return; }
    fetch(`${API_URL}/api/funnel/leads/${leadId}/similar`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { similar: [] }))
      .then((d) => setSimilar(d.similar || []))
      .catch(() => setSimilar([]));
  }, [leadId]);

  const removeLead = async () => {
    if (!window.confirm('Убрать сделку из работы? Она пропадёт с доски и из статистики. '
      + 'Данные останутся в базе — восстановить можно.')) return;
    try {
      const r = await fetch(`${API_URL}/api/funnel/leads/${leadId}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `Ошибка ${r.status}`);
      toast.success('Сделка убрана');
      onChanged();
      onClose();
    } catch (e) { toast.error(e.message); }
  };

  const merge = async (sourceId, name) => {
    if (!window.confirm(`Присоединить «${name || sourceId}» к этой сделке? `
      + 'Задачи и комментарии переедут сюда, присоединённая пропадёт с доски.')) return;
    try {
      const r = await fetch(`${API_URL}/api/funnel/leads/${leadId}/merge`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceId }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Ошибка ${r.status}`);
      toast.success(d.tasksMoved ? `Присоединено, задач перенесено: ${d.tasksMoved}` : 'Присоединено');
      setSimilar((prev) => prev.filter((x) => x.id !== sourceId));
      loadCard();
      onChanged();
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    fetch(`${API_URL}/api/funnel/managers`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { managers: [] }))
      .then((d) => { rememberManagers(d.managers); setManagers(d.managers || []); })
      .catch(() => {});
  }, []);

  const loadMail = useCallback(async () => {
    if (!leadId) return;
    try {
      const r = await fetch(`${API_URL}/api/funnel/leads/${leadId}/mail`, { headers: authHeaders() });
      if (!r.ok) return;
      const d = await r.json();
      setMail(d);
      setMailboxes(d.mailboxes || []);
      setMailbox((prev) => prev || (d.mailboxes || [])[0] || '');
    } catch { /* почта не настроена — карточка работает и без неё */ }
  }, [leadId]);

  // Ходим в Gmail только когда почту открыли: поиск по каждому ящику это
  // отдельный запрос к Google, и делать его на каждое открытие карточки —
  // значит ждать секунду там, где обычно нужен только телефон.
  useEffect(() => { if (tab === 'mail') loadMail(); }, [tab, loadMail]);
  // Смена сделки чистит всё, что относилось к прежней, — и режим ответа
  // тоже. Он не сбрасывался: поработав в режиме «WhatsApp» у одного
  // клиента, следующему можно было отправить сообщение по ⌘+Enter, думая,
  // что пишешь внутреннюю заметку. Из всех состояний карточки липло
  // именно то, у которого необратимое последствие.
  useEffect(() => {
    setTab('feed'); setMail(null); setSubject(''); setDraft({});
    setMode('comment');
    // Разбор ленты тоже сбрасывается. Иначе фильтр, поставленный на
    // одной сделке, переезжает на следующую, и её лента открывается
    // пустой — а пустая лента читается как «тут ничего не было», а не
    // как «показаны только примечания». Поиск липнул так же и до
    // появления фильтра.
    setFeedQuery(''); setFeedKind('all');
  }, [leadId]);

  // Переписка — те же события ленты, но только реплики и в обратном
  // порядке: разговор читается сверху вниз, а лента сверху вниз от нового
  // к старому. Отдельного запроса не делаем, всё уже пришло с карточкой.
  // Порядок тот же, что в ленте: новые сверху. Раньше здесь было как в
  // мессенджере — старые сверху, — и соседние вкладки показывали одно и то
  // же с разных концов. Наверху ленты последнее сообщение, наверху
  // переписки первое, и это читается как «разные сообщения».
  // Лента: мелкие записи сворачиваем в одну строку — как в amoCRM, где
  // это «10 событий — развернуть». Сворачиваются только эти три вида:
  // правка поля, системная запись и срабатывание правила. Звонок,
  // сообщение и задача остаются каждый своей строкой — это события, про
  // которые менеджер думает отдельно.
  //
  // Сворачиваем по виду записи и дню, а не по близости во времени. Здесь
  // стоял обратный комментарий и мёртвый расчёт разницы в 90 секунд: он
  // считался на каждую запись ленты и никуда не шёл. Правило по времени
  // разложило бы одно действие бота на две строки, если система записала
  // его с паузой, — а группировка по виду собирает его целиком.
  const QUIET = new Set(['edit', 'system', 'rule']);
  // Чем лента фильтруется. Группы собраны по вопросу, с которым в неё
  // лезут: «что мы друг другу сказали», «что я записал», «что обещано»,
  // «кто что менял». Типы перечислены поимённо и взяты из тех, что
  // пишет бэкенд (add_event), а не из того, что попалось в одной
  // карточке: тип, забытый в списке, пропал бы из ленты молча.
  const FEED_KINDS = [
    ['all', 'Всё', null],
    ['talk', 'Разговоры', new Set(['call', 'message', 'mail'])],
    ['note', 'Примечания', new Set(['comment'])],
    ['task', 'Задачи', new Set(['task'])],
    ['change', 'Изменения', new Set(['edit', 'stage', 'rule', 'system',
                                     'created', 'duplicate', 'handoff',
                                     'relay', 'doc', 'file'])],
  ];
  // Шесть реплик — три обмена: видно, чем разговор кончился и на чём
  // стоит. Больше — и беседа начинает вытеснять из ленты всё остальное.
  const THREAD_IN_FEED = 6;
  // Пересчёт после каждой перерисовки ленты: пришли новые записи —
  // изменилась высота, значит и признак «есть продолжение» другой.
  useScrollFade([data, tab]);

  // Переписка открывается на свежем. При хронологическом порядке новое
  // внизу, и без прокрутки вкладка встречала бы сообщениями недельной
  // давности.
  useEffect(() => {
    if (tab !== 'chat') return;
    const el = document.querySelector('.fn-chat.fn-lead-scroll');
    if (el) el.scrollTop = el.scrollHeight;
  }, [tab, data]);

  const feedRows = useMemo(() => {
    const rows = [];
    const needle = feedQuery.trim().toLowerCase();
    const kinds = (FEED_KINDS.find((k) => k[0] === feedKind) || [])[2];
    for (const e of data?.feed || []) {
      // Поиск по ленте: на длинной истории «когда отправляли КП» иначе не
      // найти, а листать её приходится каждый раз с начала. Ищем по тексту
      // и по автору — чаще всего помнят именно их.
      if (needle && !`${e.text || ''} ${e.by || ''}`.toLowerCase().includes(needle)) continue;
      if (kinds && !kinds.has(e.type)) continue;
      const last = rows[rows.length - 1];
      // Переписка собирается в беседу: подряд идущие сообщения одного дня
      // становятся одной вставкой с пузырями, как в amoCRM. Отдельными
      // карточками разговор не читался — реплики выглядели как события,
      // а не как то, что люди сказали друг другу.
      if (e.type === 'message') {
        const sameDay = last && last.thread && dayLabel(last.items[0].at) === dayLabel(e.at);
        if (sameDay) last.items.push(e);
        else rows.push({ thread: true, items: [e], id: `t-${e.id}` });
      } else if (QUIET.has(e.type) && last && last.group && last.items[0].type === e.type
                 && dayLabel(last.items[0].at) === dayLabel(e.at)) {
        // Схлопываем по виду записи, а не по минуте: «изменение поля —
        // 10 событий» говорит, что там внутри, а «5 изменений за минуту»
        // — только что их было пять. Приём из amoCRM.
        last.items.push(e);
      } else if (QUIET.has(e.type)) {
        rows.push({ group: true, items: [e], id: `g-${e.id}` });
      } else {
        rows.push({ group: false, items: [e], id: e.id });
      }
    }
    return rows;
  }, [data, feedQuery, feedKind]); // eslint-disable-line react-hooks/exhaustive-deps

  // Хронология, а не порядок ленты. Лента идёт новыми вверх — это список
  // событий. Переписка — разговор, и его читают сверху вниз: вопрос, под
  // ним ответ. Пока вкладка шла в порядке ленты, один и тот же разговор
  // читался в ней и во вставке ленты по-разному.
  const chatMessages = useMemo(
    () => (data?.feed || []).filter((e) => e.type === 'message').slice().reverse(),
    [data],
  );

  useEffect(() => {
    fetch(`${API_URL}/api/funnel/chat/channels`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { channels: [] }))
      .then((d) => {
        const list = d.channels || [];
        setChannels(list);
        // Заблокированных и неоплаченных каналов у аккаунта больше, чем
        // рабочих, — подставляем сразу тот, с которого письмо уйдёт.
        setChannelId((list.find((c) => c.ready) || {}).id || '');
      })
      .catch(() => {});
  }, []);

  const call = async (path, options) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/funnel/leads/${leadId}${path}`, {
        headers: authHeaders(), ...options,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || `Ошибка ${res.status}`);
      await loadCard();
      onChanged();
      return body;
    } catch (e) {
      toast.error(e.message);
      throw e;
    } finally { setBusy(false); }
  };

  // Сохранение черновика. Этап и ответственный уходят своими ручками —
  // они пишут в ленту «этап такой-то → такой-то» и «назначен», и через
  // общий /card этой записи не будет.
  /** Перенос на этап сразу, без черновика.
   *
   * Тем же запросом, что и перетаскивание по доске: один способ сменить
   * этап — одна дорога на сервер. Черновик по этому полю заодно
   * чистится, иначе в полосе «не сохранено» осталось бы изменение,
   * которое уже применено.
   */
  const moveStageNow = async (stageId) => {
    if (lockedRef.current || stageId === L.stageId) return;
    setDraft((d) => { const { stageId: _drop, ...rest } = d; return rest; });
    try {
      await call('/stage', {
        method: 'PUT',
        body: JSON.stringify({ stageId, lossReason: L.lossReason || '' }),
      });
      const name = (data?.stages || []).find((st) => st.id === stageId)?.name || 'другой этап';
      toast.success(`Этап: ${name}`);
    } catch (e) {
      toast.error(e.message || 'Не удалось перенести');
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const { stageId, assignedTo, ...fields } = draft;
      if (Object.keys(fields).length) {
        await call('/card', { method: 'PUT', body: JSON.stringify(fields) });
      }
      if (stageId !== undefined && stageId !== L.stageId) {
        // Причину отказа отправляем вместе с переносом. Сервер требует её
        // в теле запроса и в сохранённое поле не смотрит — а карточка
        // раньше посылала один `stageId`. Выбрать в карточке «Закрыто и
        // не реализовано» было нельзя вовсе: сохранение падало с «требует
        // причины отказа», а дать её было нечем, потому что селект
        // появлялся только у сделки, УЖЕ стоящей на отказном этапе.
        // Единственным путём оставалось перетащить карточку мышью по
        // доске — на экране, который для работы со сделкой и открывают.
        const lossReason = fields.lossReason !== undefined
          ? fields.lossReason : (L.lossReason || '');
        await call('/stage', {
          method: 'PUT',
          body: JSON.stringify({ stageId, lossReason }),
        });
      }
      if (assignedTo !== undefined && assignedTo !== (L.assignedTo || '')) {
        await call('/assign', { method: 'PUT', body: JSON.stringify({ assignedTo }) });
      }
      setDraft({});
      toast.success('Сохранено');
    } catch (e) {
      // call сам показал причину; черновик не чистим — человек не должен
      // потерять правки из-за неудачного запроса.
    } finally {
      setSaving(false);
    }
  };

  // Уход со страницы с несохранённым: браузер спросит сам. Текст задаёт
  // он, наш не показывается — это защита от сайтов, ворующих внимание.
  //
  // Но `beforeunload` ловит только уход из вкладки. Переход «К доске» и
  // кнопка «назад» идут через history внутри приложения, вкладку не
  // закрывают — и правки гибли молча. Черновик с явным сохранением был
  // лучшим, что есть в карточке, и подводил в самом частом переходе.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  /** Уйти с карточки, спросив про несохранённое. */
  const leave = useCallback(() => {
    if (dirty && !window.confirm(
      'В карточке есть несохранённые правки. Уйти и потерять их?')) return;
    onClose();
  }, [dirty, onClose]);

  const saveCard = (patch) => call('/card', { method: 'PUT', body: JSON.stringify(patch) }).catch(() => {});

  const addComment = async () => {
    if (!comment.trim()) return;
    await call('/comment', { method: 'POST', body: JSON.stringify({ text: comment }) });
    setComment('');
  };

  /** Отметить разговор, который прошёл мимо системы.
   *
   * Звонок с личного телефона и письмо из своего ящика до сих пор не
   * попадали в карточку никак: разговор был, а в ленте тишина — и
   * следующий, кто откроет сделку, видел её как брошенную. Запись идёт
   * событием того же вида, что приходят от телефонии и почты, поэтому
   * попадает и в фильтр «Разговоры», и в счётчик звонков в шапке.
   */
  const logContact = async (kind) => {
    if (!comment.trim()) return;
    await call('/comment', {
      method: 'POST',
      body: JSON.stringify({ text: comment, kind, direction: logDirection }),
    });
    setComment('');
    toast.success(kind === 'call' ? 'Звонок записан' : 'Письмо записано');
  };

  const sendMessage = async () => {
    if (!comment.trim() || !channelId) return;
    await call('/message', {
      method: 'POST',
      body: JSON.stringify({ channelId, text: comment }),
    });
    setComment('');
    toast.success('Сообщение отправлено клиенту');
  };

  const sendMail = async () => {
    if (!comment.trim() || !mailbox) return;
    await call('/mail', {
      method: 'POST',
      body: JSON.stringify({ mailbox, subject, text: comment }),
    });
    setComment('');
    setSubject('');
    toast.success('Письмо отправлено');
    if (tab === 'mail') loadMail();
  };

  const submit = () => {
    if (mode === 'message') return sendMessage();
    if (mode === 'mail') return sendMail();
    // Отметка о том, что уже случилось, а не отправка: «Звонок» и
    // «Письмо вне системы» ничего клиенту не шлют.
    if (mode === 'logCall') return logContact('call');
    if (mode === 'logMail') return logContact('mail');
    return addComment();
  };

  const handOff = async () => {
    if (!window.confirm('Передать сделку в CRM саун? Отменить передачу нельзя.')) return;
    const r = await call('/to-crm', { method: 'POST' }).catch(() => null);
    if (r) toast.success(`Передано в CRM как ${r.crmLeadId}`);
  };

  if (!lead) return null;

  const L = data?.lead || lead;

  /* Последняя причина отказа, которую по этой сделке уже называли.
     Объявлено ПОСЛЕ `L` и не выше: первая попытка стояла до неё, и
     карточка падала с «Cannot access 'L' before initialization» — той
     же ошибкой, что 20.10 положила боевую белым экраном. Там читали
     зависимость эффекта до объявления, здесь — поле лида; причина одна
     и та же, и файл на шесть тысяч строк ей способствует. */
  const lastLossReason = (() => {
    const past = (L?.stageHistory || []).filter((h) => (h.lossReason || '').trim());
    return past.length ? past[past.length - 1].lossReason : '';
  })();
  const fields = data?.cardFields || [];
  const stage = (data?.stages || []).find((s) => s.id === (L.stageId || 'unsorted'));
  const locked = !!stage?.readOnly;
  lockedRef.current = locked;
  // Имя, телефон и почта живут на самом лиде — их читает дедуп и доска.
  // Остальное лежит в fields, который заполняется по каталогу из настроек.
  const fieldValue = (f) => {
    if (f.id === 'client_name') return L.name || '';
    if (f.id === 'client_phone') return L.phone || '';
    if (f.id === 'client_email') return L.email || '';
    return (L.fields || {})[f.id] || '';
  };

  /* Ответы, которые клиент дал в другой своей заявке.
   *
   * Человек заполняет квиз, потом пишет в WhatsApp — и получается две
   * заявки: рабочая пустая, а все ответы лежат на той, что помечена
   * дублем и с доски убрана. Менеджер звонит с нулём на руках и
   * спрашивает то, на что человек уже ответил. Так и было у Zbigniew
   * Adamski: в рабочей карточке ноль полей, в дубле четырнадцать.
   *
   * Поля дублей уже приезжают в карточку, их просто не показывали.
   * Своё значение всегда главнее: показываем чужое, только если своего
   * нет, и честно подписываем, откуда оно. */
  const borrowedValue = (f) => {
    if (fieldValue(f)) return null;
    for (const dup of data?.duplicates || []) {
      const v = (dup.fields || {})[f.id];
      if (String(v || '').trim()) return { value: v, from: dup };
    }
    return null;
  };

  const onFieldSave = (f, val) => {
    if (f.id === 'client_name') return saveCard({ name: val });
    if (f.id === 'client_phone') return saveCard({ phone: val });
    if (f.id === 'client_email') return saveCard({ email: val });
    return saveCard({ fields: { [f.id]: val } });
  };

  // Вне Dialog радиксовые заголовки использовать нельзя, а внутри — нужно:
  // на них держится доступность окна. Поэтому подменяем теги, а не разметку.
  const Title = asPage ? 'h1' : DialogTitle;
  const Subtitle = asPage ? 'div' : DialogDescription;

  const body = (
    <>
        {/* Почему поля не правятся. Без этой строки человек решил бы, что
            карточка сломалась: поля есть, а не вводятся. */}
        {locked && (
          <div className="space-y-1 px-6 pt-3">
            <div className="fn-note fn-note-done">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Сделка на этапе «{stage.name}» — ею занимается производство.
              Поля и этап здесь не меняются; комментарий в ленту написать можно.
            </div>
          </div>
        )}
        {(L.crmLeadId || L.duplicateOf) && (
          <div className="space-y-1 px-6 pt-3">
            {L.crmLeadId && (
              <div className="fn-note fn-note-done">
                <ArrowRight className="h-3.5 w-3.5" />
                Передан в CRM саун как <span className="font-mono">{L.crmLeadId}</span>
              </div>
            )}
            {L.duplicateOf && (
              <div className="fn-note fn-note-warn">
                <Copy className="h-3.5 w-3.5" />
                Дубль заявки <span className="font-mono">{L.duplicateOf}</span>
              </div>
            )}
          </div>
        )}

        {/* Прошлые обращения этого человека — одной строкой, подробности
            по клику.

            Было два раскрытых блока подряд: «похоже, клиент уже есть» и
            «обращался ещё раз». С длинным текстом заявки они съедали треть
            экрана, и карточка начиналась не с клиента, а со служебной
            справки о нём. Причём справки о прошлом — а менеджер открывает
            карточку, чтобы работать с настоящим.

            Свёрнуто по умолчанию и объединено в один блок: вопрос у
            менеджера один — «мы с ним уже говорили?», а деление на
            «похожие» и «повторные» техническое, из разных мест кода. */}
        {(similar.length > 0 || data?.duplicates?.length > 0) && (
          <div className="px-6 pt-3">
            <details className="fn-note fn-note-dup fn-dup">
              <summary className="fn-dup-head">
                <Copy className="h-3.5 w-3.5 shrink-0" />
                <span className="font-medium">Клиент уже обращался</span>
                <span className="fn-ink-2">
                  {[similar.length && `${similar.length}\u00a0${plural(similar.length, 'заявка', 'заявки', 'заявок')}`,
                    data?.duplicates?.length && `ещё\u00a0${data.duplicates.length}\u00a0${plural(data.duplicates.length, 'раз', 'раза', 'раз')}`]
                    .filter(Boolean).join(' · ')}
                </span>
                <ChevronDown className="fn-dup-chev h-3.5 w-3.5 shrink-0" />
              </summary>

              <div className="mt-2 space-y-1">
                {similar.map((x) => (
                  <div key={x.id} className="flex flex-wrap items-center gap-x-2 text-[length:var(--fn-t-sm)] leading-4">
                    <time className="tabular-nums opacity-70">
                      {x.receivedAt ? new Date(x.receivedAt).toLocaleDateString('ru-RU') : '—'}
                    </time>
                    {/* Присоединённая заявка не удаляется — она лежит в
                        базе помеченной и просто ушла с доски. Значит её
                        карточку можно открыть, и это надо показать: иначе
                        человек видит, что обращение было, и не знает, куда
                        делось. */}
                    <a href={`/lead/${x.id}`} className="underline underline-offset-2 hover:no-underline">
                      {x.name || x.id}
                    </a>
                    <span className="fn-ink-2">{x.phone || x.email}</span>
                    <span className="fn-ink-2">· по {x.matchedBy}</span>
                    {x.duplicateOf && <span className="fn-ink-2">· уже присоединена</span>}
                    {x.crmLeadId && <span className="fn-ink-2">· передана в CRM</span>}
                    {!x.duplicateOf && !x.crmLeadId && (
                      <button type="button" onClick={() => merge(x.id, x.name)}
                              className="ml-auto text-[length:var(--fn-t-sm)] leading-4 underline underline-offset-2 hover:no-underline">
                        присоединить
                      </button>
                    )}
                  </div>
                ))}
                {(data?.duplicates || []).map((d) => (
                  <div key={d.id} className="flex flex-wrap items-baseline gap-x-2 text-[length:var(--fn-t-sm)] leading-4">
                    <time className="tabular-nums opacity-70">
                      {d.receivedAt ? new Date(d.receivedAt).toLocaleString('ru-RU', {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </time>
                    <a href={`/lead/${d.id}`} className="underline underline-offset-2 hover:no-underline opacity-70">
                      {d.sourceLabel || SOURCES[d.source]?.label || d.source}
                    </a>
                    {d.comment && <span className="basis-full opacity-90">«{d.comment}»</span>}
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}

        {/* Левая колонка и лента прокручиваются раздельно — так в amoCRM,
            и так правильно: поля остаются на месте, пока читаешь переписку.
            Прежде я убрал вложенную прокрутку вовсе, и страница поехала
            целиком: чтобы посмотреть начало разговора, приходилось увозить
            из виду и анкету. */}
        <div className="fn-lead-grid grid min-h-0 flex-1 grid-cols-1 gap-0 lg:grid-cols-[minmax(0,21rem)_minmax(0,1fr)]">
          {/* ЛЕВО: что известно о клиенте.
              Тёмная шапка сверху — как в карточке amoCRM, и она там не
              украшение: страница из белых блоков на белом фоне не даёт
              глазу опоры, и всё на ней кажется одинаково важным. Шапка
              задаёт верх, холст под лентой — низ, и между ними появляется
              то, чего не было: разница. */}
          <div className="flex min-h-0 flex-col lg:border-r"
               style={{ borderColor: 'var(--fn-line)' }}>
            <div className="fn-card-head">
          {/* gap-4 мало: номер набран крупно и не сжимается, кнопка не
            сжимается тоже — на узкой колонке они наезжали друг на друга.
            Имя теперь обрезается многоточием, кнопка держит свою ширину. */}
        <div className="flex items-start justify-between gap-5">
          <div className="min-w-0 flex-1">
            <Title className="fn-card-title flex items-center gap-2">
              {/* Название правится прямо в заголовке. Менеджеры пишут в
                  него пометки — «хочет на лето», — и до сих пор для этого
                  оставалось только имя клиента, которое такая пометка
                  затирала: имя уходит в раздел «Клиенты», в передачу в CRM
                  саун и в выгрузку. Теперь у сделки своё название, а
                  пустое означает «звать по клиенту»: подсказкой стоит
                  имя, а номер и заметка идут строками ниже. */}
              <input
                className="fn-title-input min-w-0 flex-1"
                disabled={locked}
                value={val('title', L.title || '')}
                placeholder={leadTitle(L)}
                aria-label="Название сделки"
                onChange={(e) => edit('title', e.target.value)}
              />
              {/* Бейджа этапа здесь нет: он показан полосой ниже, вместе
                  со временем на нём. Два раза одно и то же в двух шагах
                  друг от друга выглядит как недосмотр. */}
              {L.qualification && <Badge variant="secondary">{L.qualification}</Badge>}
            </Title>
            {/* Номер — своей строкой под именем, а не приклеенным к нему
                через точку. Склейка не помещалась в ширину и обрывалась
                посреди номера. Заметка следом: менеджер пишет в неё то,
                что надо помнить про этого человека, и читать её вместе с
                именем правильнее, чем искать в анкете ниже. */}
            {leadSubLines(L).map((line) => (
              <div key={line} className="fn-card-line">
                {/* Номер в шапке — ссылка «позвонить». Самое частое, зачем
                    открывают карточку, это позвонить; номер стоял там, где
                    его читают, но нажать было нельзя — ссылка `tel:` жила
                    только у строки анкеты, ниже задач, селектов и
                    заголовка группы. Чтобы набрать, надо было прокрутить
                    или перепечатать с экрана. */}
                {line === (L.phone || '').trim() ? (
                  <a href={`tel:${line.replace(/[^\d+]/g, '')}`} className="fn-link">
                    {line}
                  </a>
                ) : line}
              </div>
            ))}
            {/* Заметка правится здесь же, а не в анкете ниже. Сперва я
                показал её в шапке и оставил поле в анкете — один и тот же
                текст оказался дважды на одном экране, в шестистах
                пикселях друг от друга. Место должно быть одно, и это
                верх: заметку читают вместе с именем. */}
            <input
              className="fn-note-input fn-card-line"
              disabled={locked}
              value={val('managerNote', L.managerNote || '')}
              placeholder="заметка о клиенте"
              aria-label="Заметка о клиенте"
              onChange={(e) => edit('managerNote', e.target.value)}
            />
            {/* Одна служебная строка, а не три ступеньки под именем.
                Источник, номер и возраст — сведения одного порядка, и
                стоять они должны в один ряд, разделённые точками. */}
            {/* Разделители внутри строки, а не отдельными элементами:
                нарисованные точки переносились на свою строку и висели
                сиротами посреди пустоты. */}
            {/* Переносится, а не обрезается: колонка узкая, а вторая
                строка дешевле обрезанного. */}
            <Subtitle className="fn-card-sub mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="fn-tag" data-src={L.source}
                    style={L.sourceLabel ? { '--tag-h': hueFromText(L.sourceLabel) } : undefined}>
                {L.sourceLabel || SOURCES[L.source]?.label || L.source}
              </span>
              {/* Номер, а не ключ. Ключ «IN-3D0C63AFA6» не назовёшь вслух и
                  не продиктуешь по телефону, а сделку обсуждают голосом.
                  Ключ остался в подсказке: он нужен, когда сверяют с
                  выгрузкой или с передачей в CRM саун. */}
              {L.no != null && <span className="fn-num" title={L.id}>№ {L.no}</span>}
              <span>· {timeAgo(L.receivedAt)} назад</span>
              {/* Ссылка на заявку у площадки. Не украшение: на Oferteo к
                  заявке приложены чертежи и фото места установки — то, по
                  чему называют цену, — а в комментарии приходит только
                  текст. Без ссылки менеджер считает вслепую и не знает,
                  что чего-то не видел. Файлы к себе не тащим: они живут у
                  площадки, и права на них там же. */}
              {L.externalUrl && (
                <a href={L.externalUrl} target="_blank" rel="noopener noreferrer"
                   className="fn-external-link"
                   title="Откроется под учётной записью подрядчика на площадке">
                  <ExternalLink className="h-3 w-3" />
                  заявка на площадке
                </a>
              )}
            </Subtitle>
            <LeadTags leadId={L.id} tags={L.tags} onChanged={loadCard} />
            {/* Сколько труда уже вложено. Приём из amoCRM, где это
                отдельная вкладка «Статистика»: открыв чужую сделку,
                менеджер за секунду видит, звонили человеку десять раз или
                не звонили вовсе — и это меняет решение звонить.

                У нас это строка, а не вкладка: чисел семь, они короткие, и
                прятать их за переключателем значит сделать так, что их не
                посмотрит никто. */}
            {data?.passport && (
              <div className="fn-passport">
                <span title="Дней с получения заявки">
                  <b>{data.passport.days ?? '—'}</b> дн в работе
                </span>
                {data.passport.calls > 0 && (
                  <span title="Всего звонков по сделке; в скобках — пропущенных">
                    <b>{data.passport.calls}</b> звон.
                    {data.passport.callsMissed > 0 && (
                      <i> ({data.passport.callsMissed} проп.)</i>
                    )}
                  </span>
                )}
                {(data.passport.messagesIn + data.passport.messagesOut) > 0 && (
                  <span title="Сообщений от клиента и наших">
                    <b>{data.passport.messagesIn}</b>/<b>{data.passport.messagesOut}</b> сообщ.
                  </span>
                )}
                {data.passport.mails > 0 && <span><b>{data.passport.mails}</b> писем</span>}
                <span title="Задач закрыто и открыто">
                  <b>{data.passport.tasksDone}</b>/<b>{data.passport.tasksOpen}</b> задач
                </span>
                {data.passport.tasksLate > 0 && (
                  <span className="fn-passport-late"
                        title="Задачи, закрытые после срока или просроченные сейчас">
                    <b>{data.passport.tasksLate}</b> с просрочкой
                  </span>
                )}
                {data.passport.stages > 0 && (
                  <span title="Сколько раз меняли этап"><b>{data.passport.stages}</b> смен этапа</span>
                )}
              </div>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* В строке с именем осталось только «…».
                Кнопка стояла здесь и отжимала имя: телефон из двенадцати
                цифр не сжимается, кнопка не сжимается тоже, и на колонке в
                480 пикселей от номера оставалось «+485…». Главное действие
                переехало в строку над карточкой — там его место и там ему
                никто не мешает. Прежний комментарий:
                В левой колонке amoCRM нет ни одной залитой кнопки — там
                только строки «подпись, значение». Синий брусок посреди
                анкеты и был тем, из-за чего колонка читалась не списком, а
                стопкой разных блоков. Шапка держит, кто это, на чём стоит
                и что с этим можно сделать, — три вещи одного рода. */}
            <CardMenu items={admin && !L.crmLeadId
              ? [{ label: 'Удалить сделку', danger: true, onSelect: removeLead,
                   icon: <Trash2 className="h-3.5 w-3.5" /> }]
              : []} />
          </div>
        </div>

        {/* Полоса этапов. В amoCRM это первое, что видно в карточке, и не
            зря: этап и время на нём говорят о ходе сделки, а всё остальное
            в карточке — о клиенте. У нас этап лежал в списке полей наравне
            с почтой и адресом, а времени на нём не было вовсе.

            Показ, а не управление: нажатие двигало бы сделку, а промахнуться
            мышью по пятипиксельной полоске легко. Этап меняется списком ниже,
            осознанно — в amoCRM эта полоса тоже некликабельна. */}
        {(() => {
          const line = (data?.stages || []).filter(
            (st) => (st.pipelineId || 'sales') === (L.pipelineId || 'sales') && !st.isLost);
          if (line.length < 2) return null;
          const here = line.findIndex((st) => st.id === L.stageId);
          const stageName = ((data?.stages || []).find((st) => st.id === L.stageId) || {}).name;
          const since = L.stageChangedAt || L.receivedAt;
          // Считаем календарные дни, а не разницу миллисекунд, и не
          // опускаемся ниже нуля. Сразу после смены этапа сервер пишет
          // своё время, и если его часы хоть на секунду впереди часов
          // браузера, вычитание даёт минус, а Math.floor превращает это
          // в «-1 дней здесь» — первое, что менеджер видит после
          // собственного действия. Замечено 21.10.2026.
          const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
          const days = since
            ? Math.max(0, Math.round(
                (midnight(new Date()) - midnight(new Date(since))) / 86400000)) : null;
          return (
            <div className="mt-3.5">
              {/* Этап — состояние сделки, и показывать его надо так же, как
                  показывают состояние: фишкой в цвете этапа. Прежде это была
                  просто надпись, а под ней во всю ширину лежала полоса —
                  два предмета вместо одного. */}
              <div className="flex items-center gap-2">
                <span className="fn-stage-chip"
                      style={{ '--fn-stage': here >= 0 ? line[here].color : 'var(--fn-ink-3)' }}>
                  <span className="fn-dot" style={{ background: 'var(--fn-stage)' }} />
                  {/* Имя этапа берём из общего списка, а не из линии
                      воронки. Линия нарочно не содержит отказных — по
                      ним не идут, их не показывают полосой прогресса, —
                      и у закрытой сделки поиск в ней не находил ничего,
                      а шапка писала «Этап не задан» при этапе «Закрыто и
                      не реализовано». Неправда о собственном состоянии
                      сделки. «Этап не задан» остаётся для тех, у кого
                      его и правда нет: таких на стенде четыре. */}
                  {stageName || 'Этап не задан'}
                </span>
                {/* Сколько пути пройдено — числом, а не на глазок по
                    сегментам полосы. В логистической панели Ronas IT эту
                    работу делает мини-шкала в шапке карточки: видно, что
                    рейс прошёл половину, не читая названий. Считать глазами
                    восемь делений — это уже не «видно». */}
                {here >= 0 && line.length > 1 && (
                  <span className="fn-step-count">{here + 1} из {line.length}</span>
                )}
                {days !== null && (
                  <span className="text-[length:var(--fn-t-sm)] leading-4 fn-ink-3">
                    {days === 0 ? 'сегодня' : `${days} ${plural(days, 'день', 'дня', 'дней')} здесь`}
                  </span>
                )}
              </div>
              <div className="fn-steps mt-2">
                {line.map((st, i) => (
                  <span key={st.id} title={st.name}
                        className={`fn-step${here >= 0 && here >= i ? ' is-done' : ''}`}
                        style={{ '--step-color': st.color }} />
                ))}
              </div>
            </div>
          );
        })()}
            </div>
            {/* disabled на fieldset отключает каждый ввод внутри нативно —
                и мышью, и с клавиатуры. Пройтись по полям поштучно значило
                бы забыть одно из двадцати в тот день, когда добавится
                двадцать первое. */}
            <fieldset disabled={locked} className="fn-lead-scroll px-6 py-6">
              {/* Следующий шаг — в первом экране карточки.
                  Так говорят разборы устройства CRM: в главном поле зрения
                  должны быть имя, контакт, этап и следующее назначенное
                  действие, остальное — по требованию. Внизу ленты задача
                  висела между лентой и полем ответа отдельным блоком ни к
                  чему не привязанным, и в правой колонке от неё оставался
                  провал. Здесь она читается вместе с этапом: «где сделка и
                  что дальше» — один вопрос. */}
              <div className="mb-4">
                <TaskBlock leadId={L.id} managers={managers} onChanged={loadCard} />
                <FilesBlock leadId={L.id} locked={locked} onChanged={loadCard} />
              </div>

            {/* Тот же вынос на восемь пикселей влево, что у полей ниже.
                Без него две группы стоят по разным левым краям — 81 и 73
                пикселя от края экрана, — и глаз читает это как
                неаккуратность, хотя назвать причину трудно. */}
            <div className="-mx-2 mb-4 grid gap-1">
              <div className="fn-inline">
                <span className="fn-field-label">Этап</span>
                {/* Этап применяется сразу, а не ждёт «Сохранить».
                    На одном экране было три разных правила: комментарий
                    и задача сохранялись сами, перетаскивание карточки по
                    доске — мгновенно, а тот же перенос из этого поля
                    требовал кнопки внизу длинной колонки. Человек
                    выбирал этап, видел в шапке прежний и уходил, не
                    заметив полоски, — перенос не случался.

                    Исключение одно: отказ. Там сервер требует причину, и
                    её выбирают следующим полем — значит отправлять надо
                    вместе, по кнопке. */}
                <Select
                  value={val('stageId', L.stageId || 'unsorted')}
                  onValueChange={(v) => {
                    const target = (data?.stages || []).find((st) => st.id === v);
                    if (target?.isLost) { edit('stageId', v); return; }
                    moveStageNow(v);
                  }}
                >
                  <SelectTrigger className="fn-inline-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {/* Только этапы своей воронки. Прежде в списке лежали
                        все 34 этапа обеих воронок подряд, и после того
                        как выбор стал применяться сразу, промах мышью в
                        окошке на пятнадцать строк молча уводил живую
                        сделку в воронку бота — туда, куда менеджер не
                        смотрит. Перенос между воронками бывает нужен, но
                        это отдельное решение, а не соседний пункт
                        списка. */}
                    {(data?.stages || [])
                      .filter((s) => (s.pipelineId || 'sales') === (L.pipelineId || 'sales'))
                      .map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="fn-inline">
                <span className="fn-field-label">Квалификация</span>
                <Select
                  value={val('qualification', L.qualification || 'none')}
                  onValueChange={(v) => edit('qualification', v === 'none' ? '' : v)}
                >
                  <SelectTrigger className={`fn-inline-trigger ${val('qualification', L.qualification) ? '' : 'fn-inline-empty'}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">не оценён</SelectItem>
                    {(data?.qualifications || []).map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Причина нужна только у закрытых: у сделки в работе её
                  спрашивать нечего, а поле сбивало бы с толку.
                  Смотрим на ВЫБРАННЫЙ этап, а не на текущий: причину
                  спрашивают, когда сделку закрывают, а не после того как
                  закрыли. По `L.stageId` селект появлялся уже задним
                  числом, то есть ровно тогда, когда он не нужен. */}
              {(data?.stages || []).find(
                (st) => st.id === val('stageId', L.stageId))?.isLost && (
                <div className="fn-inline">
                  <span className="fn-field-label">Причина отказа</span>
                  {/* Если сделку уже закрывали и вернули в работу, причина
                      с лида стирается — у сделки в работе её быть не
                      должно. Но решение-то принято, и при повторном
                      закрытии спрашивать заново значит заставлять
                      вспоминать. Подставляем последнюю из истории: она
                      там есть, менеджер видит её в ленте. */}
                  <Select
                    value={val('lossReason', L.lossReason || lastLossReason || 'none')}
                    onValueChange={(v) => edit('lossReason', v === 'none' ? '' : v)}
                  >
                    <SelectTrigger className={`fn-inline-trigger ${L.lossReason ? '' : 'fn-warn'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">не указана</SelectItem>
                      {(data?.lossReasons || []).map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="fn-inline">
                <span className="fn-field-label">Бюджет, zł</span>
                <Input
                  type="number"
                  min="0"
                  step="100"
                  value={val('budget', L.budget ?? '') ?? ''}
                  placeholder="—"
                  className="fn-inline-input fn-num"
                  onChange={(e) => edit('budget', e.target.value === '' ? null : Number(e.target.value))}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                />
              </div>

              <div className="fn-inline">
                <span className="fn-field-label">Менеджер</span>
                <Select
                  value={val('assignedTo', L.assignedTo || 'none')}
                  onValueChange={(v) => edit('assignedTo', v === 'none' ? '' : v)}
                >
                  <SelectTrigger className={`fn-inline-trigger ${val('assignedTo', L.assignedTo) ? '' : 'fn-inline-empty'}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">не назначен</SelectItem>
                    {managers.map((m) => (
                      <SelectItem key={m.username} value={m.username}>{m.fullName || m.username}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

            </div>


            {GROUPS.map((g) => {
              // Деньги собираются из бумаг калькулятора, а не из настраиваемых
              // полей, поэтому у них свой подсчёт строк и свои ряды. Оболочка
              // та же: заголовок сворачивает, счётчик справа — иначе одна
              // группа из четырёх выглядела бы приклеенной сбоку.
              if (g.id === 'money') {
                const rows = moneyRows(data?.feed);
                // Нет ни одной бумаги — группы нет вовсе. Пустое состояние
                // здесь обещало бы, что сумму можно заполнить, а её заполняет
                // калькулятор, когда менеджер выпишет КП.
                if (!rows.length) return null;
                return (
                  <section key={g.id} className="mb-4">
                    <button
                      type="button"
                      className="fn-group-head"
                      data-open={closedGroups[g.id] ? undefined : ''}
                      onClick={() => setClosedGroups((c) => ({ ...c, [g.id]: !c[g.id] }))}
                    >
                      <ChevronRight className="h-3 w-3" />
                      {g.title}
                      <span className="fn-group-count">{rows.length}</span>
                    </button>
                    {closedGroups[g.id] ? null : (
                      <div className="-mx-2">
                        {rows.map((r, i) => (
                          <div key={`${r.label}-${i}`} className="fn-field">
                            {/* Номера заказа в подписи нет намеренно: он
                                длиннее отведённых подписи 6.5rem, ломал её на
                                две строки и повторялся в каждом ряду, разводя
                                высоты в одной колонке. Он в подсказке ссылки и
                                в ленте, где эта бумага записана. */}
                            <div className="fn-field-label">{r.label}</div>
                            {/* Сумма последней, пояснения перед ней: значения
                                в карточке прижаты вправо, и всё, что стоит
                                после числа, сдвигает его на свою длину. Суммы
                                тогда не выстраиваются в колонку, а их тут и
                                сравнивают глазом — КП с договором. */}
                            <div className="fn-field-value">
                              {r.url && (
                                <a href={r.url} target="_blank" rel="noreferrer"
                                   title={r.number ? `Заказ ${r.number}` : 'Открыть бумагу'}
                                   className="fn-good hover:underline">бумага</a>
                              )}
                              {r.note && <span className="fn-ink-3">{r.url ? ' · ' : ''}{r.note}</span>}
                              <b className="fn-num font-medium">
                                {(r.url || r.note) ? ' · ' : ''}
                                {r.diff != null && (r.diff < 0 ? '−' : '+')}
                                {zlote(r.amount)}&nbsp;{r.currency === 'PLN' ? 'zł' : r.currency}
                              </b>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                );
              }
              // Поля, спрятанные на текущем этапе, не показываем вовсе:
              // адрес монтажа на первом этапе никто не знает, и пустая
              // строка там — не пробел в данных, а вопрос не ко времени.
              // Это не то же, что «скрыть пустые»: там прячется ровно то,
              // что надо заполнить, здесь — то, что заполнять ещё рано.
              const all = fields.filter(
                (f) => f.group === g.id
                  && !((f.hiddenOnStages || []).includes(L.stageId)));
              // Пустые поля по умолчанию скрыты. В amo половина карточки —
              // это ряды с многоточием, и глазу не за что зацепиться.
              // Обязательное на этом этапе показываем всегда, даже когда
              // пустые скрыты: иначе поле, которое не пускает сделку
              // дальше, невидимо ровно до отказа при переносе.
              const rows = showEmpty ? all : all.filter(
                (f) => fieldValue(f) || borrowedValue(f)
                       || (f.requiredOnStages || []).includes(L.stageId));
              const hidden = all.length - rows.length;
              if (!all.length) return null;
              return (
                <section key={g.id} className="mb-4">
                  {/* Заголовок сворачивает группу. Рекламные поля смотрят раз
                      в месяц, а места они занимают столько же, сколько имя и
                      телефон. В amoCRM группы тоже сворачиваются. */}
                  <button
                    type="button"
                    className="fn-group-head"
                    data-open={closedGroups[g.id] ? undefined : ''}
                    onClick={() => setClosedGroups((c) => ({ ...c, [g.id]: !c[g.id] }))}
                  >
                    <ChevronRight className="h-3 w-3" />
                    {g.title}
                    <span className="fn-group-count">
                      {rows.length}
                      {hidden > 0 && ` · ${hidden} пустых`}
                    </span>
                  </button>
                  {/* Пустое состояние объясняет, что сюда попадёт и откуда,
                      а не сообщает, что пусто. Требование скилла: пустое
                      состояние учит интерфейсу, а не пишет «здесь ничего». */}
                  {closedGroups[g.id] ? null : rows.length === 0 ? (
                    <p className="px-2 py-1 text-[length:var(--fn-t-sm)] leading-4 fn-ink-3">
                      {g.id === 'config' ? 'Заполнится, когда клиент ответит на вопросы квиза'
                        : g.id === 'ad' ? 'Заполнится из рекламного кабинета вместе с заявкой'
                        : 'Пока не заполнено'}
                    </p>
                  ) : (
                    <div className="-mx-2">
                      {rows.map((f) => {
                        const lent = borrowedValue(f);
                        return (
                          <FieldRow key={f.id} field={f}
                                    value={fieldValue(f) || (lent ? lent.value : '')}
                                    borrowedFrom={lent ? lent.from : null}
                                    required={(f.requiredOnStages || []).includes(L.stageId)}
                                    onSave={(v) => onFieldSave(f, v)} />
                        );
                      })}
                    </div>
                  )}
                  {/* Путь к пустым полям. Его не было вовсе: `showEmpty`
                      умел только выключаться, кнопка «скрыть пустые» стояла
                      мёртвой разметкой, а счётчик «11 пустых» обещал то,
                      чего интерфейс показать не мог. Менеджер, которому
                      нужно вписать адрес или почту, сделать этого не мог —
                      поле есть в настройках, но на экран не выводится. */}
                  {!closedGroups[g.id] && !showEmpty && hidden > 0 && (
                    <button type="button" onClick={() => setShowEmpty(true)}
                            className="px-2 py-1 text-[length:var(--fn-t-sm)] leading-4
                                       fn-ink-3 fn-hover-ink-2">
                      показать {hidden} {plural(hidden, 'пустое', 'пустых', 'пустых')}
                    </button>
                  )}
                </section>
              );
            })}

            {showEmpty && (
              <button type="button" onClick={() => setShowEmpty(false)}
                      className="mb-4 text-[length:var(--fn-t-xs)] leading-4 fn-ink-3 fn-hover-ink-3">
                скрыть пустые поля
              </button>
            )}
            </fieldset>

            {/* Полоса сохранения. Появляется, как только что-то изменено, —
                и здесь же, а не внизу длинной колонки: иначе до неё надо
                прокручивать, а человек уже думает, что сохранил. */}
            {dirty && (
              <div className="fn-save-bar">
                <span className="text-[length:var(--fn-t-md)] leading-5">
                  {Object.keys(draft).length}&nbsp;{plural(Object.keys(draft).length,
                    'изменение', 'изменения', 'изменений')} не сохранено
                </span>
                <button type="button" className="fn-mode ml-auto"
                        onClick={() => setDraft({})} disabled={saving}>
                  Отменить
                </button>
                <button type="button" className="fn-btn fn-btn-primary"
                        onClick={saveDraft} disabled={saving}>
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Сохранить
                </button>
              </div>
            )}
          </div>

          {/* ПРАВО: лента */}
          <div className="fn-right flex min-h-0 flex-col">
            <div className="fn-tabbar flex items-center gap-1 px-6 py-2"
                 style={{ borderColor: 'var(--fn-line)' }}>
              <div className="fn-seg">
              {[['feed', 'Лента'], ['chat', 'Переписка'],
                ...(mailboxes.length > 0 ? [['mail', 'Почта']] : [])].map(([id, label]) => (
                <button key={id} type="button" onClick={() => setTab(id)}
                        className={`fn-seg-item${tab === id ? ' is-on' : ''}`}>
                  {label}
                  {id === 'chat' && chatMessages.length > 0 && (
                    <span className="ml-1.5 tabular-nums fn-ink-2">{chatMessages.length}</span>
                  )}
                  {id === 'mail' && mail?.messages?.length > 0 && (
                    <span className="ml-1.5 tabular-nums fn-ink-2">{mail.messages.length}</span>
                  )}
                </button>
              ))}
            </div>
            </div>

            {tab === 'mail' && (
              <div className="fn-feed fn-lead-scroll flex-1 px-6 py-6">
                {!mail && <p className="text-center text-sm fn-ink-3">Ищу письма…</p>}
                {mail?.problems?.map((p) => (
                  <p key={p} className="mb-2 rounded-[var(--fn-r-sm)] border border-amber-200 bg-amber-50 px-3 py-2 text-xs fn-warn">{p}</p>
                ))}
                {mail && !mail.clientEmail && (
                  <p className="text-center text-sm fn-ink-3">
                    У лида не указан адрес — искать переписку не по чему
                  </p>
                )}
                {mail?.clientEmail && mail.messages.length === 0 && !mail.problems.length && (
                  <p className="text-center text-sm fn-ink-3">
                    Писем с {mail.clientEmail} не найдено
                  </p>
                )}
                {mail?.messages?.map((m) => (
                  <div key={m.id} className="fn-event"
                       style={{ '--fn-dot': m.outgoing ? 'hsl(205 70% 52%)' : 'hsl(35 85% 50%)' }}>
                    <div className="fn-event-body">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="fn-section-label">{m.outgoing ? 'Мы' : 'Клиент'}</span>
                        <time className="text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">
                          {m.at ? new Date(m.at).toLocaleString('ru-RU', {
                            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                        </time>
                      </div>
                      {m.subject && <p className="mt-1 text-[length:var(--fn-t-md)] leading-5 font-medium">{m.subject}</p>}
                      <p className="mt-1 whitespace-pre-wrap text-[length:var(--fn-t-md)] leading-5">
                        {(m.text || m.snippet).slice(0, 2000)}
                      </p>
                      <p className="mt-1 text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">{m.mailbox}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'chat' && (
              <div className="fn-chat fn-lead-scroll flex-1 px-6 py-6">
                {chatMessages.length === 0 && (
                  <p className="text-center text-sm fn-ink-3">
                    Переписки нет. Написать можно внизу, выбрав WhatsApp.
                  </p>
                )}
                {chatMessages.map((m, i) => {
                  const prev = chatMessages[i - 1];
                  const newDay = !prev || dayLabel(prev.at) !== dayLabel(m.at);
                  return (
                    <React.Fragment key={m.id}>
                      {newDay && <div className="fn-day">{dayLabel(m.at)}</div>}
                      <Bubble m={m} />
                    </React.Fragment>
                  );
                })}
              </div>
            )}

            {tab === 'feed' && (data?.feed || []).length > 12 && (
              <>
                <div className="fn-feed-search">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 fn-ink-3" />
                  <input value={feedQuery} onChange={(e) => setFeedQuery(e.target.value)}
                         placeholder="найти в истории" className="fn-inline-input" />
                  {feedQuery && (
                    <button type="button" onClick={() => setFeedQuery('')}
                            aria-label="Очистить поиск"><X className="h-3 w-3" /></button>
                  )}
                </div>
                {/* Разбор ленты по виду записи. На истории из полутора
                    сотен строк — а именно такой она станет, когда
                    приедут примечания из amo, — «что я записал» и «что
                    наменяла автоматика» иначе не разделить: вручную
                    написанное тонет среди срабатываний правил.
                    Показываем рядом с поиском и по тому же порогу: на
                    короткой ленте пять кнопок занимают больше места,
                    чем сама лента. */}
                <div className="fn-feed-kinds">
                  {FEED_KINDS.map(([id, title]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setFeedKind(id)}
                      aria-pressed={feedKind === id}
                      className={`fn-feed-kind${feedKind === id ? ' is-on' : ''}`}
                    >
                      {title}
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="fn-feed fn-lead-scroll flex-1 px-6 py-6" hidden={tab !== 'feed'}>
              {/* Поиск показываем только на длинной истории: на короткой
                  он занимает место и ничего не экономит. */}
              {feedQuery && feedRows.length === 0 && (
                <p className="text-[length:var(--fn-t-md)] leading-5 fn-ink-3">Ничего не нашлось.</p>
              )}
              {/* Скелетон, а не надпись «Загружаю…».
                  Требование скилла для рабочих интерфейсов: показывать
                  форму будущего содержимого, а не сообщать о процессе.
                  Надпись посреди пустого места заставляет ждать вслепую;
                  скелет говорит, сколько всего придёт и как оно ляжет. */}
              {!data && <FeedSkeleton />}
              {feedRows.map((row, i) => {
                const e = row.items[0];
                const prev = feedRows[i - 1]?.items[0];
                const newDay = !prev || dayLabel(prev.at) !== dayLabel(e.at);

                // Беседа: вставка с пузырями внутри ленты. Внутри неё
                // порядок хронологический — разговор читается сверху вниз,
                // ответ под вопросом. Сама лента идёт новыми вверх, и это
                // не противоречие: лента — список событий, беседа — кусок
                // разговора, и у них разные правила чтения.
                if (row.thread) {
                  const talk = [...row.items].reverse();
                  // В ленте показываем хвост разговора, а не весь. Беседа из
                  // двадцати пяти реплик занимала ленту целиком, и всё
                  // остальное — задачи, смены этапа, звонки — уходило под
                  // экран. Полный разговор живёт во вкладке, она для этого и
                  // сделана.
                  const shown = talk.slice(-THREAD_IN_FEED);
                  const hidden = talk.length - shown.length;
                  return (
                    <div key={row.id}>
                      {newDay && (
                        <div className={`fn-day${dayLabel(e.at) === 'Сегодня' ? ' is-today' : ''}`}>
                          {dayLabel(e.at)}
                        </div>
                      )}
                      <div className="fn-thread">
                        <div className="fn-thread-head">
                          <MessageSquare size={13} strokeWidth={2} />
                          <span>WhatsApp</span>
                          <span className="fn-thread-count">
                            {talk.length} {plural(talk.length, 'сообщение', 'сообщения', 'сообщений')}
                          </span>
                        </div>
                        {hidden > 0 && (
                          <button type="button" className="fn-thread-more" onClick={() => setTab('chat')}>
                            {'↑ ещё '}{hidden}{' '}
                            {plural(hidden, 'сообщение', 'сообщения', 'сообщений')}
                            {' — открыть переписку'}
                          </button>
                        )}
                        <div className="fn-chat">
                          {shown.map((m) => <Bubble key={m.id} m={m} />)}
                        </div>
                      </div>
                    </div>
                  );
                }

                // Свёрнутая пачка мелких записей. Разворачивается щелчком —
                // как в amoCRM, где это «10 событий — развернуть».
                if (row.group && row.items.length > 1 && !openGroups[row.id]) {
                  return (
                    <div key={row.id}>
                      {newDay && (
                        <div className={`fn-day${dayLabel(e.at) === 'Сегодня' ? ' is-today' : ''}`}>
                          {dayLabel(e.at)}
                        </div>
                      )}
                      <div className="fn-event fn-event-fold" style={{ '--fn-dot': 'var(--fn-line-strong)' }}>
                        <span className="fn-event-icon"><ChevronDown size={13} strokeWidth={2} /></span>
                        <button
                          type="button"
                          onClick={() => setOpenGroups((g) => ({ ...g, [row.id]: true }))}
                          className="fn-event-quiet"
                        >
                          {/* Называем вид записи, а не «изменения»: в ленте
                              рядом стоят правки полей, срабатывания правил
                              и системные пометки, и «10 событий» не говорит,
                              что внутри. */}
                          {(FEED_STYLE[row.items[0].type] || FEED_STYLE.system).label}
                          {': '}{row.items.length}{' · показать'}
                        </button>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={row.id}>
                    {newDay && (
                      <div className={`fn-day${dayLabel(e.at) === 'Сегодня' ? ' is-today' : ''}`}>
                        {dayLabel(e.at)}
                      </div>
                    )}
                    {row.items.map((item) => {
                      const ist = FEED_STYLE[item.type] || FEED_STYLE.system;
                      const itime = item.at
                        ? new Date(item.at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
                        : '';
                      return (
                        <div key={item.id}
                             /* Сообщений здесь не бывает: они собираются в
                                беседу выше. Остаются два вида — действие
                                человека и журнальная строка. */
                             className={`fn-event ${
                               HUMAN_EVENTS.has(item.type) ? 'fn-event-said' : 'fn-event-log'}${
                               item.pinned ? ' is-pinned' : ''}`}
                             style={{ '--fn-dot': ist.dot }}>
                          <span className="fn-event-icon">
                            <ist.Icon size={13} strokeWidth={2} />
                          </span>
                          <div className="fn-event-body">
                            {/* Шапка записи одной строкой, как в amoCRM:
                                «17:22 Письмо ... Виолетта». Автор у правого
                                края — по нему взгляд идёт вниз колонкой. */}
                            {/* У журнальной строки подписи типа нет: её текст
                                сам себя называет («Правило …», «Этап …»), и
                                подпись повторяла первое слово. В карточке
                                действия подпись нужна — там текст говорит о
                                содержании, а не о роде записи. */}
                            <div className="fn-event-head">
                              <time>{itime}</time>
                              {HUMAN_EVENTS.has(item.type) && <span>{ist.label}</span>}
                              {item.by && (
                                <span className="fn-event-by" title={item.by}>{managerLabel(item.by)}</span>
                              )}
                              {/* Закрепить: важное — «просил перезвонить
                                  после 20-го», «скидку согласовали» —
                                  тонет под системными строками за неделю.
                                  Приём из amoCRM. */}
                              <button type="button" className="fn-event-pin"
                                      title={item.pinned ? 'Открепить' : 'Закрепить наверху'}
                                      aria-label={item.pinned ? 'Открепить' : 'Закрепить наверху'}
                                      onClick={async () => {
                                        await fetch(`${API_URL}/api/funnel/leads/${L.id}/pin`, {
                                          method: 'PUT',
                                          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ entryId: item.id, on: !item.pinned }),
                                        });
                                        loadCard();
                                      }}>
                                <Pin className="h-3 w-3" />
                              </button>
                            </div>
                            <p className="whitespace-pre-wrap">{item.text}</p>
                            {item.note && <p className="mt-1 text-xs fn-ink-2">{item.note}</p>}
                            {/* Запись разговора, как в amoCRM. Ссылку просим
                                в момент клика и не храним: у Binotel она
                                временная, и сохранённая протухнет молча. */}
                            {item.type === 'call' && item.meta?.callId && (
                              <CallRecord callId={item.meta.callId} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            {/* Поле ответа внизу, как в amoCRM: лента растёт вверх, а
                место для ответа не двигается. Сверху оно сбивало — верх
                карточки должен быть одинаковым всегда, а разговор идёт
                снизу. */}
            <div className="fn-composer">
              <div className="mb-2 flex items-center gap-1">
                <div className="fn-seg">
                  {/* Первые три отправляют, последние две — записывают
                      то, что уже произошло вне системы. Разделены
                      чертой: нажать «Звонок», думая, что звонишь, —
                      ошибка дешёвая, а нажать «WhatsApp», думая, что
                      делаешь пометку, — отправленное клиенту сообщение,
                      которое не вернуть. */}
                  {[['comment', 'Комментарий'], ['message', 'WhatsApp'],
                    ...(mailboxes.length ? [['mail', 'Письмо']] : []),
                    ['sep', null],
                    ['logCall', 'Был звонок'],
                    ['logMail', 'Было письмо']].map(([id, label]) => (
                    label === null ? <i key={id} className="fn-seg-sep" aria-hidden="true" /> : (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setMode(id)}
                      className={`fn-seg-item${mode === id ? ' is-on' : ''}`}
                      title={id === 'logCall' ? 'Записать звонок, сделанный с личного телефона'
                             : id === 'logMail' ? 'Записать письмо, отправленное из своего ящика'
                             : undefined}
                    >
                      {label}
                    </button>
                    )
                  ))}
                </div>
              </div>

              <Textarea
                rows={2}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={{
                  message: 'текст уйдёт клиенту в WhatsApp',
                  mail: 'текст письма',
                  logCall: 'о чём договорились по телефону',
                  logMail: 'о чём написали клиенту',
                }[mode] || 'что обсудили с клиентом'}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }}
              />

              {mode === 'logCall' && (
                <div className="mt-2 flex flex-wrap items-center gap-1">
                  {/* Кто кому. «Не указано» — полноценный ответ, а не
                      пропуск: менеджер, отмечающий вчерашний разговор,
                      может не помнить, кто набрал первым, и выбор
                      наугад сделал бы счётчик входящих неправдой. */}
                  {[['', 'не указано'], ['out', 'я звонил'], ['in', 'клиент звонил']].map(([id, label]) => (
                    <button key={id || 'none'} type="button"
                            onClick={() => setLogDirection(id)}
                            className={`fn-chip${logDirection === id ? ' is-on' : ''}`}>
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {mode === 'mail' && (
                <div className="mt-2 space-y-1.5">
                  <div className="flex gap-2">
                    <select value={mailbox} onChange={(e) => setMailbox(e.target.value)}
                            className="fn-select shrink-0">
                      {mailboxes.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                           placeholder="тема письма" className="h-8 text-[length:var(--fn-t-md)] leading-5" />
                  </div>
                  {!L.email && (
                    <p className="text-[length:var(--fn-t-xs)] leading-4 fn-warn">У лида нет адреса — писать некуда</p>
                  )}
                </div>
              )}

              {mode === 'message' && (
                <div className="mt-2 space-y-1.5">
                  {/* Состояние суточного окна — ДО набора текста, а не после.
                      Сервер считает его и кладёт в карточку ровно затем,
                      чтобы менеджер видел, можно ли писать свободным
                      текстом; фронт это поле не читал ни разу, и отказ
                      приходил 409-м уже после нажатия «Отправить».

                      Кнопку при закрытом окне НЕ блокируем: выбора шаблона
                      в карточке пока нет, и блокировка превратила бы
                      честное предупреждение в тупик без выхода. */}
                  {data?.whatsappWindow && (
                    <p className={`text-[length:var(--fn-t-xs)] leading-4 ${
                      data.whatsappWindow.open ? 'fn-ink-3' : 'fn-warn'}`}>
                      {data.whatsappWindow.open
                        ? `Свободно писать ещё ${data.whatsappWindow.hoursLeft} ч`
                        : `Окно закрыто: ${data.whatsappWindow.reason}. `
                          + 'Свободный текст не пройдёт — нужен шаблон'}
                    </p>
                  )}
                  <select
                    value={channelId}
                    onChange={(e) => setChannelId(e.target.value)}
                    className="fn-select w-full"
                  >
                    {channels.length === 0 && <option value="">каналы недоступны</option>}
                    {channels.map((c) => (
                      <option key={c.id} value={c.id} disabled={!c.ready}>
                        {c.name || c.transport}{c.ready ? '' : ` — ${c.state}`}
                      </option>
                    ))}
                  </select>
                  {!L.phone && (
                    <p className="text-[length:var(--fn-t-xs)] leading-4 fn-warn">
                      У лида нет телефона — писать некуда
                    </p>
                  )}
                </div>
              )}

              <div className="mt-2 flex items-center justify-between gap-3">
                {/* Подсказка сжимается и прячется первой: кнопка отправки
                    важнее напоминания о горячих клавишах, а на узком поле
                    они налезали друг на друга. */}
                <span className="min-w-0 truncate text-[length:var(--fn-t-xs)] leading-4 fn-ink-3">
                  ⌘ + Enter — отправить
                </span>
                <Button
                  size="sm"
                  onClick={submit}
                  disabled={busy || !comment.trim()
                    || (mode === 'message' && (!channelId || !L.phone))
                    || (mode === 'mail' && (!mailbox || !L.email))}
                >
                  <Send className="mr-1.5 h-3.5 w-3.5" />
                  {{ message: 'Отправить клиенту', mail: 'Отправить письмо' }[mode] || 'Добавить'}
                </Button>
              </div>
            </div>

          </div>
        </div>
    </>
  );

  if (asPage) {
    return (
      // Высота в один экран и раздельная прокрутка — только там, где
      // колонки стоят рядом. На узком экране они складываются одна под
      // другую, и та же высота делила её на две щели: лента получала
      // полтора сантиметра, а задача обрывалась на середине слова.
      <div className="funnel-ui fn-canvas fn-lead-page w-full px-6 pb-4 pt-3">
        <div className="mx-auto flex h-full w-full max-w-[84rem] flex-col">
        {/* Строка над карточкой: слева возврат, справа главное действие.
            Это уровень страницы, а не содержимого — здесь действие не
            спорит за место ни с именем клиента, ни с полями. */}
        <div className="mb-3 flex items-center gap-3">
          {/* Надпись — по тому, куда кнопка ведёт. Возврат делает
              history.back() и честно отдаёт назад: в «Клиенты», если
              карточку открыли оттуда. А называлась кнопка всегда «К
              доске» — человек нажимал её и попадал не туда, куда
              прочитал. */}
          <button type="button" onClick={leave}
                  className="fn-mode inline-flex items-center gap-2">
            <ArrowLeft className="h-3.5 w-3.5" />
            {backTitle}
          </button>
          {/* Создать КП. Открывает калькулятор с нашим id сделки — он по нему
              сам возьмёт имя, телефон и остальное, поэтому ни телефона, ни
              имени в ссылке нет: личные данные в адресной строке оседают в
              истории браузера и в логах.

              В amoCRM то же самое делал сценарий «Ссылка на калькулятор»: он
              клал в сделку примечание со ссылкой, и менеджер кликал по нему.
              Кнопка честнее примечания — её видно всегда, а не среди сорока
              записей ленты. */}
          <a href={`${CALC_BASE}/?calc=sauna&crmLeadId=${encodeURIComponent(L.id)}`}
             target="_blank" rel="noopener noreferrer"
             className={`fn-btn ${L.crmLeadId ? 'fn-btn-primary ml-auto' : ''}`}
             data-testid="funnel-calc-btn">
            <Calculator className="h-3.5 w-3.5" />
            Создать КП
          </a>
          {!L.crmLeadId && (
            <button type="button" onClick={handOff} disabled={busy}
                    className="fn-btn fn-btn-primary ml-auto" data-testid="funnel-handoff-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <ArrowRight className="h-3.5 w-3.5" />}
              Передать в CRM саун
            </button>
          )}
        </div>
        <div className="fn-panel flex min-h-0 flex-1 flex-col overflow-hidden">
          {body}
        </div>
        </div>
      </div>
    );
  }

  return (
    <Dialog open={!!lead} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="funnel-ui max-w-[min(96vw,84rem)] max-h-[94vh] overflow-hidden p-0">
        {body}
      </DialogContent>
    </Dialog>
  );
}

function NewLeadDialog({ open, onClose, managers, onCreated }) {
  const empty = { name: '', phone: '', email: '', comment: '', productType: '', source: 'phone', assignedTo: '' };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm(empty); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async () => {
    if (!form.name.trim() && !form.phone.trim()) {
      toast.error('Нужно имя или телефон');
      return;
    }
    setBusy(true);
    try {
      const body = { ...form, productType: form.productType || null };
      const res = await fetch(`${API_URL}/api/funnel/leads`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Ошибка ${res.status}`);
      toast.success(data.duplicateOf ? `Создан, но это дубль ${data.duplicateOf}` : 'Лид создан');
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Новый лид</DialogTitle>
          <DialogDescription>Звонок, выставка, сарафан — всё, что пришло не через форму.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Имя</Label>
            <Input value={form.name} onChange={set('name')} placeholder="как представился" autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Телефон</Label>
            <Input value={form.phone} onChange={set('phone')} placeholder="+48 ..." />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Что хочет</Label>
          <Textarea value={form.comment} onChange={set('comment')} rows={2}
            placeholder="продукт определится сам по тексту, если упомянуть сауну, купель или теплицу" />
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Продукт</Label>
            <Select value={form.productType || 'auto'} onValueChange={(v) => setForm((f) => ({ ...f, productType: v === 'auto' ? '' : v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">по тексту</SelectItem>
                {Object.entries(PRODUCTS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Источник</Label>
            <Select value={form.source} onValueChange={(v) => setForm((f) => ({ ...f, source: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(SOURCES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Менеджер</Label>
            <Select value={form.assignedTo || 'me'} onValueChange={(v) => setForm((f) => ({ ...f, assignedTo: v === 'me' ? '' : v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="me">я</SelectItem>
                {managers.map((m) => <SelectItem key={m.username} value={m.username}>{m.fullName || m.username}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button onClick={submit} disabled={busy} className="w-full" data-testid="funnel-create-submit">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
          Создать
        </Button>
      </DialogContent>
    </Dialog>
  );
}

/** Группировка задач по сроку — так их и разбирают: сначала просроченные. */
function groupTasks(tasks, keepEmpty = false) {
  const now = new Date();
  const endOfToday = new Date(now); endOfToday.setHours(23, 59, 59, 999);
  const endOfTomorrow = new Date(endOfToday); endOfTomorrow.setDate(endOfTomorrow.getDate() + 1);

  /* Цвет срока — как в доске задач amoCRM: просроченное красным, сегодня
     зелёным, дальнее нейтральным. Красный там стоит над колонкой чертой,
     а не подкрашивает каждую строку: строку читают, черту видят. */
  const groups = [
    { id: 'overdue', title: 'Просрочено', tasks: [], accent: true, tone: 'hsl(350 72% 52%)' },
    { id: 'today', title: 'Сегодня', tasks: [], tone: 'hsl(152 55% 42%)' },
    { id: 'tomorrow', title: 'Завтра', tasks: [], tone: 'hsl(215 70% 55%)' },
    { id: 'later', title: 'Позже', tasks: [], tone: 'var(--fn-line-strong)' },
    { id: 'noDate', title: 'Без срока', tasks: [], tone: 'hsl(38 85% 55%)' },
  ];
  const by = Object.fromEntries(groups.map((g) => [g.id, g]));

  tasks.forEach((t) => {
    if (!t.dueAt) return by.noDate.tasks.push(t);
    const d = new Date(t.dueAt);
    if (d <= now) return by.overdue.tasks.push(t);
    if (d <= endOfToday) return by.today.tasks.push(t);
    if (d <= endOfTomorrow) return by.tomorrow.tasks.push(t);
    return by.later.tasks.push(t);
  });
  // В списке пустая группа — пустой заголовок посреди экрана, и её
  // убираем. В колонках наоборот: пустая колонка «Сегодня» отвечает
  // «на сегодня ничего», а её отсутствие молчит — глазу не на чем
  // убедиться, что день разобран, а не потерян.
  return keepEmpty ? groups : groups.filter((g) => g.tasks.length);
}

function TaskRow({ task, onClose, onOpenLead, onPostpone, stages }) {
  const late = task.dueAt && new Date(task.dueAt) <= new Date();
  const stage = (stages || []).find((st) => st.id === task.lead?.stageId);
  return (
    <div className="fn-task-row flex items-start gap-3 px-3 py-2.5">
      {/* Значок отвечает «насколько срочно» до чтения строки: часы —
          задача впереди, восклицание — просрочена. В карточке это уже
          починено, а здесь оставался пустой квадрат, одинаковый в обоих
          случаях, — то есть именно там, где задачи просматривают пачками.
          Признак дефекта один, и искать его надо по всему коду, а не
          чинить в том месте, где заметили. */}
      <button type="button" onClick={() => onClose(task)} title="Закрыть задачу"
              className="mt-0.5 shrink-0 fn-ink-2 hover:opacity-90"
              style={{ color: late ? 'var(--fn-danger)' : undefined }}>
        {late ? <AlertTriangle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
      </button>

      <button type="button" className="min-w-0 flex-1 text-left"
              onClick={() => task.lead && onOpenLead(task.lead)}>
        <span className="block text-[length:var(--fn-t-md)] leading-5">{task.title}</span>
        <span className="mt-0.5 block text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">
          {task.type}
          {task.assignedTo && ` · ${managerLabel(task.assignedTo)}`}
          {task.createdBy && ` · от ${task.createdBy}`}
          {task.auto && !task.createdBy && ' · поставлена системой'}
        </span>
      </button>

      <span className="hidden min-w-0 flex-1 text-[length:var(--fn-t-sm)] leading-4 sm:block">
        {task.lead ? (
          <>
            <span className="block truncate">{task.lead.name || 'Без имени'}</span>
            <span className="fn-phone block fn-ink-2">{task.lead.phone}</span>
            {/* Этап сделки: он приезжал в строке с самого начала и не
                рисовался. Без него «перезвонить» у сделки на отправленном
                КП и у сделки на первом контакте — одна и та же строка, а
                разговор по ним разный. */}
            {stage && <span className="block fn-ink-3">{stage.name}</span>}
          </>
        ) : <span className="fn-ink-2">лид удалён</span>}
      </span>

      <time className={`shrink-0 text-[length:var(--fn-t-sm)] leading-4 ${late ? 'fn-danger' : 'fn-ink-2'}`}>
        {task.dueAt
          ? new Date(task.dueAt).toLocaleString('ru-RU',
              { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
          : '—'}
      </time>

      {/* Перенос срока прямо из строки. В amoCRM «отложить на завтра» —
          самое частое действие после закрытия, а у нас его не было
          вовсе: ручка на сервере есть, звать её было нечем. Менеджер
          вместо переноса закрывал задачу «перезвоню позже» — и сделка
          оставалась без задачи, то есть на доске молчала. */}
      <div className="flex shrink-0 gap-1">
        {QUICK_DUE.map((q) => (
          <button key={q.label} type="button" className="fn-chip"
                  title={`Перенести: ${q.label}`}
                  onClick={() => onPostpone(task, q.value())}>
            {q.short}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Таблица сделок с массовыми действиями.
 *
 * В amoCRM рядом с названием воронки стоят два значка: доска и список.
 * Доска отвечает «где сделка застряла», список — «сделать одно и то же с
 * десятком сделок»: галочки в строках, а вместо шапки таблицы появляется
 * полоса действий и «Выбрано N на M zł».
 *
 * Отбор тот же, что у доски: те же фильтры уходят в /leads, что и в
 * /board, — иначе число над доской и число строк разошлись бы.
 */
/** Колонки списка. Менеджер выбирает, что видеть: в amoCRM так же, и это
 *  не украшение — у разбирающего «неразобранное» и у ведущего сделки
 *  разные нужды, а ширина экрана одна.
 *
 *  Выбор лежит в браузере, а не в настройках сервиса: это предпочтение
 *  одного человека за одним столом, и синхронизировать его между людьми
 *  было бы вредно.
 */
const COLUMNS = [
  { id: 'no', title: '№', sortable: true, right: true,
    cell: (r) => r.no ?? '—' },
  { id: 'name', title: 'Клиент', sortable: true,
    /* Имя, а если его нет — название сделки, как на доске. «Без имени»
       стояло у каждой второй строки при том, что человек подписан:
       имя приезжает не всегда, а название почти всегда содержит, с кем
       имеем дело («Zbigniew — sauna sucha, Wałbrzych»). Звонить
       человеку, не зная, как его зовут, — худшее, что можно сделать со
       списком, по которому обзванивают. */
    cell: (r, { onOpenLead }) => (
      <button type="button" className="fn-link" onClick={() => onOpenLead(r)}
              title={r.name ? undefined : 'Имя клиента не заполнено — показано название сделки'}>
        {listClientName(r)}
      </button>
    ) },
  { id: 'phone', title: 'Телефон', sortable: true, cell: (r) => r.phone || '—' },
  { id: 'stage', title: 'Этап',
    cell: (r, { st }) => (
      <span className="fn-stage-mark">
        <span className="fn-dot" style={{ background: st?.color || 'var(--fn-line-strong)' }} />
        {st?.name || r.stageId || '—'}
      </span>
    ) },
  { id: 'assignedTo', title: 'Менеджер', sortable: true,
    cell: (r) => managerLabel(r.assignedTo) || <span className="fn-ink-3">не назначен</span> },
  { id: 'budget', title: 'Бюджет', sortable: true, right: true,
    cell: (r) => (r.budget ? `${r.budget.toLocaleString('pl-PL')} zł` : '—') },
  { id: 'task', title: 'Задача',
    // В одной колонке стояли две величины без различения: дата ближайшего
    // срока, если он есть, и ЧИСЛО открытых задач, если срока нет. «2»
    // читалось как дата, пока не сравнишь с соседними строками. Теперь
    // число говорит словом — оно и значит другое: задача есть, а срока у
    // неё нет, и на доске такая сделка молчит.
    cell: (r) => (r.openTasks
      ? (r.nextTaskAt
          ? new Date(r.nextTaskAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
          : <span className="fn-warn">
              {r.openTasks} {plural(r.openTasks, 'задача', 'задачи', 'задач')} без срока
            </span>)
      : <span className="fn-warn">нет задач</span>) },
  { id: 'tags', title: 'Теги',
    cell: (r) => ((r.tags || []).length
      ? (r.tags || []).map((t) => <span key={t} className="fn-chip mr-1">{t}</span>)
      : '—') },
  { id: 'source', title: 'Источник',
    cell: (r) => r.sourceLabel || SOURCES[r.source]?.label || r.source || '—' },
  { id: 'receivedAt', title: 'Создана', sortable: true, right: true,
    cell: (r) => (r.receivedAt
      ? new Date(r.receivedAt).toLocaleDateString('ru-RU',
          { day: 'numeric', month: 'short', year: '2-digit' })
      : '—') },
];

const DEFAULT_COLUMNS = ['no', 'name', 'phone', 'stage', 'assignedTo', 'budget', 'task', 'receivedAt'];

function LeadList({ params, stages, managers, admin, onOpenLead, onChanged }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  // Набор колонок держим в браузере: выбор «что видеть» — предпочтение
  // одного человека, и навязывать его остальным было бы хуже, чем не
  // сохранять вовсе. Чтение обёрнуто: в приватном окне доступ к хранилищу
  // умеет бросать исключение, и тогда просто берём набор по умолчанию.
  const [shownCols, setShownCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('fn-columns') || 'null');
      if (Array.isArray(saved) && saved.length) return saved;
    } catch (e) { /* нет доступа к хранилищу — работаем с умолчанием */ }
    return DEFAULT_COLUMNS;
  });
  const colSet = useMemo(() => new Set(shownCols), [shownCols]);
  const [colsOpen, setColsOpen] = useState(false);

  const toggleCol = (id) => {
    setShownCols((cur) => {
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      // Пустая таблица бессмысленна: последнюю колонку снять нельзя.
      if (!next.length) return cur;
      try { localStorage.setItem('fn-columns', JSON.stringify(next)); } catch (e) { /* не сохранилось */ }
      return next;
    });
  };
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState({ field: 'receivedAt', order: -1 });
  const [shown, setShown] = useState(100);

  const load = useCallback(async () => {
    const qs = new URLSearchParams(params);
    qs.set('sort', sort.field);
    qs.set('order', String(sort.order));
    qs.set('limit', String(shown));
    const r = await fetch(`${API_URL}/api/funnel/leads?${qs}`, { headers: authHeaders() });
    if (r.ok) setData(await r.json());
    else toast.error('Не удалось загрузить список');
  }, [params, sort, shown]);

  useEffect(() => { load(); }, [load]);
  // Смена условий отбора обнуляет выбор: иначе действие ушло бы в сделки,
  // которых на экране уже нет.
  useEffect(() => { setPicked(new Set()); }, [params]);

  const rows = data?.rows || [];
  /* Этапы берём из ответа списка, а не из пропса: там же приходит пометка
     «вход на этот этап пишет клиенту», которую считает сервер. */
  const stageList = data?.stages?.length ? data.stages : (stages || []);
  const stageName = useMemo(
    () => Object.fromEntries(stageList.map((st) => [st.id, st])), [stageList]);

  const allPicked = rows.length > 0 && rows.every((r) => picked.has(r.id));
  const toggleAll = () => setPicked(allPicked ? new Set() : new Set(rows.map((r) => r.id)));
  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const pickedRows = rows.filter((r) => picked.has(r.id));
  const pickedMoney = pickedRows.reduce((sum, r) => sum + (r.budget || 0), 0);

  const runBulk = async (action, extra, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/funnel/leads/bulk`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ids: [...picked], action, ...extra }),
      });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.detail || `Ошибка ${r.status}`);
      // Говорим и про прошедшее, и про непрошедшее: «готово» при десяти
      // отказах из двадцати — это ложь, за которой никто не пойдёт
      // проверять, что именно не прошло.
      if (b.failed?.length) {
        toast.warning(`Готово: ${b.done}. Не прошло: ${b.failed.length} — ${b.failed[0].detail}`);
      } else {
        toast.success(`Готово: ${b.done}`);
      }
      setPicked(new Set());
      await load();
      onChanged();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  /* Перевод пачки на этап. Если вход на этап пишет клиенту, спрашиваем
     прямо — сколько человек получат сообщение. Отправленное не отзывается,
     и «Готово: 200» постфактум здесь не годится. */
  const moveMany = (st) => {
    const n = picked.size;
    const warn = st.notifiesClient
      ? `На этапе «${st.name}» стоит правило, которое пишет клиенту.\n\n`
        + `${n} ${plural(n, 'человек получит', 'человека получат', 'человек получат')} сообщение. `
        + 'Отменить отправку будет нельзя.\n\nПереводить?'
      : null;
    runBulk('stage', { stageId: st.id }, warn);
  };

  const sortBy = (field) => setSort((s) => (
    s.field === field ? { field, order: -s.order } : { field, order: -1 }));

  const Head = ({ field, children, right }) => (
    <th className={`fn-th${right ? ' text-right' : ''}`}>
      <button type="button" onClick={() => sortBy(field)} className="fn-th-btn">
        {children}
        {sort.field === field && (
          sort.order > 0 ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
        )}
      </button>
    </th>
  );

  return (
    <div className="fn-list">
      {picked.size > 0 ? (
        <div className="fn-bulk">
          <Popup
            align="left"
            width="14rem"
            trigger={({ open, toggle: t }) => (
              <button type="button" className="fn-chip" onClick={t} aria-expanded={open} disabled={busy}>
                <ArrowRight className="h-3.5 w-3.5" /> Этап
              </button>
            )}
          >
            {({ close }) => (
              <div role="menu">
                {stageList.map((st) => (
                  <button key={st.id} type="button" role="menuitem" className="fn-menu-item"
                          onClick={() => { close(); moveMany(st); }}>
                    <span className="fn-dot" style={{ background: st.color || 'var(--fn-line-strong)' }} />
                    {st.name}
                    {/* Этап, вход на который пишет клиенту, помечен здесь же:
                        спрашивать «вы уверены?» после нажатия поздно — читать
                        такое предупреждение будут вполглаза. */}
                    {st.notifiesClient && (
                      <MessageSquare className="ml-auto h-3.5 w-3.5 fn-warn" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </Popup>

          <Popup
            align="left"
            width="14rem"
            trigger={({ open, toggle: t }) => (
              <button type="button" className="fn-chip" onClick={t} aria-expanded={open} disabled={busy}>
                <User className="h-3.5 w-3.5" /> Менеджер
              </button>
            )}
          >
            {({ close }) => (
              <div role="menu">
                <button type="button" role="menuitem" className="fn-menu-item"
                        onClick={() => { close(); runBulk('assign', { assignedTo: '' }); }}>
                  снять менеджера
                </button>
                {/* Менеджер — объект пользователя, а не строка: в остальных
                    местах берут m.username, и здесь так же. */}
                {(managers || []).map((m) => (
                  <button key={m.username} type="button" role="menuitem" className="fn-menu-item"
                          onClick={() => { close(); runBulk('assign', { assignedTo: m.username }); }}>
                    {m.fullName || m.username}
                  </button>
                ))}
              </div>
            )}
          </Popup>

          {admin && (
            <button type="button" className="fn-chip fn-chip-danger" disabled={busy}
                    onClick={() => runBulk('delete', {},
                      `Убрать из работы ${picked.size} ${plural(picked.size, 'сделку', 'сделки', 'сделок')}?`)}>
              <Trash2 className="h-3.5 w-3.5" /> Удалить
            </button>
          )}

          <button type="button" className="fn-chip" onClick={() => setPicked(new Set())}>
            Снять выбор
          </button>

          <span className="ml-auto text-[length:var(--fn-t-sm)] leading-4 fn-ink-2">
            Выбрано <b className="fn-num">{picked.size}</b>
            {pickedMoney > 0 && <> на <b className="fn-num">{pickedMoney.toLocaleString('pl-PL')} zł</b></>}
          </span>
        </div>
      ) : (
        <div className="fn-bulk">
          <span className="text-[length:var(--fn-t-sm)] leading-4 fn-ink-2">
            {data ? <>
              <b className="fn-num">{data.total}</b>{' '}
              {plural(data.total, 'сделка', 'сделки', 'сделок')}
              {data.amount > 0 && <> на <b className="fn-num">{data.amount.toLocaleString('pl-PL')} zł</b></>}
            </> : 'Загружаю…'}
          </span>
        </div>
      )}

      {/* Выбор колонок прячем за кнопкой: им пользуются раз в месяц, а
          место в строке с фильтрами нужно каждый день. */}
      <div className="flex justify-end">
        <Popup
          align="right"
          width="13rem"
          trigger={({ open, toggle: t }) => (
            <button type="button" className="fn-mode" onClick={t} aria-expanded={open}>
              Колонки · {shownCols.length}
            </button>
          )}
        >
          <div className="fn-menu">
            {COLUMNS.map((c) => (
              <button key={c.id} type="button" className="fn-menu-item"
                      onClick={() => toggleCol(c.id)}>
                {colSet.has(c.id)
                  ? <CheckSquare className="h-3.5 w-3.5" />
                  : <Square className="h-3.5 w-3.5 fn-ink-3" />}
                {c.title}
              </button>
            ))}
          </div>
        </Popup>
      </div>

      <div className="fn-list-scroll">
        <table className="fn-table">
          <thead>
            <tr>
              <th className="fn-th fn-th-pick">
                <input type="checkbox" checked={allPicked} onChange={toggleAll}
                       aria-label="Выбрать все на странице" />
              </th>
              {COLUMNS.filter((c) => colSet.has(c.id)).map((c) => (
                c.sortable
                  ? <Head key={c.id} field={c.id} right={c.right}>{c.title}</Head>
                  : <th key={c.id} className="fn-th">{c.title}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              /* Сделка без этапа — новая: приёмник о воронке не знает и
                 этап не проставляет. Доска кладёт такие в первую колонку,
                 и таблица должна называть их так же, иначе одна и та же
                 сделка здесь «—», а там «Неразобранное». */
              const st = stageName[r.stageId] || (r.stageId ? null : stageList[0]);
              const late = (r.overdueTasks || 0) > 0;
              return (
                <tr key={r.id} className={`fn-tr${picked.has(r.id) ? ' is-picked' : ''}`}>
                  <td className="fn-td fn-th-pick">
                    <input type="checkbox" checked={picked.has(r.id)}
                           onChange={() => toggle(r.id)} aria-label={`Выбрать ${r.phone || r.id}`} />
                  </td>
                  {COLUMNS.filter((c) => colSet.has(c.id)).map((c) => (
                    <td key={c.id}
                        className={`fn-td${c.right ? ' fn-num text-right' : ''}${
                          c.id === 'phone' ? ' fn-phone' : ''}${
                          c.id === 'receivedAt' ? ' fn-ink-2' : ''}${
                          c.id === 'task' && late ? ' fn-danger' : ''}`}
                        title={c.id === 'no' ? r.id : undefined}>
                      {c.cell(r, { st, late, onOpenLead })}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        {data && rows.length === 0 && (
          <p className="py-10 text-center text-sm fn-ink-2">
            Ничего не найдено — попробуйте изменить условия.
          </p>
        )}
      </div>

      {data && rows.length < data.total && (
        <button type="button" className="fn-more" onClick={() => setShown((n) => n + 100)}>
          Показать ещё · {rows.length} из {data.total}
        </button>
      )}
    </div>
  );
}

/** Клиенты — взгляд на заявки со стороны человека.
 *
 * Менеджер, пришедший из amoCRM, ищет не сделку, а человека: звонит Пётр —
 * надо за секунду понять, кто это и чем кончился прошлый разговор. Доска
 * такого не показывает: она про обращения по одному.
 */
function ContactsView({ onOpenLead }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useKeptSearch('fn-contacts-q');
  // Ключ раскрытого человека, а не телефон: у клиента из Direct номера
  // нет вовсе, и по телефону его было бы не раскрыть.
  const [open, setOpen] = useState(null);
  const [card, setCard] = useState(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    params.set('limit', '200');
    const r = await fetch(`${API_URL}/api/funnel/contacts?${params}`, { headers: authHeaders() });
    if (r.ok) setData(await r.json());
    else toast.error('Не удалось загрузить клиентов');
  }, [q]);

  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [load, q]);

  const expand = async (key) => {
    if (open === key) { setOpen(null); setCard(null); return; }
    setOpen(key); setCard(null);
    const r = await fetch(`${API_URL}/api/funnel/contacts/${encodeURIComponent(key)}`,
                          { headers: authHeaders() });
    if (r.ok) setCard(await r.json());
    else toast.error('Не удалось открыть клиента');
  };

  const rows = data?.rows || [];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 fn-ink-3" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Имя, телефон, почта…"
                 className="h-8 w-72 pl-8 text-[length:var(--fn-t-md)] leading-5" />
        </div>
        {data && (
          <span className="text-[length:var(--fn-t-sm)] leading-4 fn-ink-3">
            {data.total} {plural(data.total, 'клиент', 'клиента', 'клиентов')}
            {/* Число повторных — то, ради чего раздел и заведён. Пока
                живого потока не было, оно ноль, и это честное состояние. */}
            {data.repeat > 0 && ` · ${data.repeat} ${plural(data.repeat, 'обращался', 'обращались', 'обращались')} дважды и больше`}
          </span>
        )}
      </div>

      {!data && <p className="text-[length:var(--fn-t-md)] leading-5 fn-ink-3">Загружаю…</p>}
      {data && rows.length === 0 && (
        <p className="text-[length:var(--fn-t-md)] leading-5 fn-ink-3">Никого не нашлось.</p>
      )}

      {rows.length > 0 && (
        <div className="fn-list-scroll">
          <table className="fn-table">
            <thead>
              <tr>
                <th className="fn-th">Клиент</th>
                <th className="fn-th">Связь</th>
                <th className="fn-th text-right">Обращений</th>
                <th className="fn-th">Последнее</th>
                <th className="fn-th">Этап</th>
                <th className="fn-th">Менеджер</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <React.Fragment key={c.key}>
                  <tr className={`fn-tr${open === c.key ? ' is-picked' : ''}`}>
                    <td className="fn-td">
                      <button type="button" className="fn-link"
                              onClick={() => expand(c.key)}>
                        {c.name || 'Без имени'}
                      </button>
                      {/* Человек, уже дошедший до производства, — не новый
                          лид: звонить ему надо иначе, и менеджер должен
                          увидеть это до того, как возьмёт трубку. */}
                      {c.handedOff && <span className="fn-tag ml-1.5" data-src="site">клиент</span>}
                      {/* Теги со всех заявок человека, а не только свежей:
                          «не звонить» сказано раз и навсегда, и менеджер
                          должен увидеть это здесь, до того как наберёт
                          номер, а не открыв нужную сделку. */}
                      {(c.tags || []).map((t) => (
                        <span key={t} className="fn-chip ml-1.5">{t}</span>
                      ))}
                    </td>
                    {/* Чем с человеком связаться. Телефон первым: по нему
                        звонят. Если номера нет — сеть, из которой он
                        написал: у клиента из Direct другого способа нет, и
                        прочерк в этом месте читался бы как «связи нет». */}
                    <td className="fn-td fn-phone">
                      {c.phone || (c.channels || [])[0] || '—'}
                      {c.phone && (c.channels || []).length > 0 && (
                        <span className="fn-ink-3"> · {c.channels.join(' · ')}</span>
                      )}
                    </td>
                    <td className="fn-td fn-num text-right">{c.leads}</td>
                    <td className="fn-td fn-ink-2">{c.lastAt ? dayLabel(c.lastAt) : '—'}</td>
                    <td className="fn-td">{c.lastStage || '—'}</td>
                    <td className="fn-td">{c.assignedTo || <span className="fn-ink-3">не назначен</span>}</td>
                  </tr>
                  {open === c.key && (
                    <tr>
                      <td className="fn-td" colSpan={6}>
                        {!card && <span className="fn-ink-3">Загружаю обращения…</span>}
                        {card && (
                          <div className="space-y-1">
                            {/* Чем кончился разговор с этим человеком —
                                со всех его заявок, а не только со свежей.
                                В amo такой ответ живёт на контакте и
                                переживает сделку; у нас он лежит на
                                заявке, и у новой заявки того же человека
                                поле пусто — «молчун» не отличался от
                                «отказавшегося». Менеджеру это нужно до
                                того, как он наберёт номер. */}
                            {(card.contact?.answers || []).map((line) => (
                              <div key={line} className="fn-ink-2">{line}</div>
                            ))}
                            {card.leads.map((l) => (
                              <div key={l.id} className="flex flex-wrap items-baseline gap-x-2">
                                <button type="button" className="fn-link"
                                        onClick={() => onOpenLead({ id: l.id })}>
                                  № {l.no ?? '—'}
                                </button>
                                <span className="fn-ink-2">{l.stage || '—'}</span>
                                <span className="fn-ink-3">{l.sourceLabel || l.source}</span>
                                <time className="fn-ink-3">{l.receivedAt ? dayLabel(l.receivedAt) : ''}</time>
                                {/* Повтор в окне 48 часов — это та же
                                    заявка, а не второе обращение. */}
                                {l.duplicateOf && <span className="fn-ink-3">· повтор</span>}
                                {l.crmLeadId && <span className="fn-ink-3">· передан в производство</span>}
                                {/* Удалённое обращение показываем, а не
                                    прячем: иначе оно исчезает без
                                    объяснения у человека, которого
                                    менеджер видел вчера. В счёт
                                    «Обращений» оно не идёт. */}
                                {l.deletedAt && <span className="fn-ink-3">· удалена</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// Окно «поставить задачу» для раздела «Задачи».
//
// До него задачу можно было завести только открыв карточку — а утреннее
// планирование «обзвонить этих четверых» так не делается: четыре клиента
// означали четыре открытых карточки и четыре возврата назад. В amoCRM
// задача ставится из списка, и менеджеры этим пользуются.
//
// Сделку ищем тем же запросом, что и поиск над доской: по номеру, имени,
// телефону и почте. Свой поиск здесь завёл бы второй ответ на тот же
// вопрос, и однажды они разошлись бы.
function NewTaskDialog({ open, onClose, onCreated, types, managers, me }) {
  const [query, setQuery] = useState('');
  const [found, setFound] = useState([]);
  const [lead, setLead] = useState(null);
  const [form, setForm] = useState({ title: '', type: 'Связаться', dueAt: '', assignedTo: '' });
  const [busy, setBusy] = useState(false);

  // Поиск с задержкой: иначе каждый набранный знак — запрос к базе.
  useEffect(() => {
    if (!open || lead || query.trim().length < 2) { setFound([]); return undefined; }
    const id = setTimeout(async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/funnel/leads?q=${encodeURIComponent(query.trim())}&limit=8`,
          { headers: authHeaders() });
        if (r.ok) setFound((await r.json()).rows || []);
      } catch { /* сеть моргнула — человек наберёт ещё раз */ }
    }, 250);
    return () => clearTimeout(id);
  }, [query, open, lead]);

  useEffect(() => {
    if (!open) { setQuery(''); setFound([]); setLead(null);
                 setForm({ title: '', type: 'Связаться', dueAt: '', assignedTo: '' }); }
  }, [open]);

  const submit = async () => {
    if (!lead || !form.title.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/funnel/leads/${lead.id}/tasks`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          title: form.title.trim(), type: form.type,
          dueAt: form.dueAt || null, assignedTo: form.assignedTo || me || '',
        }),
      });
      if (r.ok) { toast.success('Задача поставлена'); onCreated(); onClose(); }
      else toast.error('Не удалось поставить задачу');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="funnel-ui max-w-md">
        <DialogHeader>
          <DialogTitle>Поставить задачу</DialogTitle>
          <DialogDescription>
            {lead ? leadTitle(lead) : 'Сначала найдите сделку'}
          </DialogDescription>
        </DialogHeader>

        {!lead ? (
          <div className="space-y-2">
            <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                   placeholder="Номер, имя, телефон, почта…" />
            <div className="space-y-1">
              {found.map((l) => (
                <button key={l.id} type="button" className="fn-pick-row"
                        onClick={() => { setLead(l); setFound([]); }}>
                  <span className="fn-link">№ {l.no ?? '—'}</span>{' '}
                  <span>{leadTitle(l)}</span>{' '}
                  <span className="fn-ink-3 fn-phone">{l.phone}</span>
                </button>
              ))}
              {query.trim().length >= 2 && found.length === 0 && (
                <p className="fn-ink-3 text-[length:var(--fn-t-sm)] leading-4">
                  Ничего не нашлось
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <button type="button" className="fn-chip"
                    onClick={() => setLead(null)}>← выбрать другую сделку</button>
            <Input autoFocus value={form.title} placeholder="что сделать"
                   onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            <div className="grid gap-2 sm:grid-cols-2">
              <select className="fn-select" value={form.type}
                      onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                {(types || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select className="fn-select" value={form.assignedTo || me || ''}
                      onChange={(e) => setForm((f) => ({ ...f, assignedTo: e.target.value }))}>
                <option value="">себе</option>
                {(managers || []).map((m) => (
                  <option key={m.username} value={m.username}>
                    {m.fullName || m.username}
                  </option>
                ))}
              </select>
            </div>
            <Input type="datetime-local" value={form.dueAt}
                   onChange={(e) => setForm((f) => ({ ...f, dueAt: e.target.value }))} />
            <div className="flex flex-wrap gap-1">
              {QUICK_DUE.map((q) => (
                <button key={q.label} type="button" className="fn-chip"
                        onClick={() => setForm((f) => ({ ...f, dueAt: q.value() }))}>
                  {q.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Отмена</Button>
          <Button onClick={submit} disabled={busy || !lead || !form.title.trim()}>
            Поставить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function TasksView({ me, admin, onOpenLead, stages }) {
  const [data, setData] = useState(null);
  /* Раздел открывается со всем, что человеку видно, — своим и ничьим.
     Прежде у работника по умолчанию стоял «Только мои», а он отбирает
     строго по исполнителю: у менеджера своих задач две из пятисот
     четырнадцати, остальные ничьи. Утро начиналось с пустого экрана и
     слова «можно выдохнуть» — при пятистах просроченных рядом. Видеть
     чужие ему всё равно не дадут: это решает visible_clause на
     сервере, а не галочка на экране. */
  const [onlyMine, setOnlyMine] = useState(false);
  const [showDone, setShowDone] = useState(false);
  // Отбор, который сервер умеет с 13.10.2026 и которого не было на экране:
  // тип, автор и срок. Состав и слова взяты из раздела «Задачи» amoCRM —
  // менеджеры фильтруют там по себе и по датам каждый день, и переучивать
  // их на другие слова незачем.
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [onlyNobody, setOnlyNobody] = useState(false);
  const [assignee, setAssignee] = useState('');
  const [adding, setAdding] = useState(false);
  const [types, setTypes] = useState([]);
  const [managers, setManagers] = useState([]);
  // Вид раздела. Список оставлен видом по умолчанию: на нём помещаются
  // кнопки переноса срока, а перенос — самое частое действие после
  // закрытия задачи.
  const [tasksMode, setTasksMode] = useRememberedMode('fn-tasks-mode', 'list');
  // Этап сделки, по которому отбираем задачи. Сервер это умеет с самого
  // начала (параметр `stage`), на экране не было. А вопрос рабочий:
  // «перезвонить» у сделки на отправленном КП и у сделки на первом
  // контакте — разные разговоры, и разбирать их удобнее пачками.
  const [stageFilter, setStageFilter] = useState('');

  // Справочники для окна постановки — те же, что у блока задач в
  // карточке: список типов задаёт сервер, список людей — тоже.
  useEffect(() => {
    if (!adding) return;
    Promise.all([
      fetch(`${API_URL}/api/funnel/task-types`, { headers: authHeaders() })
        .then((r) => (r.ok ? r.json() : { types: [] })),
      fetch(`${API_URL}/api/funnel/managers`, { headers: authHeaders() })
        .then((r) => (r.ok ? r.json() : { managers: [] })),
    ]).then(([t, m]) => {
      setTypes(t.types || []);
      setManagers(m.managers || []);
    }).catch(() => {});
  }, [adding]);
  const [type, setType] = useState('');
  const [author, setAuthor] = useState('');
  // Период — не произвольные даты, а то, что спрашивают: «сегодня»,
  // «эта неделя». Произвольный диапазон нужен раз в месяц, а эти — каждый
  // день, и набирать для них даты руками значит не пользоваться вовсе.
  const [period, setPeriod] = useState('');

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    // «Только мои» и выбранный исполнитель — одно поле на сервере.
    // Явный выбор сильнее: человек, поставивший «Виолета», хочет видеть
    // её задачи, даже если галочка «только мои» осталась включённой.
    if (assignee) params.set('assigned', assignee);
    else if (onlyMine && me) params.set('assigned', me);
    if (showDone) params.set('done', 'true');
    if (overdueOnly) params.set('overdue', 'true');
    if (onlyNobody) params.set('unassigned', 'true');
    if (type) params.set('type', type);
    if (author) params.set('createdBy', author);
    if (stageFilter) params.set('stage', stageFilter);
    if (period) {
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      if (period === 'week') end.setDate(end.getDate() + (7 - end.getDay()) % 7);
      params.set('dueTo', end.toISOString());
    }
    // Колонки набираются отдельными запросами, по одному на срок.
    //
    // Общий список приходит порциями по 300, отсортованными по сроку, и
    // на боевой все 300 — просроченные: до сегодняшних и завтрашних
    // очередь просто не доходит. Пока колонки резались из этого списка,
    // «Сегодня» и «Завтра» показывали ноль при существующих задачах —
    // то есть врал ровно тот экран, с которого начинается день. Нашлось
    // 21.10.2026, когда менеджер поставил себе задачу на завтра и не
    // увидел её в колонке «Завтра».
    if (tasksMode === 'board') {
      const now = new Date();
      const endToday = new Date(now); endToday.setHours(23, 59, 59, 999);
      const endTomorrow = new Date(endToday); endTomorrow.setDate(endTomorrow.getDate() + 1);
      const slices = [
        ['overdue', { overdue: 'true' }],
        ['today', { dueFrom: now.toISOString(), dueTo: endToday.toISOString() }],
        ['tomorrow', { dueFrom: endToday.toISOString(), dueTo: endTomorrow.toISOString() }],
        ['later', { dueFrom: endTomorrow.toISOString() }],
        ['noDate', { noDue: 'true' }],
      ];
      try {
        const got = await Promise.all(slices.map(async ([id, extra]) => {
          const q = new URLSearchParams(params);
          // Период из общего фильтра для колонок не нужен: срок у каждой
          // колонки свой, и два ограничения на одно поле давали бы
          // пустые колонки без объяснения.
          q.delete('dueTo'); q.delete('dueFrom');
          Object.entries(extra).forEach(([k, v]) => q.set(k, v));
          q.set('limit', '100');
          const res = await fetch(`${API_URL}/api/funnel/tasks?${q}`, { headers: authHeaders() });
          if (!res.ok) throw new Error('не загрузились');
          const d = await res.json();
          return [id, d];
        }));
        const bySlice = Object.fromEntries(got);
        setData({
          tasks: got.flatMap(([, d]) => d.tasks || []),
          slices: bySlice,
          total: Object.values(bySlice).reduce((n, d) => n + (d.total || 0), 0),
          facets: bySlice.overdue?.facets || {},
          unassigned: bySlice.overdue?.unassigned || 0,
        });
      } catch {
        toast.error('Не удалось загрузить задачи');
      }
      return;
    }

    const r = await fetch(`${API_URL}/api/funnel/tasks?${params}`, { headers: authHeaders() });
    if (r.ok) setData(await r.json());
    else toast.error('Не удалось загрузить задачи');
  }, [onlyMine, showDone, overdueOnly, onlyNobody, type, author, period, me, assignee,
      tasksMode, stageFilter]);

  useEffect(() => { load(); }, [load]);

  // Окно вместо `window.prompt`: здесь задачи закрывают пачками, и
  // системный диалог браузера Chrome после нескольких подряд предлагает
  // запретить — после чего закрытие перестаёт работать молча.
  const closeTask = async (task, result) => {
    const r = await fetch(`${API_URL}/api/funnel/tasks/${task.id}/done`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ result }),
    });
    if (r.ok) { toast.success('Задача закрыта'); load(); }
    else toast.error('Не удалось закрыть');
  };

  const groups = useMemo(
    () => (data ? groupTasks(data.tasks, tasksMode === 'board') : []),
    [data, tasksMode],
  );
  const [closing, setClosing] = useState(null);

  // Перенос срока. Сервер при смене срока сам сбрасывает отметки о
  // напоминаниях и пишет перенос в ленту сделки — здесь только просьба.
  const postpone = async (task, dueAt) => {
    const r = await fetch(`${API_URL}/api/funnel/tasks/${task.id}`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify({ dueAt }),
    });
    if (r.ok) { toast.success('Срок перенесён'); load(); }
    else toast.error('Не удалось перенести');
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {/* Переключатель стоит первым и выглядит как у сделок: одно и то
            же действие — «показать иначе» — в двух разделах должно
            называться и выглядеть одинаково. */}
        <div className="fn-seg">
          {[['list', 'Списком', Rows3], ['board', 'Колонками', LayoutGrid]].map(([id, title, Icon]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTasksMode(id)}
              aria-pressed={tasksMode === id}
              className={`fn-seg-item${tasksMode === id ? ' is-on' : ''}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {title}
            </button>
          ))}
        </div>
        <button type="button" onClick={() => { setOnlyMine((v) => !v); setOnlyNobody(false); }}
                disabled={!me}
                className={`fn-chip${onlyMine ? ' is-on' : ''}`}>
          <User className="h-3.5 w-3.5" /> Только мои
        </button>
        <button type="button" onClick={() => setShowDone((v) => !v)}
                className={`fn-chip${showDone ? ' is-on' : ''}`}>
          {showDone ? 'Закрытые' : 'Открытые'}
        </button>
        {/* Поставить задачу, не открывая карточку. Утреннее «обзвонить
            этих четверых» иначе означает четыре открытых карточки и
            четыре возврата назад. */}
        <button type="button" className="fn-chip" onClick={() => setAdding(true)}>
          + поставить
        </button>
        {/* Тот же скелет, что над доской: слева фильтры, справа «…».
            Прежде здесь стоял голый значок обновления там, где на доске
            меню, и при переключении вкладки управление меняло и вид, и
            место — экран будто перестраивался под другой раздел. */}
        <CardMenu items={[
          { label: 'Обновить', icon: <RefreshCw className="h-3.5 w-3.5" />, onSelect: load },
        ]} />
        <button type="button" onClick={() => setOverdueOnly((v) => !v)}
                className={`fn-chip${overdueOnly ? ' is-on' : ''}`}>
          Просроченные
        </button>
        {/* Ничьи — отдельный вопрос, а не частный случай «моих». «Только
            мои» их прячет: исполнитель сличается точным равенством, а у
            ничьей задачи его нет. На доске менеджер ничьи сделки при этом
            видит — и без этого чипа 78 задач из 97 были невидимы ровно
            там, где решается, позвонят ли человеку. */}
        {data?.unassigned > 0 && (
          <button type="button"
                  onClick={() => { setOnlyNobody((v) => !v); setOnlyMine(false); }}
                  className={`fn-chip${onlyNobody ? ' is-on' : ''}`}>
            Ничьи <b className="fn-num font-medium">{data.unassigned}</b>
          </button>
        )}
        {/* Период по сроку: «что мне делать до конца дня» и «до конца
            недели». Эти два вопроса менеджер задаёт каждый день, а
            произвольный диапазон — раз в месяц, и он остался в ручке. */}
        <select value={period} onChange={(e) => setPeriod(e.target.value)}
                className="fn-select fn-select-quiet">
          <option value="">Любой срок</option>
          <option value="today">До конца дня</option>
          <option value="week">До конца недели</option>
        </select>
        {data?.facets?.types?.length > 1 && (
          <select value={type} onChange={(e) => setType(e.target.value)}
                  className="fn-select fn-select-quiet">
            <option value="">Все типы</option>
            {data.facets.types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        {data?.facets?.authors?.length > 1 && (
          <select value={author} onChange={(e) => setAuthor(e.target.value)}
                  className="fn-select fn-select-quiet">
            <option value="">Кто поставил</option>
            {data.facets.authors.map((a) => (
              <option key={a} value={a}>{managerLabel(a)}</option>
            ))}
          </select>
        )}
        {/* Ответственный. Сервер считал этот список с самого начала и
            отдавал его в `facets.assignees` — на экране его не было.
            Без него Максим не посмотрит день Влады, а Влада не найдёт,
            что передала Виолете. Показываем, когда исполнителей больше
            одного: одному человеку выбирать не из чего. */}
        {data?.facets?.assignees?.length > 1 && (
          <select value={assignee} onChange={(e) => setAssignee(e.target.value)}
                  className="fn-select fn-select-quiet">
            <option value="">Любой исполнитель</option>
            {data.facets.assignees.map((a) => (
              <option key={a} value={a}>{managerLabel(a)}</option>
            ))}
          </select>
        )}
        {/* Этап сделки. Отбор по нему сервер умел и молчал о том, что
            умеет: «перезвонить» на отправленном КП и «перезвонить» на
            первом контакте — разговоры разные, а в общем списке они
            стоят вперемешку. */}
        {(stages || []).length > 1 && (
          <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)}
                  className="fn-select fn-select-quiet">
            <option value="">Любой этап</option>
            {stages.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
          </select>
        )}
        {data && (
          <span className="text-[length:var(--fn-t-md)] leading-5 fn-ink-2">
            {/* Сколько их всего, а не сколько доехало на страницу. Рядом
                стояло «300 открытых · показаны 300 из 515» — два числа
                про одно и то же, и первое из них неправда. */}
            {data.total ?? data.count} {showDone ? 'закрытых' : 'открытых'}
            {!showDone && data.overdue > 0 && (
              <span className="fn-danger"> · {data.overdue} просрочено</span>
            )}
            {/* Молчать об обрезанном списке нельзя: сортировка идёт по сроку
                вверх, и не доедет именно «Позже» — пустая группа читается
                как «задач нет». */}
            {data.truncated && (
              <span className="fn-ink-3"> · показаны {data.count} из {data.total}</span>
            )}
          </span>
        )}
      </div>

      {!data && <p className="py-10 text-center text-sm fn-ink-2">Загружаю…</p>}
      {data?.tasks.length === 0 && (
        <p className="py-12 text-center text-sm fn-ink-2">
          {showDone ? 'Закрытых задач нет.' : 'Открытых задач нет — можно выдохнуть.'}
        </p>
      )}

      {/* Два вида одного и того же, как и у сделок: список отвечает «что
          я должен по порядку», колонки — «сколько где накопилось». В amo
          задачи разложены именно колонками по срокам, и менеджер видит
          день целиком, не прокручивая. Классы те же, что у доски сделок:
          один и тот же способ показать столбцы должен и выглядеть
          одинаково. */}
      {tasksMode === 'board' ? (
        <div className="fn-board fn-tasks-board flex overflow-x-auto">
          {groups.map((g) => (
            <div key={g.id} className="fn-col" style={{ '--fn-stage': g.tone }}>
              <div className="fn-col-head">
                <span className="fn-col-title">{g.title}</span>
                <span className="fn-col-figures">
                  {/* Счётчик — всё, что есть на сервере, а не сколько
                      строк доехало. В колонке их сто, а просроченных
                      бывает под шестьсот: число в шапке отвечает
                      «сколько накопилось», и подменять его длиной
                      страницы значит каждый день показывать ровно сто. */}
                  <span className="fn-col-count">
                    {data?.slices?.[g.id]?.total ?? g.tasks.length}
                  </span>
                </span>
              </div>
              <div className="fn-col-body" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                {g.tasks.length === 0 && (
                  <p className="px-1 py-6 text-center text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">—</p>
                )}
                {g.tasks.map((t) => (
                  <TaskRow key={t.id} task={t} onClose={setClosing} onOpenLead={onOpenLead}
                           onPostpone={postpone} stages={stages} />
                ))}
                {/* Говорим, что показано не всё. Молчание здесь читается
                    как «это вся колонка», и менеджер уверен, что
                    разобрал просрочку до конца. */}
                {(data?.slices?.[g.id]?.total || 0) > g.tasks.length && (
                  <p className="px-1 py-2 text-center text-[length:var(--fn-t-xs)] leading-4 fn-ink-3">
                    показаны {g.tasks.length} из {data.slices[g.id].total}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : groups.map((g) => (
        <section key={g.id} className="fn-task-group" style={{ '--fn-tone': g.tone }}>
          <div className="fn-task-group-head">
            <h3 className="fn-col-title">{g.title}</h3>
            <span className="fn-col-count">{g.tasks.length}</span>
          </div>
          <div className="fn-task-list">
            {g.tasks.map((t) => (
              <TaskRow key={t.id} task={t} onClose={setClosing} onOpenLead={onOpenLead}
                       onPostpone={postpone} stages={stages} />
            ))}
          </div>
        </section>
      ))}

      <TaskDoneDialog
        task={closing}
        onClose={() => setClosing(null)}
        onDone={closeTask}
        onNext={async (t, next) => {
          // У задачи с удалённым лидом следующую ставить некуда —
          // молча пропускаем, а не падаем с ошибкой на весь экран.
          if (!t.leadId) return;
          const r = await fetch(`${API_URL}/api/funnel/leads/${t.leadId}/tasks`, {
            method: 'POST', headers: authHeaders(),
            body: JSON.stringify({
              title: next.title,
              type: next.type,
              dueAt: next.dueAt ? new Date(next.dueAt).toISOString() : null,
            }),
          });
          if (r.ok) toast.success('Следующая задача поставлена');
          else toast.error('Задача закрыта, но следующую поставить не вышло');
          load();
        }}
      />

      <NewTaskDialog
        open={adding}
        onClose={() => setAdding(false)}
        onCreated={load}
        types={types}
        managers={managers}
        me={me}
      />
    </div>
  );
}

function StagesDialog({ open, onClose, stages, onSaved }) {
  const [rows, setRows] = useState([]);
  const [pipes, setPipes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showIds, setShowIds] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows(stages.map((s) => ({ ...s })));
    fetch(`${API_URL}/api/funnel/settings`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setPipes(d.pipelines || []))
      .catch(() => {});
  }, [open, stages]);

  const patchPipe = (i, k, v) =>
    setPipes((ps) => ps.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));

  const addPipe = () => {
    const id = `pipe_${Date.now().toString(36)}`;
    setPipes((ps) => [...ps, { id, name: 'Новая воронка', sortOrder: ps.length + 1, isDefault: false }]);
    // Воронка без этапов не сохранится, поэтому сразу даём ей первый.
    setRows((r) => [...r, {
      id: `stage_${Date.now().toString(36)}`, name: 'Первый этап', pipelineId: id,
      color: '#7B8C89', sortOrder: r.length + 1, isWon: false, isLost: false,
    }]);
  };

  const removePipe = (id) => {
    if (rows.some((r) => (r.pipelineId || pipes[0]?.id) === id)
        && !window.confirm('Вместе с воронкой пропадут с доски её этапы. Сделки останутся в базе. Удалить?')) return;
    setPipes((ps) => ps.filter((p) => p.id !== id));
    setRows((r) => r.filter((row) => (row.pipelineId || pipes[0]?.id) !== id));
  };

  const setDefaultPipe = (id) =>
    setPipes((ps) => ps.map((p) => ({ ...p, isDefault: p.id === id })));

  const patch = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const move = (i, delta) => setRows((r) => {
    const next = [...r];
    const j = i + delta;
    if (j < 0 || j >= next.length) return r;
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const remove = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const add = (pipelineId) => setRows((r) => [...r, {
    id: `stage_${Date.now().toString(36)}`, name: 'Новая стадия', pipelineId,
    color: '#7B8C89', sortOrder: r.length + 1, isWon: false, isLost: false,
  }]);

  const save = async () => {
    if (rows.some((r) => !r.name.trim())) { toast.error('У стадии не может быть пустого имени'); return; }
    setBusy(true);
    try {
      const payload = {
        stages: rows.map((r, i) => ({ ...r, sortOrder: i + 1 })),
        pipelines: pipes.map((p, i) => ({ ...p, sortOrder: i + 1 })),
      };
      const res = await fetch(`${API_URL}/api/funnel/settings`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `Ошибка ${res.status}`);
      toast.success('Стадии сохранены');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Воронки и стадии</DialogTitle>
          <DialogDescription>
            Порядок задаёт колонки на доске. «Победа» — стадия, с которой сделка уходит в CRM саун;
            «отказ» прячется фильтром.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-end">
          <button type="button" onClick={() => setShowIds(!showIds)}
                  className="text-[length:var(--fn-t-xs)] leading-4 fn-ink-3 fn-hover-ink">
            {showIds ? 'скрыть идентификаторы' : 'показать идентификаторы'}
          </button>
        </div>

        {/* Воронки — группы этапов. Как в amoCRM: у менеджеров своя доска,
            у бота своя, и этапы не мешаются между собой. */}
        <div className="space-y-4">
          {pipes.map((pipe, pi) => {
            const mine = rows
              .map((r, idx) => ({ r, idx }))
              .filter(({ r }) => (r.pipelineId || pipes[0]?.id) === pipe.id);
            return (
              <section key={pipe.id} className="rounded-[var(--fn-r-sm)] border border-slate-200 p-3">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <Input value={pipe.name} onChange={(e) => patchPipe(pi, 'name', e.target.value)}
                         className="h-8 max-w-56 font-medium" />
                  {showIds && (
                    <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[length:var(--fn-t-xs)] leading-4 fn-ink-3">
                      {pipe.id}
                    </code>
                  )}
                  <label className="flex items-center gap-1 text-xs fn-ink-3">
                    <input type="radio" name="defaultPipe" checked={!!pipe.isDefault}
                           onChange={() => setDefaultPipe(pipe.id)} />
                    по умолчанию
                  </label>
                  <span className="text-xs fn-ink-3">{mine.length} стадий</span>
                  <Button size="sm" variant="ghost" className="ml-auto"
                          onClick={() => removePipe(pipe.id)} disabled={pipes.length <= 1}>
                    <Trash2 className="h-4 w-4 fn-danger" />
                  </Button>
                </div>

                <div className="space-y-2">
                  {mine.map(({ r, idx: i }) => (
            <div key={r.id} className="flex items-center gap-2 rounded-[var(--fn-r-sm)] border border-slate-200 p-2">
              <input
                type="color"
                value={r.color}
                onChange={(e) => patch(i, 'color', e.target.value)}
                className="h-8 w-8 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0"
                aria-label="цвет стадии"
              />
              <Input value={r.name} onChange={(e) => patch(i, 'name', e.target.value)} className="flex-1" />
              {showIds && (
                <code className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[length:var(--fn-t-xs)] leading-4 fn-ink-3">
                  {r.id}
                </code>
              )}
              <label className="flex shrink-0 items-center gap-1 text-xs fn-ink-3">
                <input type="checkbox" checked={!!r.isWon}
                  onChange={(e) => patch(i, 'isWon', e.target.checked)} />
                победа
              </label>
              <label className="flex shrink-0 items-center gap-1 text-xs fn-ink-3">
                <input type="checkbox" checked={!!r.isLost}
                  onChange={(e) => patch(i, 'isLost', e.target.checked)} />
                отказ
              </label>
              <Button size="sm" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0}>
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => move(i, 1)} disabled={i === rows.length - 1}>
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(i)} disabled={rows.length <= 1}>
                <Trash2 className="h-4 w-4 fn-danger" />
              </Button>
            </div>
                  ))}
                </div>

                <button type="button" onClick={() => add(pipe.id)}
                        className="mt-2 text-[length:var(--fn-t-xs)] leading-4 fn-ink-3 fn-hover-ink">
                  + стадия в «{pipe.name}»
                </button>
              </section>
            );
          })}
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={addPipe} className="flex-1">
            <Plus className="mr-2 h-4 w-4" /> Добавить воронку
          </Button>
          <Button onClick={save} disabled={busy} className="flex-1">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Сохранить
          </Button>
        </div>

        <p className="text-xs fn-ink-3">
          Удаление стадии не трогает лиды: они останутся с прежним идентификатором и просто
          перестанут попадать на доску, пока стадию не вернуть. Идентификаторы менять нельзя —
          на них ссылаются уже заведённые сделки и правила автоматизации.
        </p>
      </DialogContent>
    </Dialog>
  );
}

/** Горизонтальная полоса: одна мера, один цвет — сравниваем длину, не оттенок. */
function Bar({ label, value, max, extra, total }) {
  const pct = max > 0 ? Math.max((value / max) * 100, value > 0 ? 1.5 : 0) : 0;
  // Доля считается от всего потока, а длина полосы — от лидера. Это разные
  // числа, и подписывать полосу процентом от максимума было бы враньём.
  const share = total ? Math.round((value / total) * 100) : null;
  return (
    <div className="grid grid-cols-[minmax(0,10rem)_1fr_auto] items-center gap-3 py-[3px]">
      <span className="truncate text-[length:var(--fn-t-sm)] leading-4 opacity-70" title={label}>{label}</span>
      <span className="h-[14px] rounded-[var(--fn-r-xs)]" style={{ background: 'var(--fn-line)' }}>
        <span className="block h-full rounded-[var(--fn-r-xs)]"
              style={{ width: `${pct}%`, background: 'hsl(160 55% 42%)' }} />
      </span>
      <span className="fn-num whitespace-nowrap text-[length:var(--fn-t-sm)] leading-4 tabular-nums">
        {value}
        {share != null && <span className="fn-ink-2"> · {share}%</span>}
        {extra ? <span className="fn-ink-2"> · {extra}</span> : null}
      </span>
    </div>
  );
}

/** Рабочий стол — то, с чего менеджер начинает день.
 *
 * В amoCRM это первый раздел: плитки с числами, по которым видно, где
 * горит. У нас те же числа были сводкой над доской и статистикой в
 * модальном окне — то есть их надо было искать. Раздел собирает их в одно
 * место и раскрашивает по смыслу: тревожное красным, деньги зелёным,
 * остальное нейтрально.
 */
function DeskView({ onGo, admin }) {
  const [board, setBoard] = useState(null);
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState('30');
  // Заказы, за которые уже взяли предоплату. Отдельной ручкой, а не внутри
  // аналитики: источник у них чужой — карточки производственного сервиса, и
  // отказ доступа к ним не должен гасить весь рабочий стол.
  const [orders, setOrders] = useState(null);
  const [ordersError, setOrdersError] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/funnel/board`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null)).then(setBoard).catch(() => {});
    fetch(`${API_URL}/api/funnel/orders/summary`, { headers: authHeaders() })
      .then(async (r) => {
        if (r.ok) return setOrders(await r.json());
        const body = await r.json().catch(() => ({}));
        setOrdersError(body.detail || 'Заказы производства прочитать не удалось');
      })
      .catch(() => setOrdersError('Заказы производства прочитать не удалось'));
  }, []);
  useEffect(() => {
    setStats(null);
    fetch(`${API_URL}/api/funnel/analytics?days=${days}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null)).then(setStats).catch(() => {});
  }, [days]);

  const s = board?.summary;

  return (
    <div className="space-y-5">
      {/* Плитки: сначала то, что требует действия сегодня, потом объём
          работы, потом деньги. Порядок — приоритет, а не алфавит. */}
      {/* Пока числа не приехали, на плитках стоит полоска-заглушка, а не
          прочерк. Прочерк здесь означал бы «ноль» — четыре прочерка
          читаются как «всё разобрано», то есть ровно наоборот тому, что
          происходит. Один знак не может отвечать и «пусто», и «ещё
          считаю»: это два разных состояния, и различать их должен экран,
          а не догадка смотрящего. */}
      {/* Четыре числа дня — на своей подложке. Белые плитки на белом поле
          стояли вровень со всем остальным на экране, хотя читают их
          первыми и каждое утро. Подложка отделяет «с чего начать» от
          «что было», и это та же картина, к которой менеджеры привыкли
          в amo. */}
      <div className="fn-desk-hero">
      <div className="fn-tiles">
        <button type="button" className="fn-tile fn-tile-alarm" onClick={() => onGo('overdue')}>
          <b>{s ? s.overdue : <span className="fn-skel-num" />}</b>
          <span>просрочено</span>
        </button>
        <button type="button" className="fn-tile" onClick={() => onGo('today')}>
          <b>{s ? s.today : <span className="fn-skel-num" />}</b>
          <span>задача на сегодня</span>
        </button>
        <button type="button" className="fn-tile fn-tile-warn" onClick={() => onGo('noTask')}>
          <b>{s ? s.noTask : <span className="fn-skel-num" />}</b>
          <span>без задачи</span>
        </button>
        <button type="button" className="fn-tile" onClick={() => onGo('all')}>
          <b>{s ? s.total : <span className="fn-skel-num" />}</b>
          <span>в работе</span>
        </button>
      </div>
      </div>

      {ordersError && (
        <p className="text-[length:var(--fn-t-sm)] leading-4 fn-ink-3">{ordersError}</p>
      )}

      {/* Деньги производства — не работа менеджера.
          Ниже плиток дня шли «Заказы в производстве»: суммы заказов,
          внесённое, недоплаченное и список фамилий с копейками, которые
          не сходятся. Половина первого экрана у человека, который
          пришёл узнать, кому звонить. Показываем это владельцу, а
          менеджеру оставляем то, чем он занят. */}
      {admin && orders && orders.totals.withStage > 0 && (
        <>
          <h3 className="fn-section-label">Заказы в производстве</h3>
          <div className="fn-tiles">
            {/* «Осталось изготовить» появляется, только когда на доске
                заказов этапы помечены признаком «сауна сделана». Пока его
                нет, число не выводим из названий этапов — показываем
                честное «в работе», а что уже готово, видно в разбивке. */}
            {orders.totals.toMake != null && (
              <div className="fn-tile fn-tile-flat">
                <b>{orders.totals.toMake}</b>
                <span>{plural(orders.totals.toMake, 'сауну', 'сауны', 'саун')} изготовить</span>
              </div>
            )}
            <div className="fn-tile fn-tile-flat">
              <b>{orders.totals.open}</b>
              <span>{plural(orders.totals.open, 'заказ', 'заказа', 'заказов')} в работе</span>
            </div>
            <div className="fn-tile fn-tile-flat">
              <b>{orders.totals.amount.toLocaleString('pl-PL')} zł</b>
              <span>на сумму заказов</span>
            </div>
            <div className="fn-tile fn-tile-flat fn-tile-good">
              <b>{orders.totals.paid.toLocaleString('pl-PL')} zł</b>
              <span>внесено</span>
            </div>
            <div className="fn-tile fn-tile-flat">
              <b>{orders.totals.due.toLocaleString('pl-PL')} zł</b>
              <span>не доплачено</span>
            </div>
          </div>
          {/* Из скольких заказов сложены деньги — рядом с ними, а не в
              подсказке: без этой строки остаток читается как весь долг, а он
              посчитан только по тем, где суммы сошлись. */}
          <p className="text-[length:var(--fn-t-sm)] leading-4 fn-ink-3">
            Деньги посчитаны по {orders.totals.counted} из {orders.totals.withStage}{' '}
            {plural(orders.totals.withStage, 'заказа', 'заказов', 'заказов')}
            {orders.totals.noMoney > 0 && `; у ${orders.totals.noMoney} оплаты не заполнены`}
            {orders.unclear.length > 0 && `; ${orders.unclear.length} не сходятся`}.
            {/* Пересчёт — это наше чтение чужой записи. Прятать его нельзя:
                человек вправе знать, что «17,5» мы прочли как 17 500. */}
            {orders.totals.rescaled > 0 && (
              <> В {orders.totals.rescaled}{' '}
                {plural(orders.totals.rescaled, 'заказе', 'заказах', 'заказах')}{' '}
                суммы записаны в тысячах — прочитаны как злотые, потому что
                только так они сходятся с суммой заказа.</>
            )}
          </p>

          {/* Этапы — чипами, а не плитками: плиточная сетка тянет элементы
              на всю ширину, и один этап превращался в пустой блок во весь
              экран. Здесь же их бывает от одного до пяти. */}
          {orders.stages.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {orders.stages.map((st) => (
                /* Число текстом, не в кружке fn-chip-count: тот рисует фон
                   из currentColor, а текст — цветом фона страницы, и вне
                   включённого фильтра (единственное место, где он до сих пор
                   стоял) число на светлом чипе пропадает. */
                <span key={st.id} className="fn-chip">
                  {st.name}
                  <b className="fn-num font-medium">{st.count}</b>
                </span>
              ))}
            </div>
          )}

          {orders.unclear.length > 0 && (
            <section>
              <h3 className="fn-section-label mb-2">Суммы не сходятся — свести руками</h3>
              {/* Строкой слева направо, а не рядом «подпись — значение»:
                  ряды карточки разводят имя и суммы по краям, и на широком
                  экране между ними полэкрана пустоты. */}
              <div>
                {/* Показываем десять: список нужен, чтобы пойти и свести,
                    а не чтобы занять собой весь рабочий стол. */}
                {orders.unclear.slice(0, 10).map((u) => (
                  <div key={u.id}
                       className="flex flex-wrap items-baseline gap-x-2 py-1
                                  text-[length:var(--fn-t-md)] leading-5">
                    <b className="font-medium">{u.client || '—'}</b>
                    {/* Показываем написанное в карточке как есть. «20» при
                        заказе на 32 351 скорее всего значит 20 000, но такая
                        замена иногда сходится точно, а иногда расходится на
                        тысячу — то есть это догадка, а не правило. */}
                    <span className="fn-ink-3">
                      внесено <span className="fn-num">{u.paidRaw ?? '—'}</span>
                      {' · '}остаток <span className="fn-num">{u.dueRaw ?? '—'}</span>
                      {u.total != null && (
                        <> · заказ <span className="fn-num">
                          {u.total.toLocaleString('pl-PL')} zł</span></>
                      )}
                      {u.manager && ` · ${u.manager}`}
                    </span>
                  </div>
                ))}
              </div>
              {orders.unclear.length > 10 && (
                <p className="mt-1 text-[length:var(--fn-t-sm)] leading-4 fn-ink-3">
                  и ещё {orders.unclear.length - 10}.
                </p>
              )}
            </section>
          )}
        </>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <h3 className="fn-section-label">Итоги за период</h3>
        <select value={days} onChange={(e) => setDays(e.target.value)} className="fn-select fn-select-quiet">
          <option value="7">7 дней</option>
          <option value="30">30 дней</option>
          <option value="90">90 дней</option>
          <option value="365">год</option>
        </select>
        {stats && <span className="text-[length:var(--fn-t-sm)] leading-4 fn-ink-3">{stats.totalLeads} заявок</span>}
      </div>

      {!stats && <p className="text-[length:var(--fn-t-md)] leading-5 fn-ink-3">Считаю…</p>}

      {stats && (
        <>
          <div className="fn-tiles">
            <div className="fn-tile fn-tile-flat">
              <b>{stats.reaction.medianHours ?? '—'}{stats.reaction.medianHours != null ? ' ч' : ''}</b>
              <span>медиана до первого касания</span>
            </div>
            <div className="fn-tile fn-tile-flat">
              <b>{stats.reaction.withinTarget}/{stats.reaction.measured}</b>
              <span>уложились в {stats.reaction.targetHours} ч</span>
            </div>
            <div className={`fn-tile fn-tile-flat${stats.reaction.untouched ? ' fn-tile-alarm' : ''}`}>
              <b>{stats.reaction.untouched}</b>
              <span>вообще не тронуты</span>
            </div>
          </div>

        </>
      )}
    </div>
  );
}


function AnalyticsDialog({ open, onClose }) {
  const [days, setDays] = useState('30');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setData(null); setError('');
    fetch(`${API_URL}/api/funnel/analytics?days=${days}`, { headers: authHeaders() })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.detail || `Ошибка ${r.status}`);
        return body;
      })
      .then(setData)
      .catch((e) => setError(e.message));
  }, [open, days]);

  const maxOf = (rows, key) => Math.max(1, ...rows.map((r) => r[key] || 0));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="funnel-ui max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Статистика воронки</DialogTitle>
          <DialogDescription>Только заявки до предоплаты. Что было дальше — в CRM саун.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 дней</SelectItem>
              <SelectItem value="30">30 дней</SelectItem>
              <SelectItem value="90">90 дней</SelectItem>
              <SelectItem value="365">Год</SelectItem>
            </SelectContent>
          </Select>
          {data && <span className="text-[length:var(--fn-t-md)] leading-5 fn-ink-2">{data.totalLeads} заявок за период</span>}
        </div>

        {error && <p className="text-sm fn-danger">{error}</p>}
        {!data && !error && <p className="py-8 text-center text-sm fn-ink-2">Считаю…</p>}

        {data && (
          <div className="space-y-6">
            <section>
              <h3 className="fn-section-label mb-2">Скорость реакции</h3>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="fn-stat flex-col items-start">
                  <b>{data.reaction.medianHours ?? '—'}{data.reaction.medianHours != null ? ' ч' : ''}</b>
                  <span>медиана до первого касания</span>
                </div>
                <div className="fn-stat flex-col items-start">
                  <b>{data.reaction.withinTarget}/{data.reaction.measured}</b>
                  <span>уложились в {data.reaction.targetHours} ч</span>
                </div>
                <div className="fn-stat flex-col items-start">
                  <b className={data.reaction.untouched ? 'fn-danger' : ''}>{data.reaction.untouched}</b>
                  <span>вообще не тронуты</span>
                </div>
              </div>
            </section>

            {data.byLossReason?.length > 0 && (
              <section>
                <h3 className="fn-section-label mb-2">Причины отказа</h3>
                <div className="space-y-1">
                  {data.byLossReason.map((r) => (
                    <Bar key={r.reason} label={r.reason} value={r.count}
                         max={data.byLossReason[0].count}
                         total={data.byLossReason.reduce((a, x) => a + x.count, 0)} />
                  ))}
                </div>
              </section>
            )}

            {data.tasks && (
              <section>
                <h3 className="fn-section-label mb-2">Задачи</h3>
                <div className="grid gap-4 sm:grid-cols-4">
                  <div className="fn-stat flex-col items-start">
                    <b>{data.tasks.created}</b><span>поставлено</span>
                  </div>
                  <div className="fn-stat flex-col items-start">
                    <b>{data.tasks.done}</b><span>закрыто</span>
                  </div>
                  <div className="fn-stat flex-col items-start">
                    <b className={data.tasks.overdue ? 'fn-danger' : ''}>{data.tasks.overdue}</b>
                    <span>просрочено</span>
                  </div>
                  <div className="fn-stat flex-col items-start">
                    <b>{data.tasks.medianCloseHours ?? '—'}{data.tasks.medianCloseHours != null ? ' ч' : ''}</b>
                    <span>медиана до закрытия</span>
                  </div>
                </div>
              </section>
            )}

            <section>
              <h3 className="fn-section-label mb-2">Менеджеры</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-[length:var(--fn-t-md)] leading-5">
                  <thead>
                    <tr className="text-left">
                      {['Менеджер', 'Всего', 'Договор', 'Отказ', 'Зависло', 'Медиана касания',
                        'Задач', 'Закрыто', 'Просрочено', 'Медиана закрытия'].map((h) => (
                        <th key={h} className="fn-section-label pb-1.5 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.byManager.map((m) => (
                      <tr key={m.manager} className="border-t" style={{ borderColor: 'var(--fn-line)' }}>
                        <td className="py-1.5">{m.manager}</td>
                        <td className="fn-num">{m.total}</td>
                        <td className="fn-num">{m.won}</td>
                        <td className="fn-num fn-ink-2">{m.lost}</td>
                        <td className={`fn-num ${m.stale ? 'fn-warn' : 'fn-ink-2'}`}>{m.stale}</td>
                        <td className="fn-num opacity-70">{m.medianTouchHours ?? '—'}</td>
                        <td className="fn-num">
                          {m.tasks?.created ?? 0}
                          {m.tasks?.auto > 0 && (
                            <span className="ml-1 text-[length:var(--fn-t-xs)] leading-4 fn-ink-2" title="из них поставлено правилами">
                              ({m.tasks.auto})
                            </span>
                          )}
                        </td>
                        <td className="fn-num">{m.tasks?.done ?? 0}</td>
                        <td className={`fn-num ${m.tasks?.overdue ? 'fn-danger' : 'fn-ink-2'}`}>
                          {m.tasks?.overdue ?? 0}
                        </td>
                        <td className="fn-num opacity-70">{m.tasks?.medianCloseHours ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">
                Зависшими считаются сделки в работе, которых не касались дольше 72 часов.
                В скобках рядом с задачами — сколько из них поставлено правилами, а не человеком.
                Касание — комментарий, смена этапа или назначение; открыть карточку не считается.
              </p>
            </section>

            {data.byDay?.length > 1 && (
              <section>
                <h3 className="fn-section-label mb-2">Заявки по дням</h3>
                <div className="flex items-end gap-[3px]" style={{ height: 56 }}>
                  {data.byDay.map((d) => {
                    const max = Math.max(1, ...data.byDay.map((x) => x.count));
                    return (
                      <div key={d.day} className="group relative flex-1" title={`${d.day}: ${d.count}`}>
                        <div className="w-full rounded-[var(--fn-r-xs)]"
                             style={{ height: `${Math.max((d.count / max) * 56, 2)}px`,
                                      background: 'hsl(160 55% 42%)' }} />
                      </div>
                    );
                  })}
                </div>
                <div className="mt-1 flex justify-between text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">
                  <span>{data.byDay[0].day}</span>
                  <span>{data.byDay[data.byDay.length - 1].day}</span>
                </div>
              </section>
            )}

            <section>
              <h3 className="fn-section-label mb-2">Воронка</h3>
              {data.byStage.filter((s) => s.count).map((s) => (
                <Bar key={s.id} label={s.name} value={s.count} max={maxOf(data.byStage, 'count')} />
              ))}
            </section>

            {[['ad_creative', 'Креативы'], ['ad_campaign', 'Кампании'], ['ad_platform', 'Площадки']].map(
              ([key, title]) => (data.byAd[key]?.length ? (
                <section key={key}>
                  <h3 className="fn-section-label mb-2">{title}</h3>
                  {data.byAd[key].map((a) => (
                    <Bar key={a.name} label={a.name} value={a.leads} max={maxOf(data.byAd[key], 'leads')}
                         total={data.totalLeads} extra={a.won ? `${a.won} догов.` : null} />
                  ))}
                </section>
              ) : null)
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Почтовые ящики. Доступ выдаётся на каждый ящик отдельно: админ входит
 *  тем ящиком, который подключает. */
/** Раздел почты: список писем по всем видимым ящикам.
 *
 *  Отдельный режим рядом со сделками и задачами — письмо часто приходит
 *  раньше, чем появляется сделка, и до этого момента его негде увидеть. */

/** Правила: событие → условия → действия.
 *
 *  До этого экрана правила заводились только через API — механизм каждый
 *  день ставил людям задачи, а посмотреть и выключить их было негде. */
function RulesDialog({ open, onClose, stages, managers, focusStage }) {
  const [rules, setRules] = useState([]);
  const [events, setEvents] = useState({});
  const [actions, setActions] = useState({});
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState({});
  // Исключения по источникам свёрнуты, пока их не раскроют: строка из
  // четырнадцати кнопок читается как часть правила, хотя почти всегда
  // пуста.
  const [openExcept, setOpenExcept] = useState({});
  const [conf, setConf] = useState(null);
  const [channels, setChannels] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/funnel/rules`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Не удалось загрузить правила');
      const d = await r.json();
      setRules(d.rules || []);
      setEvents(d.events || {});
      setActions(d.actions || {});
    } catch (e) { toast.error(e.message); }
  }, []);

  useEffect(() => { if (open) { load(); setChecked({}); } }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const get = (path, pick) => fetch(`${API_URL}${path}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null)).then((d) => d && pick(d)).catch(() => {});
    get('/api/funnel/rules/automation', setConf);
    get('/api/funnel/chat/channels', (d) => setChannels((d.channels || []).filter((c) => c.ready)));
    get('/api/funnel/chat/templates', (d) => setTemplates(d.templates || []));
    get('/api/integrations/gmail/mailboxes', (d) => setMailboxes(d.mailboxes || []));
  }, [open]);

  const saveConf = async (fields) => {
    const next = { ...conf, ...fields };
    setConf(next);
    try {
      const r = await fetch(`${API_URL}/api/funnel/rules/automation`, {
        method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sendingEnabled: next.sendingEnabled,
          quietFrom: next.quietFrom, quietTo: next.quietTo,
        }),
      });
      if (!r.ok) throw new Error('Не удалось сохранить');
      setConf(await r.json());
    } catch (e) { toast.error(e.message); }
  };

  // Пришли с плюсика на колонке: показываем правила этого этапа, а если их
  // нет — сразу заготовку с уже выбранным этапом, чтобы не выбирать его
  // второй раз руками.
  const [ready, setReady] = useState(false);
  useEffect(() => { if (!open) setReady(false); }, [open]);
  useEffect(() => {
    if (!open || !focusStage || ready || !rules.length) return;
    setReady(true);
    const has = rules.some((r) => r.event === 'stage_enter' && r.stageId === focusStage.id);
    if (!has) {
      setRules((prev) => [...prev, {
        id: `R-${Math.random().toString(16).slice(2, 8).toUpperCase()}`,
        name: `На этапе «${focusStage.name}»`, enabled: false, event: 'stage_enter',
        stageId: focusStage.id, hours: null, productType: null, source: null,
        onlyUnassigned: false, excludeSources: [], applyToExisting: false, activatedAt: null,
        actions: [{ type: 'create_task', taskType: 'Связаться', title: '', dueInHours: 24,
                   to: '', text: '', stageId: null, channelId: '', templateId: '',
                   templateValues: [], mailbox: '', subject: '' }],
      }]);
    }
  }, [open, focusStage, ready, rules]);

  const shown = focusStage
    ? rules.filter((r) => r.event === 'stage_enter' && r.stageId === focusStage.id)
    : rules;

  const patch = (i, fields) =>
    setRules(rules.map((r, n) => (n === i ? { ...r, ...fields } : r)));

  const patchAction = (i, j, fields) =>
    patch(i, { actions: rules[i].actions.map((a, n) => (n === j ? { ...a, ...fields } : a)) });

  const addRule = () => setRules([...rules, {
    id: `R-${Math.random().toString(16).slice(2, 8).toUpperCase()}`,
    name: focusStage ? `На этапе «${focusStage.name}»` : 'Новое правило',
    enabled: false,
    event: focusStage ? 'stage_enter' : 'lead_created',
    stageId: focusStage ? focusStage.id : null,
    hours: null, productType: null, source: null,
    onlyUnassigned: false, excludeSources: [], applyToExisting: false, activatedAt: null,
    actions: [{ type: 'create_task', taskType: 'Связаться', title: '', dueInHours: 2,
               to: '', text: '', stageId: null, channelId: '', templateId: '',
               templateValues: [], mailbox: '', subject: '' }],
  }]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/funnel/rules`, {
        method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Ошибка ${r.status}`);
      setRules(d.rules || []);
      toast.success('Правила сохранены');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const check = async (rule) => {
    try {
      // Шлём то, что сейчас на экране: иначе после правки условия человек
      // увидит число от прошлой сохранённой версии.
      const r = await fetch(`${API_URL}/api/funnel/rules/test/${rule.id}`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(rule),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Правило ещё не сохранено');
      setChecked({ ...checked, [rule.id]: d });
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="funnel-ui max-h-[88vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{focusStage ? `Этап «${focusStage.name}»` : 'Автоматизация'}</DialogTitle>
          <DialogDescription>
            {focusStage
              ? 'Что происходит со сделкой, когда она попадает на этот этап.'
              : 'Что система делает сама: ставит задачи, назначает менеджера, пишет комментарий.'}
          </DialogDescription>
        </DialogHeader>

        {conf && !focusStage && (
          <div className="rounded-[var(--fn-r-sm)] border px-3 py-2" style={{ borderColor: 'var(--fn-line)' }}>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => saveConf({ sendingEnabled: !conf.sendingEnabled })}
                      className="flex items-center gap-2 text-sm">
                {conf.sendingEnabled
                  ? <CheckSquare className="h-4 w-4 fn-good" />
                  : <Square className="h-4 w-4 fn-ink-2" />}
                Отправлять сообщения и письма клиентам
              </button>
              <span className="ml-auto text-[length:var(--fn-t-sm)] leading-4 fn-ink-2">не писать с</span>
              <select value={conf.quietFrom} onChange={(e) => saveConf({ quietFrom: +e.target.value })}
                      className="fn-select">
                {[...Array(24).keys()].map((h) => <option key={h} value={h}>{h}:00</option>)}
              </select>
              <span className="text-[length:var(--fn-t-sm)] leading-4 fn-ink-2">до</span>
              <select value={conf.quietTo} onChange={(e) => saveConf({ quietTo: +e.target.value })}
                      className="fn-select">
                {[...Array(24).keys()].map((h) => <option key={h} value={h}>{h}:00</option>)}
              </select>
            </div>
            <p className="mt-1.5 text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">
              {conf.sendingEnabled
                ? 'Сообщения уходят клиентам. В тихие часы откладываются до утра.'
                : 'Отправка выключена: правило запишет в ленту, что ушло бы, но клиент ничего не получит.'}
              {conf.quietNow && ' Сейчас тихие часы.'}
              {conf.queued > 0 && ` Отложено: ${conf.queued}.`}
            </p>
          </div>
        )}

        <div className="space-y-3">
          {shown.length === 0 && <p className="text-sm fn-ink-2">Ни одного правила.</p>}

          {shown.map((r) => {
            const i = rules.indexOf(r);
            return (
            <div key={r.id} className="rounded-[var(--fn-r-sm)] border p-3" style={{ borderColor: 'var(--fn-line)' }}>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => patch(i, { enabled: !r.enabled })}
                        className="shrink-0" title={r.enabled ? 'Выключить' : 'Включить'}>
                  {r.enabled ? <CheckSquare className="h-4 w-4 fn-good" />
                             : <Square className="h-4 w-4 fn-ink-2" />}
                </button>
                <Input value={r.name} onChange={(e) => patch(i, { name: e.target.value })}
                       className="h-8 flex-1 text-[length:var(--fn-t-md)] leading-5" />
                <Button variant="ghost" size="sm"
                        onClick={() => setRules(rules.filter((_, n) => n !== i))}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>

              {/* Три группы с подписями, а не поток полей: правило читается
                  как предложение «когда — если — тогда», и глазу нужно за
                  что зацепиться, чтобы не перечитывать всё подряд. */}
              <div className="fn-rule-row">
                <span className="fn-rule-label">Когда</span>
                <div className="fn-rule-fields">
                <select value={r.event} onChange={(e) => patch(i, { event: e.target.value })}
                        className="fn-select">
                  {Object.entries(events).map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
                </select>

                {r.event === 'stage_enter' && (
                  <>
                    <select value={r.stageId || ''} onChange={(e) => patch(i, { stageId: e.target.value })}
                            className="fn-select">
                      <option value="">выберите этап</option>
                      {stages.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
                    </select>
                    {/* Задержка. Нужна, когда условие на момент входа ещё
                        не сложилось: бот заполняет поле через минуту после
                        перевода, и мгновенное правило увидит пустое. */}
                    <span className="fn-rule-group">
                      <span className="fn-ink-2">через</span>
                      <Input type="number" min="0" max="1440" value={r.delayMinutes || ''}
                             placeholder="0"
                             onChange={(e) => patch(i, { delayMinutes: Number(e.target.value) || null })}
                             className="h-8 w-16 text-[length:var(--fn-t-md)] leading-5" />
                      <span className="fn-ink-2">мин</span>
                    </span>
                  </>
                )}

                {['no_activity', 'task_overdue', 'no_client_reply'].includes(r.event) && (
                  <>
                    <span className="fn-rule-group">
                      <Input type="number" min="1" value={r.hours || ''}
                             onChange={(e) => patch(i, { hours: Number(e.target.value) || null })}
                             className="h-8 w-20 text-[length:var(--fn-t-md)] leading-5" />
                      <span className="fn-ink-2">
                        {{ task_overdue: 'часов после срока',
                           no_client_reply: 'часов с его последнего сообщения',
                         }[r.event] || 'часов'}
                      </span>
                    </span>
                  </>
                )}
                {/* Разбор ответа клиента. Строк может быть несколько: сначала
                    кнопки точным текстом, ниже слова внутри фразы. Порядок
                    правил в списке и есть порядок разбора — срабатывает
                    первое подошедшее, поэтому узкие условия держат выше. */}
                {r.event === 'client_message' && (
                  <span className="fn-rule-group flex-col items-stretch gap-1">
                    {(r.textMatch || []).map((m, k) => (
                      <span key={k} className="flex items-center gap-2">
                        <select value={m.mode || 'equals'} className="fn-select"
                                onChange={(e) => {
                                  const next = [...(r.textMatch || [])];
                                  next[k] = { ...next[k], mode: e.target.value };
                                  patch(i, { textMatch: next });
                                }}>
                          <option value="equals">ровно</option>
                          <option value="contains">содержит</option>
                        </select>
                        <Input value={m.value || ''} placeholder="текст от клиента"
                               onChange={(e) => {
                                 const next = [...(r.textMatch || [])];
                                 next[k] = { ...next[k], value: e.target.value };
                                 patch(i, { textMatch: next });
                               }}
                               className="h-8 flex-1 text-[length:var(--fn-t-md)] leading-5" />
                        <button type="button" className="fn-hover-ink fn-ink-3"
                                title="убрать условие"
                                onClick={() => patch(i, {
                                  textMatch: (r.textMatch || []).filter((_, x) => x !== k),
                                })}>
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                    <button type="button" className="fn-hover-ink fn-ink-2 self-start text-[length:var(--fn-t-sm)]"
                            onClick={() => patch(i, {
                              textMatch: [...(r.textMatch || []), { mode: 'equals', value: '' }],
                            })}>
                      + условие
                    </button>
                    {!(r.textMatch || []).length && (
                      <span className="fn-ink-3 text-[length:var(--fn-t-sm)] leading-4">
                        Условий нет — правило поймает любой ответ. Такое держат
                        последним в списке.
                      </span>
                    )}
                  </span>
                )}
                {/* Расписание стоит здесь, а не среди условий: «ежедневно в
                    одиннадцать» — это когда, а не если. */}
                <span className="fn-rule-group">
                  <span className="fn-ink-3">·</span>
                  <span className="fn-ink-2">ежедневно в</span>
                  <Input type="time" value={r.dailyAt || ''}
                         onChange={(e) => patch(i, { dailyAt: e.target.value || null })}
                         className="h-8 w-28 text-[length:var(--fn-t-md)] leading-5" />
                </span>
                </div>
              </div>

              <div className="fn-rule-row">
                <span className="fn-rule-label">Если</span>
                <div className="fn-rule-fields">
                <select value={r.productType || ''} onChange={(e) => patch(i, { productType: e.target.value || null })}
                        className="fn-select">
                  <option value="">любой продукт</option>
                  {Object.entries(PRODUCTS).map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
                </select>
                <select value={r.source || ''} onChange={(e) => patch(i, { source: e.target.value || null })}
                        className="fn-select">
                  <option value="">любой источник</option>
                  {Object.entries(SOURCES).map(([id, v]) => (
                    <option key={id} value={id}>{v.label}</option>
                  ))}
                </select>
                <button type="button" onClick={() => patch(i, { onlyUnassigned: !r.onlyUnassigned })}
                        className={`fn-mode ${r.onlyUnassigned ? 'fn-mode-on' : ''}`}>
                  без ответственного
                </button>
                <span className="fn-ink-2">с тегом</span>
                <Input value={r.tag || ''} maxLength={40} placeholder="любым"
                       onChange={(e) => patch(i, { tag: e.target.value || null })}
                       className="h-8 w-32 text-[length:var(--fn-t-md)] leading-5" />
                {/* Условие по ответу клиента боту. В amoCRM на нём стоят ветки
                    «не звонить, писать» и возврат в квалификацию: по этапу и
                    тегу они неразличимы, разница только в том, что ответил
                    человек. Значений может быть несколько — через запятую. */}
                <span className="fn-ink-2">поле</span>
                <Input value={r.field || ''} maxLength={40} placeholder="любое"
                       onChange={(e) => patch(i, { field: e.target.value || null })}
                       className="h-8 w-36 text-[length:var(--fn-t-md)] leading-5" />
                {r.field && (
                  <>
                    <span className="fn-ink-2">=</span>
                    <Input value={(r.fieldValues || []).join(', ')}
                           placeholder="любое значение"
                           onChange={(e) => patch(i, {
                             fieldValues: e.target.value.split(',')
                               .map((v) => v.trim()).filter(Boolean),
                           })}
                           className="h-8 w-44 text-[length:var(--fn-t-md)] leading-5" />
                  </>
                )}
                </div>
              </div>

              {/* Исключения нужны общему правилу: у почты своё правило, и без
                  этого заявка с письма получает две задачи разом.

                  Свёрнуты по умолчанию: четырнадцать кнопок занимали треть
                  окна и почти всегда стоят пустыми — а место отнимали у
                  того, что читают каждый раз. */}
              <div className="fn-rule-row">
                <span className="fn-rule-label" />
                <div className="fn-rule-fields">
                {!openExcept[r.id] ? (
                  <button type="button" className="fn-mode"
                          onClick={() => setOpenExcept((o) => ({ ...o, [r.id]: true }))}>
                    {(r.excludeSources || []).length
                      ? `кроме: ${r.excludeSources.length} ${plural(r.excludeSources.length, 'источник', 'источника', 'источников')}`
                      : 'кроме источников…'}
                  </button>
                ) : (<>
                <span className="fn-ink-2">Кроме</span>
                {Object.entries(SOURCES).map(([id, v]) => {
                  const off = (r.excludeSources || []).includes(id);
                  return (
                    <button key={id} type="button"
                            onClick={() => patch(i, {
                              excludeSources: off
                                ? r.excludeSources.filter((x) => x !== id)
                                : [...(r.excludeSources || []), id],
                            })}
                            className={`fn-mode ${off ? 'fn-mode-on' : ''}`}>
                      {v.label}
                    </button>
                  );
                })}
                </>)}
                </div>
              </div>

              <div className="fn-rule-row fn-rule-then">
                <span className="fn-rule-label">Тогда</span>
                <div className="fn-rule-fields flex-col items-stretch gap-2">
                {(r.actions || []).map((a, j) => (
                  <div key={j} className="flex flex-wrap items-center gap-2 text-[length:var(--fn-t-md)] leading-5">
                    <select value={a.type} onChange={(e) => patchAction(i, j, { type: e.target.value })}
                            className="fn-select">
                      {Object.entries(actions).map(([id, label]) => (
                        <option key={id} value={id}>{label}</option>
                      ))}
                    </select>

                    {a.type === 'create_task' && (
                      <>
                        <Input value={a.title} placeholder="текст задачи"
                               onChange={(e) => patchAction(i, j, { title: e.target.value })}
                               className="h-8 min-w-40 flex-1 text-[length:var(--fn-t-md)] leading-5" />
                        <span className="fn-ink-2">через</span>
                        <Input type="number" min="0" step="1" value={a.dueInHours ?? ''}
                               onChange={(e) => patchAction(i, j, {
                                 dueInHours: e.target.value === '' ? null : Number(e.target.value) })}
                               className="h-8 w-20 text-[length:var(--fn-t-md)] leading-5" />
                        <span className="fn-ink-2">ч</span>
                      </>
                    )}

                    {a.type === 'assign' && (
                      <select value={a.to || ''} onChange={(e) => patchAction(i, j, { to: e.target.value })}
                              className="fn-select">
                        <option value="">кому</option>
                        {managers.map((m) => (
                          <option key={m.username} value={m.username}>
                            {m.fullName || m.username}
                          </option>
                        ))}
                      </select>
                    )}

                    {a.type === 'comment' && (
                      <Input value={a.text} placeholder="текст комментария"
                             onChange={(e) => patchAction(i, j, { text: e.target.value })}
                             className="h-8 flex-1 text-[length:var(--fn-t-md)] leading-5" />
                    )}

                    {a.type === 'move_stage' && (
                      <select value={a.stageId || ''} className="fn-select"
                              onChange={(e) => patchAction(i, j, { stageId: e.target.value })}>
                        <option value="">на какой этап</option>
                        {stages.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
                      </select>
                    )}

                    {(a.type === 'set_tag' || a.type === 'unset_tag') && (
                      <Input value={a.tagName || ''} placeholder="имя тега"
                             onChange={(e) => patchAction(i, j, { tagName: e.target.value })}
                             className="h-8 flex-1 text-[length:var(--fn-t-md)] leading-5" />
                    )}

                    {a.type === 'set_field' && (
                      <>
                        <Input value={a.fieldName || ''} placeholder="имя поля"
                               onChange={(e) => patchAction(i, j, { fieldName: e.target.value })}
                               className="h-8 w-40 text-[length:var(--fn-t-md)] leading-5" />
                        <span className="fn-ink-2">=</span>
                        <Input value={a.fieldValue || ''} placeholder="значение"
                               onChange={(e) => patchAction(i, j, { fieldValue: e.target.value })}
                               className="h-8 flex-1 text-[length:var(--fn-t-md)] leading-5" />
                      </>
                    )}

                    {a.type === 'send_message' && (
                      <>
                        <select value={a.channelId || ''} className="fn-select"
                                onChange={(e) => patchAction(i, j, { channelId: e.target.value })}>
                          <option value="">канал</option>
                          {channels.map((c) => (
                            <option key={c.id} value={c.id}>{c.name || c.transport}</option>
                          ))}
                        </select>
                        {/* WhatsApp Business не даёт написать первым обычным
                            текстом — только одобренным шаблоном. */}
                        <select value={a.templateId || ''} className="fn-select"
                                onChange={(e) => patchAction(i, j, { templateId: e.target.value })}>
                          <option value="">свой текст (только в ответ)</option>
                          {templates.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.title} · {t.risky ? 'Marketing — часть не дойдёт' : 'Utility'}
                            </option>
                          ))}
                        </select>
                        {!a.templateId && (
                          <Input value={a.text} placeholder="текст сообщения"
                                 onChange={(e) => patchAction(i, j, { text: e.target.value })}
                                 className="h-8 min-w-40 flex-1 text-[length:var(--fn-t-md)] leading-5" />
                        )}
                        {templates.find((t) => t.id === a.templateId)?.risky && (
                          <span className="basis-full text-[length:var(--fn-t-xs)] leading-4 fn-warn">
                            Meta отнесла шаблон к Marketing: часть сообщений не дойдёт,
                            и узнать об этом будет неоткуда — отправка пройдёт, ошибки
                            не будет, клиент просто промолчит. Для служебных напоминаний
                            текст стоит переподать так, чтобы он прошёл как Utility.
                          </span>
                        )}
                      </>
                    )}

                    {a.type === 'send_mail' && (
                      <>
                        <select value={a.mailbox || ''} className="fn-select"
                                onChange={(e) => patchAction(i, j, { mailbox: e.target.value })}>
                          <option value="">из какого ящика</option>
                          {mailboxes.map((m) => (
                            <option key={m.email} value={m.email}>{m.email}</option>
                          ))}
                        </select>
                        <Input value={a.subject} placeholder="тема"
                               onChange={(e) => patchAction(i, j, { subject: e.target.value })}
                               className="h-8 w-40 text-[length:var(--fn-t-md)] leading-5" />
                        <Input value={a.text} placeholder="текст письма"
                               onChange={(e) => patchAction(i, j, { text: e.target.value })}
                               className="h-8 min-w-40 flex-1 text-[length:var(--fn-t-md)] leading-5" />
                      </>
                    )}

                    <Button variant="ghost" size="sm"
                            onClick={() => patch(i, { actions: r.actions.filter((_, n) => n !== j) })}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                <button type="button" className="text-[length:var(--fn-t-xs)] leading-4 fn-ink-2 hover:opacity-90"
                        onClick={() => patch(i, { actions: [...(r.actions || []),
                          { type: 'create_task', taskType: 'Связаться', title: '', dueInHours: 2,
                            to: '', text: '', stageId: null, channelId: '', templateId: '',
                            templateValues: [], mailbox: '', subject: '' }] })}>
                  + ещё действие
                </button>
                </div>
              </div>

              <div className="fn-rule-row">
                <span className="fn-rule-label" />
                <div className="fn-rule-fields">
                <button type="button" onClick={() => patch(i, { applyToExisting: !r.applyToExisting })}
                        className={`fn-mode ${r.applyToExisting ? 'fn-mode-on' : ''}`}>
                  применить к уже накопленным
                </button>
                <Button variant="outline" size="sm" onClick={() => check(r)}>Проверить</Button>
                {checked[r.id] && (
                  <span className="text-[length:var(--fn-t-sm)] leading-4 opacity-65">
                    подойдёт сделок: <b>{checked[r.id].matches}</b>
                    {checked[r.id].notYetActive
                      ? ' — только те, что появятся после включения'
                      : (!checked[r.id].appliesToExisting && checked[r.id].since
                          ? ` (только новее ${new Date(checked[r.id].since).toLocaleDateString('ru-RU')})`
                          : '')}
                  </span>
                )}
                </div>
              </div>
            </div>
            );
          })}
        </div>

        <p className="text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">
          Без галочки «применить к уже накопленным» правило работает только с тем, что появилось
          после его включения. На нашей базе «нет движения 72 часа» иначе выдало бы 84 задачи разом —
          поэтому сначала «Проверить», потом сохранение.
        </p>

        <DialogFooter className="fn-dialog-footer gap-2">
          <Button variant="outline" onClick={addRule}>
            <Plus className="mr-1.5 h-4 w-4" /> Правило
          </Button>
          <Button onClick={save} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Служебное ли это письмо.
 *
 * Половина ящика — отчёты DMARC от почтовых служб: 25 из 50 в боевом
 * слепке. Менеджеру они не говорят ничего, но лежат вперемешку с
 * живыми запросами, и запрос в них тонет. Не удаляем — прячем, и
 * показываем по кнопке: это всё-таки почта компании, а не наш список.
 */
function isServiceMail(m) {
  const subject = (m.subject || '').toLowerCase();
  const from = (m.from || '').toLowerCase();
  return /report domain:|dmarc|abuse report|delivery status|mail delivery/.test(subject)
      || /dmarc|noreply@|no-reply@|postmaster@|mailer-daemon/.test(from);
}

function MailView({ onOpenLead }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [full, setFull] = useState(null);
  const [unread, setUnread] = useState(false);
  // Что показывать: всё живое, только непривязанное к сделкам (там
  // лежат заявки, которых нет в воронке) или служебные отчёты.
  const [kind, setKind] = useState('live');
  const [box, setBox] = useState('');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [making, setMaking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (query) p.set('q', query);
      if (unread) p.set('unread', 'true');
      if (box) p.set('mailbox', box);
      const r = await fetch(`${API_URL}/api/funnel/mail/inbox?${p}`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Не удалось загрузить почту');
      setData(await r.json());
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [query, unread, box]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 400);
    return () => clearTimeout(t);
  }, [q]);

  const toLead = async () => {
    if (!open) return;
    setMaking(true);
    try {
      const r = await fetch(
        `${API_URL}/api/funnel/mail/message/${open.id}/to-lead?mailbox=${encodeURIComponent(open.mailbox)}`,
        { method: 'POST', headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `Ошибка ${r.status}`);
      toast.success(`Сделка заведена — ${d.name || d.id}`);
      setOpen(null);
      load();
      onOpenLead(d);
    } catch (e) { toast.error(e.message); } finally { setMaking(false); }
  };

  const openMessage = async (m) => {
    setOpen(m);
    setFull(null);
    try {
      const r = await fetch(
        `${API_URL}/api/funnel/mail/message/${m.id}?mailbox=${encodeURIComponent(m.mailbox)}`,
        { headers: authHeaders() });
      if (r.ok) setFull(await r.json());
    } catch { /* покажем то, что уже есть в списке */ }
  };

  const boxes = data?.mailboxes || [];
  const all = data?.messages || [];
  const service = all.filter(isServiceMail);
  const live = all.filter((m) => !isServiceMail(m));
  const orphan = live.filter((m) => !m.lead && !m.outgoing);
  const shown = kind === 'service' ? service : kind === 'orphan' ? orphan : live;

  return (
    <div className="mt-3">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 fn-ink-2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="поиск по письмам" className="h-8 w-56 pl-8 text-[length:var(--fn-t-md)] leading-5" />
        </div>
        <button type="button" onClick={() => setUnread(!unread)}
                className={`fn-mode ${unread ? 'fn-mode-on' : ''}`}>Непрочитанные</button>
        {/* Разбор ящика. «Без сделки» — главный: там лежат письма, по
            которым человек написал, а заявки в воронке нет. В боевом
            слепке таких шестнадцать из пятидесяти, и среди них прямые
            запросы на сауны. */}
        <div className="fn-seg">
          {[['live', 'Живые', live.length],
            ['orphan', 'Без сделки', orphan.length],
            ['service', 'Служебные', service.length]].map(([id, title, n]) => (
            <button key={id} type="button" onClick={() => setKind(id)}
                    aria-pressed={kind === id}
                    className={`fn-seg-item${kind === id ? ' is-on' : ''}`}>
              {title}
              {n > 0 && <span className="ml-1 tabular-nums fn-ink-3">{n}</span>}
            </button>
          ))}
        </div>
        {boxes.length > 1 && (
          <select value={box} onChange={(e) => setBox(e.target.value)} className="fn-select">
            <option value="">все ящики</option>
            {boxes.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        )}
        <Button variant="outline" size="sm" onClick={load} title="Обновить">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {data?.problems?.map((p) => (
        <p key={p} className="mb-2 rounded-[var(--fn-r-sm)] border border-amber-200 bg-amber-50 px-3 py-2 text-xs fn-warn">{p}</p>
      ))}

      {loading && !data && <p className="py-8 text-center text-sm fn-ink-2">Загружаю письма…</p>}
      {data && boxes.length === 0 && (
        <p className="py-8 text-center text-sm fn-ink-2">
          Ни одного ящика не подключено — добавьте его кнопкой с конвертом.
        </p>
      )}
      {data && boxes.length > 0 && shown.length === 0 && !loading && (
        <p className="py-8 text-center text-sm fn-ink-2">
          {kind === 'orphan' ? 'Все письма разобраны по сделкам'
            : kind === 'service' ? 'Служебных писем нет' : 'Писем не найдено'}
        </p>
      )}

      <div className="divide-y" style={{ borderColor: 'var(--fn-line)' }}>
        {shown.map((m) => (
          <button key={`${m.mailbox}-${m.id}`} type="button" onClick={() => openMessage(m)}
                  className="flex w-full items-baseline gap-3 px-1 py-2 text-left hover:bg-black/[0.03] dark:hover:bg-white/[0.04]">
            <span className={`w-40 shrink-0 truncate text-[length:var(--fn-t-md)] leading-5 ${m.unread ? 'font-semibold' : ''}`}>
              {m.outgoing ? `→ ${m.to}` : m.from}
            </span>
            <span className={`min-w-0 flex-1 truncate text-[length:var(--fn-t-md)] leading-5 ${m.unread ? 'font-medium' : ''}`}>
              {m.subject || '(без темы)'}
              <span className="ml-2 fn-ink-2">{m.snippet}</span>
            </span>
            {m.lead && (
              <span className="fn-tag shrink-0" style={{ '--fn-hue': hueFromText(m.lead.name || '') }}>
                {m.lead.name || m.lead.id}
              </span>
            )}
            <time className="shrink-0 text-[length:var(--fn-t-xs)] leading-4 tabular-nums fn-ink-2">
              {m.at ? new Date(m.at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }) : ''}
            </time>
          </button>
        ))}
      </div>

      <Dialog open={!!open} onOpenChange={() => { setOpen(null); setFull(null); }}>
        <DialogContent className="funnel-ui max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-base">{open?.subject || '(без темы)'}</DialogTitle>
            <DialogDescription className="text-[length:var(--fn-t-sm)] leading-4">
              {open?.from} → {open?.to}
              {open?.at && ` · ${new Date(open.at).toLocaleString('ru-RU')}`}
              {open?.mailbox && ` · ${open.mailbox}`}
            </DialogDescription>
          </DialogHeader>

          {(full?.lead || open?.lead) ? (
            <button type="button"
                    onClick={() => { const l = full?.lead || open.lead; setOpen(null); onOpenLead(l); }}
                    className="flex items-center gap-2 rounded-[var(--fn-r-sm)] border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm fn-good">
              <ExternalLink className="h-4 w-4 shrink-0" />
              Открыть сделку {(full?.lead || open.lead).name || (full?.lead || open.lead).id}
            </button>
          ) : (
            <Button variant="outline" size="sm" onClick={toLead} disabled={making || !full}
                    className="w-full">
              {making ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
              Завести сделку из письма
            </Button>
          )}

          <div className="max-h-[60vh] overflow-y-auto whitespace-pre-wrap text-[length:var(--fn-t-md)] leading-5">
            {full ? (full.text || full.snippet || '(пустое письмо)')
                  : <span className="fn-ink-2">Загружаю письмо…</span>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MailboxesDialog({ open, onClose, managers }) {
  const [boxes, setBoxes] = useState([]);
  const [shared, setShared] = useState(true);
  const [owner, setOwner] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/integrations/gmail/mailboxes`, { headers: authHeaders() });
      if (r.ok) setBoxes((await r.json()).mailboxes || []);
    } catch { /* показываем пустой список */ }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const connect = async () => {
    setBusy(true);
    try {
      const q = `shared=${shared}&owner=${encodeURIComponent(shared ? '' : owner)}`;
      const r = await fetch(`${API_URL}/api/integrations/gmail/auth-url?${q}`, { headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Не удалось начать подключение');
      // Окно Google открываем отдельной вкладкой: внутри диалога Google
      // показываться отказывается.
      window.open(d.url, '_blank', 'width=520,height=680');
      toast.info('Войдите тем ящиком, который подключаете');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const remove = async (email) => {
    if (!window.confirm(`Отключить ${email}? Переписка из него перестанет показываться.`)) return;
    await fetch(`${API_URL}/api/integrations/gmail/mailboxes/${encodeURIComponent(email)}`,
                { method: 'DELETE', headers: authHeaders() });
    load();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="funnel-ui max-w-lg">
        <DialogHeader>
          <DialogTitle>Почта</DialogTitle>
          <DialogDescription>
            Каждый ящик подключается отдельно — войдите в Google тем адресом, который добавляете.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {boxes.length === 0 && <p className="text-sm fn-ink-2">Пока ни одного ящика.</p>}
          {boxes.map((b) => (
            <div key={b.email} className="flex items-center justify-between gap-2 rounded-[var(--fn-r-sm)] border px-3 py-2"
                 style={{ borderColor: 'var(--fn-line)' }}>
              <div className="min-w-0">
                <p className="truncate text-sm">{b.email}</p>
                <p className="text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">
                  {b.shared ? 'общий — виден всем' : `личный — ${b.owner || 'без владельца'}`}
                </p>
                {b.error && <p className="text-[length:var(--fn-t-xs)] leading-4 fn-warn">{b.error}</p>}
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove(b.email)}>Отключить</Button>
            </div>
          ))}
        </div>

        <div className="space-y-2 border-t pt-3" style={{ borderColor: 'var(--fn-line)' }}>
          <div className="flex items-center gap-1">
            {[[true, 'Общий'], [false, 'Личный']].map(([v, label]) => (
              <button key={label} type="button" onClick={() => setShared(v)}
                      className={`fn-mode ${shared === v ? 'fn-mode-on' : ''}`}>{label}</button>
            ))}
            {!shared && (
              <select value={owner} onChange={(e) => setOwner(e.target.value)} className="fn-select ml-1">
                <option value="">чей ящик</option>
                {managers.map((m) => (
                  <option key={m.username} value={m.username}>
                    {m.fullName || m.username}
                  </option>
                ))}
              </select>
            )}
          </div>
          <Button onClick={connect} disabled={busy || (!shared && !owner)} className="w-full">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
            Подключить ящик
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GoogleSheetDialog({ open, onClose, onImported }) {
  const [status, setStatus] = useState(null);
  const [managers, setManagers] = useState([]);
  const [url, setUrl] = useState('');
  const [sheets, setSheets] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/integrations/google/status`, { headers: authHeaders() });
    if (r.ok) setStatus(await r.json());
  }, []);

  useEffect(() => { if (open) { setPreview(null); load(); } }, [open, load]);

  useEffect(() => {
    if (!open) return;
    fetch(`${API_URL}/api/funnel/managers`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { managers: [] }))
      .then((d) => { rememberManagers(d.managers); setManagers(d.managers || []); })
      .catch(() => {});
  }, [open]);

  // Согласие Google открывается отдельной вкладкой, и вернуться оттуда в наше
  // окно нельзя — поэтому просто перечитываем состояние, когда пользователь
  // возвращается на эту вкладку.
  useEffect(() => {
    if (!open) return;
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [open, load]);

  const call = async (path, options = {}) => {
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/integrations/google${path}`, {
        headers: authHeaders(), ...options,
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || `Ошибка ${r.status}`);
      return body;
    } finally { setBusy(false); }
  };

  const connect = async () => {
    try {
      const { url: authUrl } = await call('/auth-url');
      window.open(authUrl, '_blank', 'width=520,height=680');
    } catch (e) { toast.error(e.message); }
  };

  const chooseSheet = async (sheetName) => {
    try {
      const r = await call('/spreadsheet', {
        method: 'POST',
        body: JSON.stringify({ url: url || status?.spreadsheetId, sheet: sheetName }),
      });
      setSheets(r.sheets || []);
      setUrl('');
      toast.success(`Таблица «${r.title}» добавлена`);
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const doPreview = async () => {
    try { setPreview(await call('/preview')); }
    catch (e) { toast.error(e.message); }
  };

  const doSync = async () => {
    try {
      const r = await call('/sync', { method: 'POST' });
      toast.success(r.summary);
      await load();
      onImported();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="funnel-ui max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sheet className="h-4 w-4" /> Google Таблица
          </DialogTitle>
          <DialogDescription>
            Выгрузка лидов из рекламного кабинета. Читаем только на чтение, amoCRM
            продолжает забирать ту же таблицу — друг другу не мешаем.
          </DialogDescription>
        </DialogHeader>

        {!status && <p className="py-6 text-center text-sm fn-ink-2">Проверяю…</p>}

        {status && !status.configured && (
          <div className="rounded border border-amber-300/50 bg-amber-500/5 p-4 text-[length:var(--fn-t-md)] leading-5">
            <p className="font-medium">Не заданы ключи приложения Google.</p>
            <p className="mt-1.5 opacity-75">
              Создайте OAuth-приложение в Google Cloud Console и добавьте в
              <span className="mono"> backend/.env.local</span> строки
              <span className="mono"> GOOGLE_CLIENT_ID</span> и
              <span className="mono"> GOOGLE_CLIENT_SECRET</span>, затем перезапустите бэкенд.
            </p>
            <p className="mt-2 opacity-75">
              Адрес возврата для Console:<br />
              <span className="mono select-all">{status.redirectUri}</span>
            </p>
          </div>
        )}

        {status?.configured && !status.connected && (
          <Button onClick={connect} disabled={busy} className="w-full">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" />}
            Войти через Google
          </Button>
        )}

        {status?.connected && (
          <>
            <div className="flex items-center justify-between gap-3 rounded border px-3 py-2 text-[length:var(--fn-t-md)] leading-5"
                 style={{ borderColor: 'var(--fn-line)' }}>
              <span className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 fn-good" />
                Подключён {status.email}
              </span>
              <Button variant="ghost" size="sm" disabled={busy}
                      onClick={() => call('/disconnect', { method: 'POST' }).then(load)}>
                Отключить
              </Button>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Добавить таблицу</Label>
              <div className="flex gap-2">
                <Input value={url} onChange={(e) => setUrl(e.target.value)}
                       placeholder="https://docs.google.com/spreadsheets/d/…" />
                <Button variant="outline" size="sm" onClick={() => chooseSheet(null)} disabled={busy || !url}>
                  Добавить
                </Button>
              </div>
            </div>

            {status.sources?.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs">Подключённые таблицы</Label>
                <div className="space-y-2">
                  {status.sources.map((src) => (
                    <div key={src.spreadsheetId} className="rounded border p-3"
                         style={{ borderColor: 'var(--fn-line)' }}>
                      <div className="flex items-start justify-between gap-2">
                        <span className="min-w-0 text-[length:var(--fn-t-md)] leading-5">
                          <span className="block truncate font-medium">{src.title}</span>
                          <span className="fn-ink-2">лист «{src.sheet}»</span>
                        </span>
                        <Button variant="ghost" size="sm" disabled={busy}
                                onClick={() => call(`/spreadsheet/${src.spreadsheetId}`, { method: 'DELETE' }).then(load)}>
                          <Trash2 className="h-3.5 w-3.5 fn-danger" />
                        </Button>
                      </div>

                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        <div className="space-y-1">
                          <Label className="text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">Метка на карточке</Label>
                          <Input
                            defaultValue={src.label || ''}
                            placeholder="напр. Сауны FB"
                            className="h-8"
                            onBlur={(e) => {
                              if ((src.label || '') === e.target.value) return;
                              call('/spreadsheet', {
                                method: 'POST',
                                body: JSON.stringify({ url: src.spreadsheetId, sheet: src.sheet,
                                                       label: e.target.value }),
                              }).then(load);
                            }}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">Ответственный</Label>
                          <Select
                            value={src.assignTo || 'none'}
                            onValueChange={(v) => call('/spreadsheet', {
                              method: 'POST',
                              body: JSON.stringify({ url: src.spreadsheetId, sheet: src.sheet,
                                                     assignTo: v === 'none' ? '' : v }),
                            }).then(load)}
                          >
                            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">никто — разбирают все</SelectItem>
                              {managers.map((m) => (
                                <SelectItem key={m.username} value={m.username}>{m.fullName || m.username}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">
                  Без ответственного заявка ложится в общий доступ: неразобранных видят все
                  менеджеры и разбирают сами.
                </p>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 rounded border px-3 py-2"
                 style={{ borderColor: 'var(--fn-line)' }}>
              <span className="text-[length:var(--fn-t-md)] leading-5">
                Загружать самой
                <span className="block text-[length:var(--fn-t-xs)] leading-4 fn-ink-2">
                  Проверяет таблицы раз в {status.autoSyncIntervalMinutes} мин — как виджет в amoCRM
                </span>
              </span>
              <Button
                variant={status.autoSyncEnabled ? 'default' : 'outline'}
                size="sm"
                disabled={busy}
                onClick={() => call('/auto-sync', {
                  method: 'POST',
                  body: JSON.stringify({
                    enabled: !status.autoSyncEnabled,
                    intervalMinutes: status.autoSyncIntervalMinutes || 10,
                  }),
                }).then(load)}
              >
                {status.autoSyncEnabled ? 'Включена' : 'Выключена'}
              </Button>
            </div>

            {status.sources?.length > 0 && (
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={doPreview} disabled={busy}>
                  Посмотреть, что читается
                </Button>
                <Button className="flex-1" onClick={doSync} disabled={busy}>
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Загрузить сейчас
                </Button>
              </div>
            )}

            {status.lastSyncResult && (
              <p className="text-[length:var(--fn-t-sm)] leading-4 fn-ink-2">
                Последняя загрузка: {status.lastSyncResult}
                {status.lastSyncAt && ` · ${new Date(status.lastSyncAt).toLocaleString('ru-RU')}`}
              </p>
            )}

            {preview && (
              <div className="space-y-2">
                <p className="text-[length:var(--fn-t-md)] leading-5">
                  Строк в таблице: <b className="fn-num">{preview.rowCount}</b>, колонок:{' '}
                  <b className="fn-num">{preview.columns.length}</b>
                </p>
                <div className="max-h-40 overflow-auto rounded border p-2 text-[length:var(--fn-t-xs)] leading-4"
                     style={{ borderColor: 'var(--fn-line)' }}>
                  {preview.columns.map((c) => (
                    <span key={c} className="mr-2 inline-block fn-ink-2">{c}</span>
                  ))}
                </div>
                {preview.sample[0] && (
                  <div className="rounded border p-2 text-[length:var(--fn-t-sm)] leading-4" style={{ borderColor: 'var(--fn-line)' }}>
                    <div className="fn-section-label mb-1">Первая строка</div>
                    {Object.entries(preview.sample[0]).filter(([, v]) => v).slice(0, 8).map(([k, v]) => (
                      <div key={k} className="truncate"><span className="fn-ink-2">{k}:</span> {String(v)}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const FunnelPage = () => {
  const [columns, setColumns] = useState([]);
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  // Карточка живёт по адресу /lead/IN-…, а не в состоянии: на неё нужна
  // ссылка. Само состояние осталось — в нём лежат данные открытой сделки,
  // а адрес говорит, какую показывать.
  const { leadId: routeLeadId, openLead: goToLead, closeLead, cameFrom } = useLeadRoute();
  const [openLead, setOpenLead] = useState(null);
  const [leadMissing, setLeadMissing] = useState(false);

  // Сделку тянем по адресу, а не только по клику: человек может прийти по
  // ссылке из задачи или из чата, и доски перед этим не видеть.
  useEffect(() => {
    if (!routeLeadId) { setOpenLead(null); setLeadMissing(false); return; }
    if (openLead && openLead.id === routeLeadId) return;
    let alive = true;
    setLeadMissing(false);
    fetch(`${API_URL}/api/funnel/leads/${routeLeadId}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => { if (alive) setOpenLead(d); })
      .catch(() => { if (alive) { setOpenLead(null); setLeadMissing(true); } });
    return () => { alive = false; };
  }, [routeLeadId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [productFilter, setProductFilter] = useState('all');
  const [showLost, setShowLost] = useState(true);
  const [onlyMine, setOnlyMine] = useState(false);
  const [managers, setManagers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [view, setView] = useState('board');
  /* Доска или таблица — не отдельный раздел, а два вида одного и того же.
     В amoCRM это пара значков у названия воронки, и правильно: раздел
     остаётся один, фильтры и поиск общие, меняется только подача. */
  const [boardMode, setBoardMode] = useRememberedMode('fn-board-mode', 'board');
  const [showStages, setShowStages] = useState(false);
  const [showMail, setShowMail] = useState(false);
  const [pipeline, setPipeline] = useState('');
  const [pipelines, setPipelines] = useState([]);
  const [lossReasons, setLossReasons] = useState([]);
  const [showRules, setShowRules] = useState(false);
  const [focusStage, setFocusStage] = useState(null);
  const [rulesByStage, setRulesByStage] = useState({});

  // Раздел почты показываем, только когда есть что показывать: пустая
  // вкладка в шапке сбивает с толку сильнее, чем её отсутствие.
  const [hasMail, setHasMail] = useState(false);

  // Перечитываем после закрытия диалога ящиков: только что подключённый
  // ящик должен открыть раздел сразу, а не после перезагрузки страницы.
  useEffect(() => {
    fetch(`${API_URL}/api/integrations/gmail/mailboxes`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { mailboxes: [] }))
      .then((d) => setHasMail((d.mailboxes || []).length > 0))
      .catch(() => {});
  }, [showMail]);

  // Метка непрочитанных на вкладке. Опрашиваем раз в минуту: счётчик берётся
  // из метки INBOX одним запросом, а письмо, пришедшее при открытой воронке,
  // иначе останется незамеченным до перезагрузки.
  const [unread, setUnread] = useState(0);
  const seenUnread = useRef(null);
  const [pushOn, setPushOn] = useState(false);

  useEffect(() => { pushStatus().then(setPushOn); }, []);

  const exportCsv = async () => {
    try {
      const p = new URLSearchParams();
      if (pipeline) p.set('pipeline', pipeline);
      const r = await fetch(`${API_URL}/api/funnel/export?${p}`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Не удалось выгрузить');
      // Файл приходит по авторизованному запросу, поэтому просто ссылкой
      // его не скачать — сохраняем полученный ответ.
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Файл выгружен');
    } catch (e) { toast.error(e.message); }
  };

  const togglePush = async () => {
    const r = pushOn ? await unsubscribeFromPush() : await subscribeToPush();
    if (!r.ok) { toast.error(r.error, { duration: 6000 }); return; }
    setPushOn(!pushOn);
    toast.success(pushOn ? 'Уведомления выключены' : 'Уведомления включены');
  };

  useEffect(() => {
    if (!hasMail) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const r = await fetch(`${API_URL}/api/funnel/mail/unread`, { headers: authHeaders() });
        if (!r.ok || !alive) return;
        const n = (await r.json()).unread || 0;
        setUnread(n);
        // Первый замер только запоминает — иначе всплывёт при каждом входе.
        if (seenUnread.current !== null && n > seenUnread.current) {
          toast.info(n - seenUnread.current === 1 ? 'Новое письмо' : `Новых писем: ${n - seenUnread.current}`);
        }
        seenUnread.current = n;
      } catch { /* сеть моргнула — попробуем через минуту */ }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [hasMail]);

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('authUser') || '{}'); } catch { return {}; }
  }, []);
  const me = user.username || '';
  const admin = user.role === 'admin';

  // Сколько задач требует внимания прямо сейчас. Считаем по той же
  // сводке, что рисует доску, — отдельный запрос дал бы второе число,
  // которое рано или поздно разойдётся с первым.
  const [taskCount, setTaskCount] = useState(0);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        // Просроченные из того, что человеку видно, — своих и ничьих.
        // Отбор строго по исполнителю давал у менеджера ноль: своих
        // задач у него две, а пятьсот ничьих лежат просроченными. Над
        // разделом стоял пустой значок при пятистах горящих задачах, и
        // это ровно то число, ради которого значок и заводили. Кого
        // пускать к задаче, решает сервер; здесь мы просто не сужаем
        // сверх того. `limit: 1` потому, что нужен `total`, а не строки.
        const params = new URLSearchParams({
          done: 'false', overdue: 'true', limit: '1',
        });
        const r = await fetch(`${API_URL}/api/funnel/tasks?${params}`,
                              { headers: authHeaders() });
        if (!r.ok || !alive) return;
        setTaskCount(((await r.json()).total) || 0);
      } catch { /* сеть моргнула — посчитаем через минуту */ }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [me]);

  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  // Заявки, которые никто ни разу не открывал. До появления этого
  // признака «никто не смотрел» и «посмотрели и отложили» выглядели на
  // доске одинаково, а решения это разные: первое надо брать, второе —
  // спросить у того, кто смотрел.
  const [unseenOnly, setUnseenOnly] = useState(false);
  const [tagFilter, setTagFilter] = useState('');
  const [tagList, setTagList] = useState([]);

  /* Сколько фильтров включено — одним числом на кнопке. Отказы по
     умолчанию показаны, поэтому фильтром считается их сокрытие, а не показ:
     счётчик должен означать «я сузил выборку», иначе он горит всегда. */
  const filterCount = [onlyMine, overdueOnly, unassignedOnly, unseenOnly, !!tagFilter, !showLost,
                       productFilter !== 'all', sourceFilter !== 'all'].filter(Boolean).length;
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showGoogle, setShowGoogle] = useState(false);

  // Счётчик на колонке. Перечитываем после закрытия диалога, иначе только
  // что добавленное правило не отражается до перезагрузки.
  useEffect(() => {
    if (!admin) return;
    fetch(`${API_URL}/api/funnel/rules`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { rules: [] }))
      .then((d) => {
        const by = {};
        (d.rules || []).forEach((r) => {
          if (r.event === 'stage_enter' && r.stageId && r.enabled) {
            by[r.stageId] = (by[r.stageId] || 0) + 1;
          }
        });
        setRulesByStage(by);
      })
      .catch(() => {});
  }, [admin, showRules]);


  // Ждём паузы в наборе: запрос на каждую букву — это 15 лишних запросов на
  // один телефон.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  /* Условия отбора собираем в одном месте: доска и таблица показывают одни
     и те же сделки, и если строки разойдутся, «246 в работе» над доской и
     число строк в таблице перестанут совпадать. На сервере отбор тоже
     сходится в одну функцию — с той же целью. */
  const boardParams = useMemo(() => {
    const params = new URLSearchParams();
    if (productFilter !== 'all') params.set('product', productFilter);
    if (onlyMine && me) params.set('assigned', me);
    if (sourceFilter !== 'all') params.set('source', sourceFilter);
    if (overdueOnly) params.set('overdue', 'true');
    if (unassignedOnly) params.set('unassigned', 'true');
    if (unseenOnly) params.set('unseen', 'true');
    if (tagFilter) params.set('tag', tagFilter);
    if (query) params.set('q', query);
    if (pipeline) params.set('pipeline', pipeline);
    params.set('include_lost', String(showLost));
    return params.toString();
  }, [productFilter, onlyMine, me, sourceFilter, overdueOnly, unassignedOnly, unseenOnly,
      tagFilter,
      query, pipeline, showLost]);

  // Отбор на доске сейчас задан или нет. Нужно пустым колонкам, чтобы
  // они могли сказать «ничего не нашлось» вместо прочерка. Воронка и
  // «показывать отказы» сюда не идут: это не отбор, а то, на какую
  // доску человек смотрит.
  /* Какой вид показываем на самом деле.
     При поиске — всегда список, какой бы вид ни был выбран. Доска
     находку прячет: она ложится в свою колонку по этапу, и если этап
     одиннадцатый из тридцати, её не видно без прокрутки вбок мимо
     десяти пустых колонок. Менеджер читает это как «не найдено» —
     проверено на стенде: поиск по адресу почты нашёл одну сделку,
     стоявшую в одиннадцатой колонке. Выбранный вид при этом не
     меняется: очистил поиск — вернулась доска. */
  const shownMode = query ? 'list' : boardMode;

  const boardFiltered = useMemo(() => Boolean(
    query || tagFilter || onlyMine || overdueOnly || unassignedOnly || unseenOnly
    || productFilter !== 'all' || sourceFilter !== 'all',
  ), [query, tagFilter, onlyMine, overdueOnly, unassignedOnly, unseenOnly,
      productFilter, sourceFilter]);

  const load = useCallback(async () => {
    try {
      const params = boardParams;
      const [b, s] = await Promise.all([
        fetch(`${API_URL}/api/funnel/board?${params}`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/funnel/stats${pipeline ? `?pipeline=${pipeline}` : ''}`,
              { headers: authHeaders() }),
      ]);
      if (!b.ok) throw new Error('Не удалось загрузить доску');
      const board = await b.json();
      setColumns(board.columns || []);
      setSummary(board.summary || null);
      setPipelines(board.pipelines || []);
      setLossReasons(board.lossReasons || []);
      // Воронку запоминаем ту, что вернул сервер: при первой загрузке мы её
      // не передавали, и без этого переключатель остался бы без выбранного.
      setPipeline((prev) => prev || board.pipelineId || '');
      if (s.ok) setStats(await s.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [boardParams, pipeline]);

  useEffect(() => { load(); }, [load]);

  /* Возврат с карточки на доску перечитывает доску.

     Прежде здесь стояло рассуждение: «на странице доски под карточкой
     нет, обновлять нечего — она перезагрузится сама при возврате».
     Замер 27.09.2026 показал обратное: название и заметку, изменённые в
     карточке, на доске видно только после перезагрузки страницы. Доска
     живёт в состоянии компонента, а возврат по истории состояние не
     трогает — меняется только адрес.

     Перечитываем именно на возврате, а не после каждой правки: лишний
     запрос на каждое нажатие перерисовывал бы карточку второй раз и
     закрывал открытые выпадающие списки. */
  const wasOnLead = useRef(false);
  useEffect(() => {
    if (routeLeadId) { wasOnLead.current = true; return; }
    if (!wasOnLead.current) return;
    wasOnLead.current = false;
    load();
  }, [routeLeadId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetch(`${API_URL}/api/funnel/managers`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { managers: [] }))
      .then((d) => { rememberManagers(d.managers); setManagers(d.managers || []); })
      .catch(() => {});
    // Список тегов для фильтра — те, что уже стоят на сделках, со
    // счётчиком. Берём один раз при открытии раздела: теги меняются
    // редко, а запрос перебирает все сделки.
    fetch(`${API_URL}/api/funnel/tags`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { tags: [] }))
      .then((d) => setTagList(d.tags || []))
      .catch(() => {});
  }, []);

  // Диалог держит копию лида, поэтому после любого действия его надо перечитать
  // из свежей доски — иначе комментарий появится только после закрытия.
  const refresh = useCallback(async () => {
    await load();
    if (openLead) {
      const r = await fetch(`${API_URL}/api/funnel/leads/${openLead.id}`, { headers: authHeaders() });
      if (r.ok) setOpenLead(await r.json());
    }
  }, [load, openLead]);

  const stages = useMemo(() => columns.map((c) => c.stage), [columns]);

  // Перенос, ждущий причины отказа. Спрашиваем до отправки, а не после:
  // сервер такой перенос отклоняет, и менеджер, перетащивший карточку,
  // увидел бы ошибку вместо вопроса.
  const [askLoss, setAskLoss] = useState(null);

  const sendMove = async (leadId, stageId, lossReason = '') => {
    try {
      const res = await fetch(`${API_URL}/api/funnel/leads/${leadId}/stage`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ stageId, lossReason }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Не удалось перенести');
      load();
    } catch (e) {
      toast.error(e.message);
      load();
    }
  };

  const onDragEnd = async ({ active, over }) => {
    setActiveId(null);
    if (!over || !active) return;
    const from = columns.find((c) => c.leads.some((l) => l.id === active.id));
    if (!from || from.stage.id === over.id) return;

    const target = columns.find((c) => c.stage.id === over.id)?.stage;
    if (target?.isLost) {
      // Карточку на месте не двигаем: причину могут не выбрать, и тогда
      // переноса не было вовсе.
      setAskLoss({ leadId: active.id, stage: target, reason: '' });
      return;
    }

    // Двигаем карточку сразу, не дожидаясь ответа: при ошибке всё вернёт load().
    setColumns((prev) => prev.map((c) => {
      if (c.stage.id === from.stage.id) return { ...c, leads: c.leads.filter((l) => l.id !== active.id), count: c.count - 1 };
      if (c.stage.id === over.id) {
        const moved = from.leads.find((l) => l.id === active.id);
        return { ...c, leads: [{ ...moved, stageId: over.id }, ...c.leads], count: c.count + 1 };
      }
      return c;
    }));

    await sendMove(active.id, over.id);
  };

  const activeLead = useMemo(
    () => columns.flatMap((c) => c.leads).find((l) => l.id === activeId),
    [columns, activeId]
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center fn-ink-3">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Загружаю воронку
      </div>
    );
  }

  // Открыта карточка по адресу — показываем только её. Доска под ней не
  // нужна: она всё равно не видна, а её запросы и перетаскивание работали
  // бы вхолостую.
  if (routeLeadId) {
    if (leadMissing) {
      return (
        <div className="funnel-ui mx-auto max-w-lg px-4 py-16 text-center">
          <p className="text-[length:var(--fn-t-lg)] leading-[22px]">Сделка {routeLeadId} не найдена.</p>
          <p className="mt-1 text-[length:var(--fn-t-md)] leading-5 fn-ink-2">
            Её могли удалить, или в ссылке опечатка.
          </p>
          <button type="button" onClick={closeLead} className="fn-mode mt-4 inline-flex items-center gap-2">
            <ArrowLeft className="h-3.5 w-3.5" /> К доске
          </button>
        </div>
      );
    }
    if (!openLead) {
      return (
        <div className="flex h-64 items-center justify-center fn-ink-3">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Загружаю сделку
        </div>
      );
    }
    return (
      <>
        <LeadDialog
          lead={openLead}
          asPage
          onClose={closeLead}
          // Правка не дёргает доску: лишний setOpenLead после каждого
          // нажатия пересоздавал объект сделки, перерисовывал карточку
          // второй раз и ломал открытые выпадающие списки — они
          // открывались один раз, а потом переставали. Доска перечитается
          // на возврате, отдельным эффектом по адресу.
          onChanged={() => {}}
          admin={admin}
          cameFrom={cameFrom}
        />
      </>
    );
  }

  return (
    <div className="funnel-ui fn-shell">
      {/* Разделы — слева, столбцом.
          Прежде «Сделки / Задачи / Почта» и переключатель воронок висели
          посреди шапки в два ряда, между заголовком и поиском: место для
          навигации там случайное, и глазу приходилось искать её заново на
          каждом экране. Слева она стоит там, где её ищут, и не отнимает
          высоту у содержимого. */}
      <nav className="fn-rail">
        <div className="fn-rail-group">
          {[['desk', 'Рабочий стол', BarChart3],
            ['board', 'Сделки', Briefcase], ['tasks', 'Задачи', CheckSquare],
            ['contacts', 'Клиенты', User],
            ...(hasMail ? [['mail', 'Почта', Mail]] : [])].map(([id, title, Icon]) => (
            <button
              key={id}
              type="button"
              onClick={() => setView(id)}
              aria-current={view === id ? 'page' : undefined}
              className={`fn-rail-item${view === id ? ' is-on' : ''}`}
              data-testid={`funnel-view-${id}`}
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span>{title}</span>
              {id === 'mail' && unread > 0 && (
                <span className="fn-badge">{unread > 99 ? '99+' : unread}</span>
              )}
              {/* Число просроченных задач — тех, по которым срок уже вышел.
                  Это единственный признак срока, который не зависит от
                  того, разрешил ли человек уведомления в браузере, — а
                  на боевой подписок нет ни у Влады, ни у Виолеты, и
                  напоминания им физически некуда слать. Пока признака не
                  было, узнать о подошедшем сроке можно было только
                  открыв раздел и посмотрев. */}
              {id === 'tasks' && taskCount > 0 && (
                <span className="fn-badge"
                      title="Задачи, у которых вышел срок">
                  {taskCount > 99 ? '99+' : taskCount}
                </span>
              )}
            </button>
          ))}
        </div>

      </nav>

      <div className="fn-main space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          {/* На доске заголовок — это и есть выбор воронки: держать рядом
              слово «Воронка» и отдельный переключатель значило бы сказать
              одно и то же дважды. Когда воронка одна, выбирать нечего и
              остаётся обычный заголовок. */}
          {view === 'board' && pipelines.length > 1 ? (
            <div className="fn-pipe-pick">
              <select
                className="fn-page-title fn-pipe-select"
                value={pipeline || ''}
                onChange={(e) => setPipeline(e.target.value)}
                aria-label="Воронка"
                data-testid="funnel-pipeline-select"
              >
                {pipelines.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <ChevronDown className="h-4 w-4 shrink-0 fn-ink-3" aria-hidden="true" />
            </div>
          ) : (
            <h2 className="fn-page-title">
              {view === 'desk' ? 'Рабочий стол'
                : view === 'tasks' ? 'Задачи'
                : view === 'contacts' ? 'Клиенты'
                : view === 'mail' ? 'Почта' : 'Воронка'}
            </h2>
          )}
          <p className="mt-0.5 text-[length:var(--fn-t-md)] leading-5 fn-ink-2">
            {view === 'desk' ? 'С чего начать день: где горит и что с деньгами.'
              : view === 'tasks' ? 'Что обещано клиентам и когда.'
              : view === 'contacts' ? 'Люди, а не заявки: один человек — одна строка, сколько бы раз он ни обращался.'
              : view === 'mail' ? 'Входящие ящиков компании. «Без сделки» — те, по которым заявки ещё нет.'
              : 'Заявки до предоплаты. Дальше сделка уходит в CRM саун.'}
          </p>
        </div>

        {view === 'board' && (
          <div className="fn-seg">
            {/* Словами, а не одними значками. Два квадратика и три полоски
                понятны тому, кто уже знает, что они переключают; словом
                «Доска» не надо угадывать. Значок оставлен рядом — он
                помогает найти нужное взглядом, когда слово уже прочитано
                однажды. */}
            {[['board', 'Доска', LayoutGrid], ['list', 'Список', Rows3]].map(([id, title, Icon]) => (
              <button
                key={id}
                type="button"
                onClick={() => setBoardMode(id)}
                aria-pressed={boardMode === id}
                className={`fn-seg-item${boardMode === id ? ' is-on' : ''}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {title}
              </button>
            ))}
          </div>
        )}



        {view === 'board' && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 fn-ink-2" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Номер, имя, телефон, почта…"
              className="h-9 w-64 pl-8 pr-7"
              data-testid="funnel-search"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 fn-ink-2 hover:opacity-80">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {/* Фильтры — за одной кнопкой, как «Поиск и фильтр» в amoCRM.
              Их было шесть в ряд, и включена кнопка или нет, приходилось
              разбирать по заливке. Счётчик говорит это одним числом. */}
          <Popup
            align="left"
            width="16rem"
            trigger={({ open, toggle }) => (
              <button type="button" onClick={toggle} aria-expanded={open}
                      className={`fn-chip${filterCount ? ' is-on' : ''}`}>
                <Filter className="h-3.5 w-3.5" />
                Фильтр
                {filterCount > 0 && <span className="fn-chip-count">{filterCount}</span>}
              </button>
            )}
          >
            <div className="fn-filter">
              <label className="fn-filter-row">
                <input type="checkbox" checked={onlyMine} disabled={!me}
                       onChange={() => setOnlyMine((v) => !v)} />
                <span>Только мои</span>
              </label>
              <label className="fn-filter-row">
                <input type="checkbox" checked={overdueOnly}
                       onChange={() => setOverdueOnly((v) => !v)} />
                <span>Только просроченные</span>
              </label>
              <label className="fn-filter-row">
                <input type="checkbox" checked={unassignedOnly}
                       onChange={() => setUnassignedOnly((v) => !v)} />
                <span>Без менеджера</span>
              </label>
              <label className="fn-filter-row">
                <input type="checkbox" checked={unseenOnly}
                       onChange={() => setUnseenOnly((v) => !v)} />
                <span>Никто не открывал</span>
              </label>
              <label className="fn-filter-row">
                <input type="checkbox" checked={showLost}
                       onChange={() => setShowLost((v) => !v)} />
                <span>Показывать отказы</span>
              </label>

              <div className="fn-filter-field">
                <span>Продукт</span>
                {/* Свой список, а не Radix: он рисуется в портале вне этой
                    панели, и щелчок по пункту считался бы щелчком мимо —
                    панель закрывалась бы на выборе. */}
                <select className="fn-select" value={productFilter}
                        onChange={(e) => setProductFilter(e.target.value)}>
                  <option value="all">Все продукты</option>
                  {Object.entries(PRODUCTS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="fn-filter-field">
                <span>Источник</span>
                <select className="fn-select" value={sourceFilter}
                        onChange={(e) => setSourceFilter(e.target.value)}>
                  <option value="all">Все источники</option>
                  {Object.entries(SOURCES).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              {/* Тег. Сервер принимал его с самого начала (`lead_filter`,
                  параметр `tag`), и ручка со списком тегов и счётчиками
                  тоже была, — а передать его с экрана было нечем. То есть
                  выборки «кому отправляли каталог», «не звонить»,
                  «whatsapp-stop» существовали в коде и были недостижимы
                  для менеджера. Список берём из тех же тегов, что уже
                  стоят на сделках, со счётчиком: выдуманный словарь
                  предлагал бы то, чего в базе нет. */}
              {tagList.length > 0 && (
                <div className="fn-filter-field">
                  <span>Тег</span>
                  <select className="fn-select" value={tagFilter}
                          onChange={(e) => setTagFilter(e.target.value)}>
                    <option value="">Любой тег</option>
                    {tagList.map((t) => (
                      <option key={t.tag} value={t.tag}>{t.tag} · {t.leads}</option>
                    ))}
                  </select>
                </div>
              )}

              {filterCount > 0 && (
                <button type="button" className="fn-filter-clear"
                        onClick={() => {
                          setOnlyMine(false);
                          setOverdueOnly(false);
                          setUnassignedOnly(false);
                          setUnseenOnly(false);
                          setTagFilter('');
                          setShowLost(true);
                          setProductFilter('all');
                          setSourceFilter('all');
                        }}>
                  Сбросить фильтры
                </button>
              )}
            </div>
          </Popup>

          {/* Настройки, выгрузки и статистика — редкие действия. Восемь
              значков подряд над доской выглядели пультом; в amoCRM над
              воронкой стоят четыре кнопки, остальное в «…». */}
          <CardMenu items={[
            { label: 'Обновить', icon: <RefreshCw className="h-3.5 w-3.5" />, onSelect: load },
            ...(pushSupported() ? [{
              label: pushOn ? 'Выключить уведомления' : 'Включить уведомления',
              icon: <Bell className={`h-3.5 w-3.5${pushOn ? ' fn-good' : ''}`} />,
              onSelect: togglePush,
            }] : []),
            ...(admin ? [
              { label: 'Статистика', icon: <BarChart3 className="h-3.5 w-3.5" />,
                onSelect: () => setShowAnalytics(true) },
              { label: 'Автоматизация', icon: <Zap className="h-3.5 w-3.5" />,
                onSelect: () => setShowRules(true) },
              { label: 'Настроить стадии', icon: <Settings className="h-3.5 w-3.5" />,
                onSelect: () => setShowStages(true) },
              { label: 'Почтовые ящики', icon: <Mail className="h-3.5 w-3.5" />,
                onSelect: () => setShowMail(true) },
              { label: 'Google Таблица', icon: <Sheet className="h-3.5 w-3.5" />,
                onSelect: () => setShowGoogle(true) },
              { label: 'Выгрузить в CSV', icon: <Download className="h-3.5 w-3.5" />,
                onSelect: exportCsv },
            ] : []),
          ]} />

          <button type="button" className="fn-btn fn-btn-primary ml-auto"
                  onClick={() => setShowNew(true)} data-testid="funnel-new-lead-btn">
            <Plus className="h-4 w-4" /> Новый лид
          </button>
        </div>
        )}
      </div>

      {view === 'tasks' && (
        <TasksView me={me} admin={admin} stages={stages}
                   onOpenLead={(lead) => goToLead(lead.id, 'tasks')} />
      )}

      {view === 'desk' && (
        <DeskView admin={admin} onGo={(what) => {
          // Плитка — не картинка, а вход: щёлкнул по «просрочено» и
          // оказался на доске с этим фильтром.
          setView('board');
          setOverdueOnly(what === 'overdue');
          setUnassignedOnly(false);
        }} />
      )}

      {view === 'contacts' && (
        <ContactsView onOpenLead={(lead) => goToLead(lead.id, 'contacts')} />
      )}

      {view === 'mail' && (
        <MailView onOpenLead={(lead) => goToLead(lead.id, 'mail')} />
      )}

      {view === 'board' && stats && (
        <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2 border-y py-3"
             style={{ borderColor: 'var(--fn-line)' }}>
          {/* «В работе» берём из той же сводки, что и остальные числа строки.
              Раньше оно приходило из отдельной ручки /stats с третьим по
              счёту определением живой сделки, и в одной строке стояли числа
              из трёх разных подсчётов. Разница с числом карточек на доске
              объясняется соседним «передано в CRM»: они с доски не убраны,
              но работой уже не считаются. */}
          {summary && (
            <>
              <span className="fn-stat"><b>{summary.total}</b><span>в работе</span></span>
              {/* Передано — рядом с «в работе», потому что объясняет разницу
                  между ним и числом в шапке списка: там все отобранные
                  сделки, здесь без переданных в производство. Без этой
                  подписи два числа на одном экране отличаются непонятно
                  почему, и человек ищет ошибку, которой нет. Показываем
                  только когда есть: ноль переданных объяснять нечего. */}
              {summary.handedOff > 0 && (
                <span className="fn-stat"><b>{summary.handedOff}</b>
                  <span>передано в производство</span></span>
              )}
            </>
          )}
          {/* Три числа выше — про размер базы. Эти три — про работу:
              в amoCRM над доской стоит то, что заставляет взяться за
              сделку, а не то, сколько их накопилось. */}
          {summary && (
            <>
              {/* Все числа строки — про сделки, поэтому подпись не называет
                  задачи: «12 на сегодня» значит двенадцать сделок, у которых
                  есть задача на сегодня. Прежнее «12 задач на сегодня» рядом
                  с «246 в работе» смешивало две единицы в одной строке. */}
              <span className="fn-stat">
                <b>{summary.today}</b>
                <span>на сегодня</span>
              </span>
              <span className={`fn-stat ${summary.overdue > 0 ? 'fn-danger' : ''}`}>
                <b>{summary.overdue}</b><span>просрочено</span>
              </span>
              <span className={`fn-stat ${summary.noTask > 0 ? 'fn-warn' : ''}`}>
                <b>{summary.noTask}</b><span>без задачи</span>
              </span>
            </>
          )}
          <span className="fn-stat"><b>{stats.handedOff}</b><span>передано в CRM</span></span>
          <span className="fn-stat"><b>{stats.duplicates}</b><span>дублей отсеяно</span></span>
        </div>
      )}

      {view === 'board' && shownMode === 'board' && columns.every((c) => c.count === 0) && !query && !overdueOnly && !unassignedOnly && (
        <div className="flex items-start gap-3 rounded border border-amber-300/50 bg-amber-500/5 px-4 py-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Заявок пока нет. Приёмник ждёт их на <span className="font-mono">/api/lead-intake/webhook/&#123;источник&#125;</span> —
            пока адрес не заведён в рекламном кабинете и на сайте, поток идёт только в amoCRM.
          </p>
        </div>
      )}

      {view === 'board' && shownMode === 'board' && columns.every((c) => c.count === 0) && (query || overdueOnly || unassignedOnly) && (
        <p className="py-8 text-center text-sm fn-ink-2">Ничего не найдено — попробуйте изменить условия.</p>
      )}

      {view === 'board' && shownMode === 'list' && (
        <LeadList
          params={boardParams}
          stages={stages}
          managers={managers}
          admin={admin}
          onOpenLead={(lead) => goToLead(lead.id, 'list')}
          onChanged={load}
        />
      )}

      {view === 'board' && shownMode === 'board' && (
      <DndContext
        sensors={sensors}
        /* Цель выбираем по курсору, а не по прямоугольнику карточки.
           Со стандартным правилом побеждает колонка, с которой у
           «призрака» карточки больше площадь пересечения, — а призрак
           шире колонки и накрывает соседнюю. На боевой это значит, что
           карточка, отпущенная над «Nowy lead», уезжает в соседнюю
           ботовскую колонку, и живой клиент молча достаётся боту.
           Замечено 21.10.2026 при разборе работы менеджера.

           Запасной вариант нужен для края доски: когда курсор вне всех
           колонок, pointerWithin не вернёт ничего, и перенос сорвался бы
           без объяснения. */
        collisionDetection={(args) => {
          const byPointer = pointerWithin(args);
          return byPointer.length ? byPointer : rectIntersection(args);
        }}
        onDragStart={({ active }) => setActiveId(active.id)}
        onDragCancel={() => setActiveId(null)}
        onDragEnd={onDragEnd}
      >
        {/* Колонки стоят через зазор, а не впритык, и линии над ними нет.
            Впритык их скруглённые верхушки складывались в фестон: два
            полукруга рядом читаются как один вырез, и где кончается этап
            — не видно. Линия поверх скруглений добавляла к этому третью
            границу. Объекты разделяет пустота, а не черта. */}
        <div className="fn-board flex overflow-x-auto pb-4">
          {columns.map((c) => (
            <Column
              key={c.stage.id}
              stage={c.stage}
              count={c.count}
              amount={c.amount}
              overdue={c.overdue || 0}
              leads={c.leads}
              onOpen={(lead) => goToLead(lead.id, 'board')}
              activeId={activeId}
              filtered={boardFiltered}
              rules={rulesByStage[c.stage.id] || 0}
              onRules={admin ? (st) => { setFocusStage(st); setShowRules(true); } : null}
            />
          ))}
        </div>

        <DragOverlay>
          {activeLead && (
            <div className="fn-card rotate-1 shadow-lg" style={{ width: '15rem' }}>
              <span className="fn-card-name">{activeLead.name || activeLead.phone || 'Без имени'}</span>
            </div>
          )}
        </DragOverlay>
      </DndContext>
      )}

      <LeadDialog
        lead={routeLeadId ? null : openLead}
        onClose={closeLead}
        onChanged={refresh}
        admin={admin}
      />

      <NewLeadDialog
        open={showNew}
        managers={managers}
        onClose={() => setShowNew(false)}
        onCreated={load}
      />

      <GoogleSheetDialog open={showGoogle} onClose={() => setShowGoogle(false)} onImported={load} />
      <MailboxesDialog open={showMail} onClose={() => setShowMail(false)} managers={managers} />
      <RulesDialog open={showRules} onClose={() => { setShowRules(false); setFocusStage(null); }}
                   stages={stages} managers={managers} focusStage={focusStage} />

      {/* Причина отказа — до переноса, а не после. Сервер отклоняет перенос
          в «Закрыто» без причины, и спросить надо здесь: иначе менеджер
          перетащил карточку, а в ответ получил отказ. Отменить тоже можно —
          тогда переноса не было вовсе, карточка осталась на месте. */}
      <Dialog open={!!askLoss} onOpenChange={(v) => !v && setAskLoss(null)}>
        <DialogContent className="funnel-ui max-w-sm">
          <DialogHeader>
            <DialogTitle>Почему не продали?</DialogTitle>
            <DialogDescription>
              Сделка уходит на «{askLoss?.stage?.name}». Без причины отказ —
              просто число в колонке, из которого ничего не следует.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1">
            {lossReasons.map((r) => (
              <button
                key={r}
                type="button"
                className={`fn-chip${askLoss?.reason === r ? ' is-on' : ''}`}
                onClick={() => setAskLoss((a) => ({ ...a, reason: r }))}
              >
                {r}
              </button>
            ))}
            {lossReasons.length === 0 && (
              <p className="fn-ink-3 text-[length:var(--fn-t-sm)] leading-4">
                Список причин пуст — заведите его в настройках воронки.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAskLoss(null)}>Отмена</Button>
            <Button
              disabled={!askLoss?.reason}
              onClick={() => {
                const { leadId, stage, reason } = askLoss;
                setAskLoss(null);
                sendMove(leadId, stage.id, reason);
              }}
            >
              Перенести
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AnalyticsDialog open={showAnalytics} onClose={() => setShowAnalytics(false)} />

      <StagesDialog
        open={showStages}
        stages={stages}
        onClose={() => setShowStages(false)}
        onSaved={load}
      />
      </div>
    </div>
  );
};

export default FunnelPage;
